/**
 * Update Meals extension for Recharge Affinity.
 *
 * Architecture overview:
 * 1) Mount custom content into the Affinity `overview.header` slot.
 * 2) Load session + subscription + bundle-selection data through the Recharge SDK.
 * 3) Render an inline editor that groups meals by collection and supports
 *    inline quantity adjustments with +/- controls.
 * 4) Submit a sanitized bundle-selection payload and refresh Affinity surfaces.
 *
 * Developer onboarding map:
 * - Entry point: this file self-executes and attaches listeners immediately.
 * - Affinity slot lifecycle: the extension only mounts for `overview.header`.
 * - Data model: `state` is the single source of truth for current editor state.
 * - Catalog model: meals from current subscription and Storefront catalog are merged
 *   into one grouped structure (`getUnifiedMealsForDisplayContext`).
 * - Rendering model: overview slot has two modes:
 *   1) summary mode (items + Update button)
 *   2) editing mode (sticky actions + inline editor body)
 * - Save model: outgoing payload is sanitized before hitting Recharge APIs.
 *
 * Operational constraints:
 * - This script is intentionally self-contained so it can run as a raw theme asset.
 * - No ESM imports or build-time assumptions are required.
 * - API payload normalization is defensive because Recharge response shapes vary
 *   between endpoints and SDK versions.
 */
(function bootstrapUpdateMealsExtension() {
  // ---------------------------------------------------------------------------
  // Recharge SDK access wrappers
  // ---------------------------------------------------------------------------
  /**
   * Recharge method accessors backed by the shared SDK bootstrap.
   */
  function getSharedRechargeSdk() {
    const sharedGetter = window.etRechargeUtils?.getSharedRechargeSdk;
    if (typeof sharedGetter === 'function') {
      return sharedGetter();
    }
    const sharedSdk = window.etRechargeSdk;
    if (!sharedSdk || typeof sharedSdk.getMethod !== 'function') {
      throw new Error(
        'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js loads before affinity-update-meals.js.'
      );
    }
    return sharedSdk;
  }

  function getRechargeMethod(pathSegments) {
    const sharedGetter = window.etRechargeUtils?.getRechargeMethod;
    if (typeof sharedGetter === 'function') {
      return sharedGetter(pathSegments);
    }
    return getSharedRechargeSdk().getMethod(pathSegments);
  }

  function loginCustomerPortal(...args) {
    return getRechargeMethod(['auth', 'loginCustomerPortal'])(...args);
  }

  // Reads share one per-page-view memo with every other portal script, so the
  // same subscriptions/charges/catalog are fetched once per screen (#341).
  function rechargeRead(pathSegments, session, ...args) {
    const sharedRead = window.etRechargeUtils?.memoizedRechargeRead;
    if (typeof sharedRead === 'function') {
      return sharedRead(pathSegments, session, args);
    }
    return getRechargeMethod(pathSegments)(session, ...args);
  }

  // Writes invalidate that memo before the caller's `await` resumes.
  function rechargeWrite(pathSegments, ...args) {
    const sharedWrite = window.etRechargeUtils?.runRechargeWrite;
    if (typeof sharedWrite === 'function') {
      return sharedWrite(pathSegments, ...args);
    }
    return getRechargeMethod(pathSegments)(...args);
  }

  function listSubscriptions(...args) {
    return rechargeRead(['subscription', 'listSubscriptions'], ...args);
  }

  function getSubscription(...args) {
    return rechargeRead(['subscription', 'getSubscription'], ...args);
  }

  function listBundleSelections(...args) {
    return rechargeRead(['bundle', 'listBundleSelections'], ...args);
  }

  function getBundleSelection(...args) {
    return rechargeRead(['bundle', 'getBundleSelection'], ...args);
  }

  function updateBundleSelection(...args) {
    return rechargeWrite(['bundle', 'updateBundleSelection'], ...args);
  }

  function productSearch(...args) {
    return rechargeRead(['product', 'productSearch'], ...args);
  }

  function getOptionalRechargeMethod(pathSegments) {
    try {
      return getRechargeMethod(pathSegments);
    } catch (error) {
      return null;
    }
  }

  function getAddress(...args) {
    return rechargeRead(['address', 'getAddress'], ...args);
  }

  function listAddresses(...args) {
    return rechargeRead(['address', 'listAddresses'], ...args);
  }

  function listCharges(...args) {
    return rechargeRead(['charge', 'listCharges'], ...args);
  }

  // Centralized constants keep SDK/loading settings and DOM IDs in one place.
  const CONFIG = Object.freeze({
    // Storefront API configuration for direct catalog reads.
    storefrontApiVersion: '2026-01',
    storefrontCollectionsPageSize: 50,
    storefrontCollectionBatchSize: 8,
    // Product batches in flight at once (#363); a collection's next page
    // still waits for its cursor.
    storefrontCollectionBatchConcurrency: 3,
    storefrontCollectionProductsPageSize: 24,
    storefrontProductVariantsPageSize: 50,
    storefrontCategoryInternalIdKey: 'category_internal_id',
    storefrontCategoryInternalIdNamespace: 'data',
    storefrontCollectionTitlesSessionKey: 'et_update_meals_collection_titles_v1',
    scrollAnchorRenderCount: 3,
    defaultRechargeDiscountMultiplier: 0.9,
    defaultSubscriptionDeliveryFeeThreshold: 25,
    progressiveHydrationInitialBatchSize: 12,
    progressiveHydrationBatchSize: 8,
    progressiveHydrationBatchDelayMs: 16,
    slotTarget: 'overview.header',
    slotTemplateId: 'et-update-meals-overview-slot-template',
    slotRootSelector: '.et-update-meals-overview-slot',
    editorBodySelector: '.et-update-meals-inline-editor__body',
    editorFooterSelector: '.et-update-meals-inline-editor__footer',
    expectedLeadDaysFromChargeDate: 2, // The expected number of days between the charge date and the delivery date
  });
  const DEFAULT_CURRENCY_CODE = 'USD';
  const LOADING_MEALS_LABEL = 'Loading Meals';
  const NO_MEALS_AVAILABLE_MESSAGE = 'No meals are available for this subscription.';
  const UPDATE_BUTTON_DEFAULT_LABEL = 'Update Subscription';
  const SAVE_BUTTON_LOADING_LABEL = 'Saving…';
  const BUNDLE_SELECTION_UPDATE_MAX_ATTEMPTS = 2;
  // CDN rendition sizes (#340). Thumbnails render in a fixed 56px CSS box
  // (slot + sidebar); meal cards are ~200px wide. `var`: read by hoisted
  // render helpers, and the Shopify CDN minifier can hoist those above a
  // `const` (TDZ, SHOPIFY-398).
  var THUMBNAIL_IMAGE_BOX = 56;
  var THUMBNAIL_IMAGE_WIDTH = THUMBNAIL_IMAGE_BOX * 2;
  var MEAL_CARD_IMAGE_WIDTH = 480;
  // Slot rows above the fold on mobile; the rest lazy-load.
  var OVERVIEW_SLOT_EAGER_IMAGE_COUNT = 4;
  // Manual span metadata for the overview header hydration lifecycle.
  // This span is started when a new mount token begins hydrating and ended only
  // after the summary rows are visibly render-ready.
  const OVERVIEW_HYDRATION_SPAN = Object.freeze({
    name: 'subscription.overview.hydration',
    op: 'ui.render',
  });
  const EDITOR_ACTIONS = Object.freeze({
    cancel: 'cancel',
    save: 'save',
    adjustMealQuantity: 'adjust-meal-qty',
    filterCategory: 'filter-category'
  });

  const subscription_menu_icon_svg =
    '<svg width="36px" height="36px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<rect width="24" height="24" fill="currentColor" opacity="0"/>' +
    '<path d="M12,1.5A1.5,1.5,0,1,1,13.5,3,1.5,1.5,0,0,1,12,1.5Zm-6,0A1.5,1.5,0,1,1,7.5,3,1.5,1.5,0,0,1,6,1.5Zm-6,0A1.5,1.5,0,1,1,1.5,3,1.5,1.5,0,0,1,0,1.5Z" transform="translate(4.5 11)" fill="currentColor"/>' +
    '</svg>';
  const scroll_to_up = '<button class="afinity-scroll-up-btn" aria-label="Scroll to Up" style="display: none;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">'+
          '<path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>'+
        '</svg></button>';  

  /**
   * Runtime state model
   * -------------------
   * This object is intentionally centralized because the extension is not built
   * with a framework state manager. Every renderer/handler reads from and writes
   * to this object directly.
   *
   * Naming conventions:
   * - `active*`: data bound to the subscription currently being edited.
   * - `overview*`: cached summary data rendered when editor is closed.
   * - `*Promise`: memoized in-flight async task to avoid duplicate network calls.
   * - `*JobId` / `*RequestId`: monotonic tokens used to ignore stale async work.
   *
   * Lifecycle expectations:
   * - Opening editor populates `active*` keys and sets `editorOpen=true`.
   * - Closing editor clears transient editing keys and restores overview summary.
   * - Affinity refresh invalidates/rehydrates overview snapshot as needed.
   */
  const state = {
    sdkInitialized: false,
    sessionPromise: null,
    editorOpen: false,
    submitting: false,
    activeSubscriptionId: null,
    activeSubscription: null,
    activeBundleSelectionId: null,
    activeBundleSelection: null,
    activeBundleItems: [],
    activeBundleInitialQuantityByMealKey: new Map(),
    activeBundleInitialCollectionStatsById: new Map(),
    activeCurrencyCode: DEFAULT_CURRENCY_CODE,
    activeVariantProfile: null,
    activeFulfillmentType: null,
    activeFeeConfig: null,
    activeFeeTemplatesByType: createEmptyOperationalFeeTemplates(),
    addableMealsCatalog: [],
    addableMealsSource: null,
    addableMealsLoading: false,
    addableMealsError: null,
    collectionTitleFallbackAtMs: 0,
    storefrontCollectionsCache: null,
    storefrontCollectionTitlePrefetchPromise: null,
    storefrontCollectionNamesById: new Map(),
    addMealsRequestId: 0,
    editorSelectedCategoryId: null,
    pendingScrollAnchor: null,
    progressiveHydrationJobId: 0,
    loadingDotsTimerId: null,
    activeOverviewSlotElement: null,
    overviewSlotMountToken: 0,
    overviewSlotSubscriptionId: null,
    overviewSlotAddressId: null,
    overviewSlotItems: [],
    overviewSlotCurrencyCode: DEFAULT_CURRENCY_CODE,
    overviewSlotCharge: null,
    // Set when the upcoming charge is missing because a payment failed: the
    // charge sits in `error`, invisible to the queued-only lookup, so the
    // customer used to get "we couldn't find your next order" with no reason.
    overviewSlotChargeFailure: null,
    overviewSlotActiveSubscriptionCount: 0,
    overviewSlotSubscriptions: [],
    preferredOverviewSubscriptionId: null,
    overviewSlotDeliveryContext: null,
    overviewSlotFulfillmentType: null,
    overviewSlotAddress: null,
    // Span state is tracked separately from data state because the same slot can
    // remount or be superseded while async hydration work is still in flight.
    overviewSlotHydrationFinalizePendingToken: null,
    overviewSlotHydrationFinalizedToken: null,
    overviewSlotHydrationSpan: null,
    overviewSlotHydrationSpanToken: null,
    // Portal-timings handle for the Update click -> first meal card (#347).
    editorOpenSpan: null,
    overviewRefreshTimerId: null,
    // Re-reads left for a charge caught mid-recalculation -- armed by a
    // refresh -- and the pending one (see revalidateChargeWhileRecalculating).
    chargeRecalculationRetriesLeft: 0,
    // The discount (`{ chargeId, codes, total }`) of the last charge ReCharge
    // returned that was not mid-recalculation: what a write's re-reads
    // compare against (the optimistic render changes the charge on screen).
    serverChargeDiscount: null,
    chargeRecalculationTimerId: null,
    reportedOverviewMissingImageKeys: new Set(),
    // Image-sizing health (#340): each reports once per page view.
    reportedImageSizerUnavailable: false,
    reportedSizedImageLoadFailure: false,
    // False while the upcoming-charge lookup is still in flight; true once it
    // settles (with or without a charge) so renders can distinguish "loading"
    // from "no next order exists".
    overviewSlotChargeResolved: false,
    // A hydration read failed even after its retry, so the slot shows
    // "Try again" instead of stating something false about the account:
    // the whole overview, just the items, or just the next-order totals.
    // The whole-overview one is a kind: 'retryable', or 'session-expired' for
    // a dead portal link that "Try again" cannot revive.
    overviewSlotLoadFailure: null,
    overviewSlotItemsLoadFailed: false,
    // Why the settled lookup found no upcoming charge (the reason reported to
    // Sentry), so the totals can say something true and actionable about it.
    overviewSlotChargeMissingReason: null,
    // Whether the subscription itself carries a next charge date. Reschedule
    // anchors on it, so without one "Choose a delivery date" would dead-end.
    overviewSlotHasNextChargeDate: false,
    reportedOverviewChargeMissingKeys: new Set(),
    notifiedPortalSessionExpired: false,
    // The overview load boot started on an overview landing (#361), until the
    // first hydration reads whether it had settled. Here, not in a `var`
    // further down: boot assigns it before a later declaration would run.
    bootOverviewModel: null,
    // Storefront product lookups by product id, per page view: that GraphQL
    // is outside the ReCharge read memo.
    storefrontProductsById: new Map(),
    // Every catalog collection's product pages (#363): the catalog is the
    // same for every subscription, so the idle prefetch and each editor open
    // share one fetch. `{ promise, startedAt, settledAt }`; see
    // loadStorefrontCatalogPages.
    storefrontCatalogPages: null,
    // The overview mount the idle prefetch was announced for, and the
    // editor prefetch for the subscription it last warmed (#363).
    overviewSlotUsableAnnouncedToken: null,
    editorPrefetch: null,
    // The last complete overview render, per page view (#364): its markup
    // with every action disabled, and the subscription choice it was made
    // under. A slot remount shows it at once instead of the skeleton, until
    // the fresh render replaces it. `{ markup, preferredSubscriptionId }`.
    lastOverviewRender: null,
    // Whether the slot keeps what it shows -- lastOverviewRender on a
    // remount, or the current render through a refresh -- until fresh data
    // can replace it (see renderOverviewHeaderSlot).
    overviewSlotHoldingRender: false,
  };

  // Boot order matters:
  // 1) Restore cached collection titles before first render.
  // 2) Inject overview slot template.
  // 3) Bind global event listeners once.
  // 4) Initialize extensions API bridge.
  // 5) Start reading the overview while Affinity is still booting (#361).
  hydrateStorefrontCollectionTitleCache();
  injectSlotTemplate();
  installEventListeners();
  initializeExtensionsBridge();
  prefetchOverviewModel();

  /**
   * Global event wiring
   * -------------------
   * We attach once and use delegation because Affinity rerenders slot content.
   * Listener responsibilities:
   * - `Recharge::slot::mounted`: initialize/rehydrate our custom header content.
   * - `Recharge::slot::unmounted`: cleanup references for detached DOM.
   * - `click` (capturing): intercept custom action buttons rendered by this asset.
   * - `Affinity:refresh`: refresh overview state after save or external updates.
   */
  function installEventListeners() {
    // Header-slot entrypoint: mount/update on slot lifecycle events.
    document.addEventListener(
      'Recharge::slot::mounted',
      handleOverviewHeaderSlotMounted
    );
    document.addEventListener(
      'Recharge::slot::unmounted',
      handleOverviewHeaderSlotUnmounted
    );
    document.addEventListener('click', handleOverviewHeaderActionClick, true);
    document.addEventListener('click', handleFulfillmentMenuClick, true);
    document.addEventListener('Affinity:refresh', handleOverviewDataRefresh);
    // Both drop the shared read memo, so what the boot load read can no
    // longer serve the mount (#361).
    document.addEventListener('Affinity:refresh', discardBootOverviewModelReads);
    document.addEventListener('Recharge::location::change', discardBootOverviewModelReads);
    // `error` does not bubble from <img>; capture sees every portal image.
    document.addEventListener('error', handleSizedImageLoadError, true);
    // Dispatched by announceOverviewPrefetchWhenUsable; the loyalty modal
    // listens for it too.
    document.addEventListener('ET::overview::prefetch', handleOverviewPrefetch);
    // Both may change what the editor loads, so a later usable overview warms
    // it again.
    document.addEventListener('Affinity:refresh', forgetEditorPrefetch);
    document.addEventListener('Recharge::location::change', forgetEditorPrefetch);
    // A refresh follows a write, so the last render may show what it changed.
    document.addEventListener('Affinity:refresh', forgetLastOverviewRender);
    document.addEventListener(
      'ET::view-other-subscriptions::changed',
      function () {
        if (!isOverviewPath(window.location.pathname)) return;
        requestOverviewSlotHydration({ preserveExistingContent: false, trigger: 'view-switch' });
      }
    );
  }

  function injectSlotTemplate() {
    if (document.getElementById(CONFIG.slotTemplateId)) return;

    const slotTemplate = document.createElement('script');
    slotTemplate.id = CONFIG.slotTemplateId;
    slotTemplate.type = 'text/html';
    slotTemplate.setAttribute('data-recharge-slot', CONFIG.slotTarget);
    slotTemplate.textContent = buildOverviewHeaderSlotMarkup({ isLoading: true });
    document.head.appendChild(slotTemplate);
  }

  function initializeExtensionsBridge() {
    if (!window.etUpdateMealsExtensions) {
      window.etUpdateMealsExtensions = {};
    }
    const extensions = window.etUpdateMealsExtensions;

    window.etRechargeSubscriptionCancelIntegration = {
      getFallbackSubscriptionId: function () { return state.overviewSlotSubscriptionId; },
      parseSubscriptionId: parseOptionalSubscriptionId,
      onError: showInlineErrorToast,
    };

    extensions.getState = () => Object.freeze({ ...state });
    extensions.getPortalSession = getPortalSession;
    extensions.parseOptionalSubscriptionId = parseOptionalSubscriptionId;
    extensions.showInlineErrorToast = showInlineErrorToast;
    extensions.resolveChargeDiscountMeta = resolveChargeDiscountMeta;
    extensions.getSubscriptions = getSubscriptions;
    extensions.escapeHtml = escapeHtml;
    extensions.listCharges = listCharges;
    extensions.getOverviewSubscriptions = () =>
      Array.isArray(state.overviewSlotSubscriptions)
        ? state.overviewSlotSubscriptions
        : [];
    extensions.getOverviewActiveSubscriptionCount = () =>
      Number.isFinite(state.overviewSlotActiveSubscriptionCount)
        ? state.overviewSlotActiveSubscriptionCount
        : 0;
    extensions.getPreferredOverviewSubscriptionId = () =>
      state.preferredOverviewSubscriptionId;
    extensions.setPreferredOverviewSubscriptionId = (value) => {
      state.preferredOverviewSubscriptionId =
        parseOptionalSubscriptionId(value) ?? null;
    };
    extensions.applyOverviewDiscountOptimistic = (options = {}) => {
      applyOverviewDiscountOptimistic(options);
    };
    extensions.clearOverviewDiscountOptimistic = () => {
      clearOverviewDiscountOptimistic();
    };

    if (extensions.rewards) {
      extensions.rewards.installListeners();
    }
  }

  function handleOverviewHeaderSlotMounted(event) {
    const detail = event?.detail;
    if (!isOverviewHeaderSlot(detail)) return;

    if (window.etUpdateMealsExtensions?.routing) {
      window.etUpdateMealsExtensions.routing.updateRouteAttributesForSlotMounted();
    }
    const slotElement = resolveSlotElement(detail);
    markOverviewColumnsSingleLayout(slotElement);
    state.activeOverviewSlotElement =
      slotElement instanceof HTMLElement ? slotElement : null;
    // Returning to the overview inside the page view (the editor, another
    // Affinity page) paints the last render instead of the skeleton (#364).
    requestOverviewSlotHydration({ showLastRender: true, trigger: 'mount' });
  }

  function handleOverviewHeaderSlotUnmounted(event) {
    const detail = event?.detail;
    if (!isOverviewHeaderSlot(detail)) return;

    if (window.etUpdateMealsExtensions?.routing) {
      window.etUpdateMealsExtensions.routing.updateRouteAttributesForSlotUnmounted();
    }
    const slotElement = resolveSlotElement(detail) || state.activeOverviewSlotElement;
    if (slotElement instanceof HTMLElement) {
      slotElement.innerHTML = '';
    }
    endActiveOverviewHydrationSpan({
      'et.overview_hydration.outcome': 'unmounted',
    });
    state.activeOverviewSlotElement = null;
    resetOverviewSlotSnapshot();
    state.overviewSlotMountToken += 1;
  }

  function handleOverviewHeaderActionClick(event) {
    const target = event?.target instanceof Element ? event.target : null;
    if (!target) return;

    const updateMealsTrigger = target.closest('[data-action="et-open-update-meals"]');
    if (updateMealsTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      const subscriptionId = parseOptionalSubscriptionId(
        updateMealsTrigger.getAttribute('data-subscription-id') ||
          state.overviewSlotSubscriptionId
      );
      if (!subscriptionId) {
        showInlineErrorToast('Subscription is still loading. Please try again.');
        return;
      }

      openUpdateMealsFlow(subscriptionId);
      return;
    }

    const removeDiscountTrigger =
      event?.target instanceof Element
        ? event.target.closest('[data-action="et-remove-discount"]')
        : null;
    if (removeDiscountTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      if (window.etUpdateMealsExtensions?.discount?.openRemoveDiscountModal) {
        window.etUpdateMealsExtensions.discount.openRemoveDiscountModal();
      }
      return;
    }

    const retryTrigger = target.closest('[data-action="et-retry-overview-hydration"]');
    if (retryTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      // Show the loading skeleton, not the stale failure, while it re-reads.
      renderOverviewHeaderSlot({ isLoading: true });
      requestOverviewSlotHydration({ preserveExistingContent: false, trigger: 'retry' });
      return;
    }

    const loginTrigger = target.closest('[data-action="et-portal-login"]');
    if (loginTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      window.location.href = '/tools/recurring/login';
      return;
    }

    const paymentTrigger = target.closest('[data-action="et-update-payment"]');
    if (paymentTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      closeClosestDetails(paymentTrigger);
      window.location.href =
        '/tools/recurring/login?redirect=customer_portal%2Fpayment_methods';
      return;
    }

    const contactTrigger = target.closest('[data-action="et-contact-us"]');
    if (contactTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      closeClosestDetails(contactTrigger);
      window.etContactUs?.open?.();
      return;
    }
  }

  function closeClosestDetails(node) {
    const details = node?.closest ? node.closest('details') : null;
    if (details && typeof details.open === 'boolean') {
      details.open = false;
    }
  }

  function handleFulfillmentMenuClick(event) {
    const trigger =
      event?.target instanceof Element
        ? event.target.closest('[data-action="et-manage-delivery"], [data-action="et-manage-pickup"]')
        : null;
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const menuDetails = trigger.closest('details');
    if (menuDetails) {
      menuDetails.removeAttribute('open');
    }

    const action = trigger.getAttribute('data-action');
    const subscriptionId = parseOptionalSubscriptionId(
      trigger.getAttribute('data-subscription-id') ||
        state.overviewSlotSubscriptionId
    );
    if (!subscriptionId) {
      showInlineErrorToast('Subscription is still loading. Please try again.');
      return;
    }

    if (action === 'et-manage-delivery') {
      if (window.etManageShipment && typeof window.etManageShipment.openDelivery === 'function') {
        window.etManageShipment.openDelivery(subscriptionId);
      } else {
        showInlineErrorToast('Shipment management is not available. Please refresh the page.');
      }
    } else if (action === 'et-manage-pickup') {
      if (window.etManageShipment && typeof window.etManageShipment.openPickup === 'function') {
        window.etManageShipment.openPickup(subscriptionId);
      } else {
        showInlineErrorToast('Shipment management is not available. Please refresh the page.');
      }
    }
  }

  function handleOverviewDataRefresh(event) {
    if (!isOverviewPath(window.location.pathname)) return;
    if (!isOverviewHeaderSlotMounted()) return;
    if (state.editorOpen) return;

    try {
      const detail = event && typeof event === 'object' ? event.detail : null;
      const payload =
        detail && typeof detail === 'object' ? detail.payload || {} : {};

      const rawIds =
        payload &&
        (payload.subscriptionIds ||
          payload.subscription_ids ||
          payload.subscriptionId ||
          payload.subscription_id);

      const normalizeId = (value) => {
        const parsed = parseOptionalSubscriptionId(value);
        return parsed || null;
      };

      const payloadIds = [];
      if (Array.isArray(rawIds)) {
        for (const value of rawIds) {
          const id = normalizeId(value);
          if (id && !payloadIds.includes(id)) {
            payloadIds.push(id);
          }
        }
      } else {
        const singleId = normalizeId(rawIds);
        if (singleId) {
          payloadIds.push(singleId);
        }
      }

      if (payloadIds.length) {
        const currentId = normalizeId(state.overviewSlotSubscriptionId);

        if (currentId && payloadIds.includes(currentId)) {
          state.preferredOverviewSubscriptionId = currentId;
        } else if (
          !state.preferredOverviewSubscriptionId &&
          payloadIds.length === 1
        ) {
          state.preferredOverviewSubscriptionId = payloadIds[0];
        }
      }
    } catch (_error) {
      // Swallow payload parsing issues and fall back to default refresh behavior.
    }

    // A refresh follows a write, which may leave the charge recalculating:
    // arm its re-reads (against state.serverChargeDiscount).
    state.chargeRecalculationRetriesLeft = CHARGE_RECALCULATION_RETRY_DELAYS_MS.length;

    if (state.overviewRefreshTimerId !== null) {
      window.clearTimeout(state.overviewRefreshTimerId);
      state.overviewRefreshTimerId = null;
    }
    state.overviewRefreshTimerId = window.setTimeout(() => {
      state.overviewRefreshTimerId = null;
      requestOverviewSlotHydration({ preserveExistingContent: true, holdExistingContent: true, trigger: 'refresh' });
    }, 120);
  }

  function applyOverviewDiscountOptimistic(options = {}) {
    if (!isOverviewHeaderSlotMounted()) return;
    if (!state.overviewSlotCharge || typeof state.overviewSlotCharge !== 'object') return;

    const code = String(options.code || '').trim();
    const amount = Number.parseFloat(String(options.amount ?? ''));
    const nextCharge = { ...state.overviewSlotCharge };

    if (code) {
      nextCharge.discount_code = code;
    }

    if (Number.isFinite(amount) && amount >= 0) {
      nextCharge.total_discounts = amount;
      nextCharge.discount_total = amount;
    }

    const currentDiscounts = Array.isArray(nextCharge.discounts)
      ? nextCharge.discounts.slice()
      : [];
    if (code) {
      const exists = currentDiscounts.some((entry) => {
        const entryCode = String(
          entry?.code || entry?.coupon_code || entry?.discount_code || ''
        ).trim();
        return entryCode && entryCode === code;
      });
      if (!exists) {
        currentDiscounts.push(
          Number.isFinite(amount) && amount >= 0
            ? { code, amount }
            : { code }
        );
      }
      nextCharge.discounts = currentDiscounts;
    }

    state.overviewSlotCharge = nextCharge;
    renderOverviewHeaderSlot({ isLoading: false });
  }

  function clearOverviewDiscountOptimistic() {
    if (!isOverviewHeaderSlotMounted()) return;
    if (!state.overviewSlotCharge || typeof state.overviewSlotCharge !== 'object') return;

    const nextCharge = { ...state.overviewSlotCharge };
    nextCharge.discount_code = '';
    nextCharge.total_discounts = 0;
    nextCharge.discount_total = 0;
    nextCharge.discounts = [];
    nextCharge.applied_discounts = [];

    state.overviewSlotCharge = nextCharge;
    renderOverviewHeaderSlot({ isLoading: false });
  }

  function resetOverviewSlotSnapshot(options = {}) {
    const preserveCharge = options?.preserveCharge === true;
    state.overviewSlotSubscriptionId = null;
    state.overviewSlotAddressId = null;
    state.overviewSlotItems = [];
    state.overviewSlotCurrencyCode = DEFAULT_CURRENCY_CODE;
    if (!preserveCharge) state.overviewSlotCharge = null;
    state.overviewSlotChargeFailure = null;
    state.overviewSlotChargeResolved = false;
    state.overviewSlotLoadFailure = null;
    state.overviewSlotItemsLoadFailed = false;
    state.overviewSlotChargeMissingReason = null;
    state.overviewSlotHasNextChargeDate = false;
    state.overviewSlotActiveSubscriptionCount = 0;
    state.overviewSlotSubscriptions = [];
    state.overviewSlotDeliveryContext = null;
    window.etUpdateMealsOverviewSubscriptionId = null;
    state.overviewSlotFulfillmentType = null;
    state.overviewSlotAddress = null;
  }

  /**
   * Requests overview summary hydration.
   *
   * Concurrency note:
   * - `overviewSlotMountToken` is incremented before each request.
   * - async hydration must compare the token before committing state to avoid
   *   rendering stale data after fast navigation/remount cycles.
   */
  function requestOverviewSlotHydration(options = {}) {
    const mountToken = ++state.overviewSlotMountToken;
    const preserveExistingContent = options?.preserveExistingContent !== false;
    state.overviewSlotHydrationFinalizePendingToken = null;
    state.overviewSlotHydrationFinalizedToken = null;
    const showedLastRender =
      options?.showLastRender === true && showLastOverviewRender();
    // A refresh keeps the overview on screen until its fresh charge lands,
    // rather than dropping the totals to a skeleton and back.
    state.overviewSlotHoldingRender =
      showedLastRender ||
      (options?.holdExistingContent === true && isOverviewHeaderSlotMounted());
    // Only the first hydration after an overview landing has a boot load to
    // race; the attribute stays off every other span.
    const bootModel = state.bootOverviewModel;
    state.bootOverviewModel = null;
    // Start a fresh manual span for this exact mount token before any async work
    // begins so later remounts can close older spans as "superseded".
    startOverviewHydrationSpan(
      mountToken,
      {
        // How often the boot-time load wins the race against Affinity's mount.
        // Reads the memo dropped since do not count: the mount reads them again.
        'et.portal.model_ready_before_mount': bootModel
          ? bootModel.settled && !bootModel.readsDiscarded
          : undefined,
        // The customer saw the last render at mount, not a skeleton (#364).
        'et.portal.last_render_shown': showedLastRender,
        // What started this hydration, so dashboards can split landings from
        // refreshes, re-reads and returns (#400).
        'et.portal.hydration_trigger': bootModel ? 'landing' : options?.trigger || 'other',
        // Whether the boot-time load (#361) could serve this landing: still
        // in flight or settled means the mount joins its reads; discarded
        // means an event dropped the read memo first, and which one.
        'et.portal.boot_model': bootModel
          ? bootModel.readsDiscarded
            ? 'discarded'
            : bootModel.settled
              ? 'settled'
              : 'in-flight'
          : undefined,
        'et.portal.boot_discarded_by': bootModel?.readsDiscardedBy || undefined,
        'et.portal.boot_to_mount_ms':
          bootModel && Number.isFinite(bootModel.startedAt)
            ? Math.round(readPerformanceNow() - bootModel.startedAt)
            : undefined,
      },
      // Since #361 most of the load runs before this span starts, so its
      // duration no longer covers what the customer waits for: the landing's
      // span also records when, in the page load, it started and ended. Only
      // the landing's -- a remount's time since navigation measures nothing.
      { navigationTimes: Boolean(bootModel) }
    );
    resetOverviewSlotSnapshot({ preserveCharge: preserveExistingContent });
    renderOverviewModel(
      mountToken,
      loadOverviewModel(parseOptionalSubscriptionId(state.preferredOverviewSubscriptionId))
    );
  }

  // Our deferred scripts run well before Affinity mounts the slot (~1.1 s vs
  // ~1.5 s warm on desktop, ~2.4 s vs ~4.4 s cold on a phone), and the
  // overview reads are a serial chain of ~1.5-2 s, so start them here. The
  // mount's own load joins these reads through the memo; the handle is kept
  // only to tell the first hydration whether they had already landed.
  function prefetchOverviewModel() {
    // Rendered by layout/theme.liquid, which on the same pages loads the
    // ReCharge client ahead of this script: one definition of a landing.
    if (window.etUpdateMealsConfig?.overviewLanding !== true) return;
    // Nothing can have chosen a subscription before the slot mounts.
    const startedAt = readPerformanceNow();
    state.bootOverviewModel = loadOverviewModel(null);
    state.bootOverviewModel.startedAt = startedAt;
  }

  function discardBootOverviewModelReads(event) {
    const bootModel = state.bootOverviewModel;
    if (!bootModel) return;
    if (!bootModel.readsDiscarded) bootModel.readsDiscardedBy = event?.type || 'unknown';
    bootModel.readsDiscarded = true;
  }

  // The performance clock, or NaN where the page has none.
  function readPerformanceNow() {
    const perf = window.performance;
    return perf && typeof perf.now === 'function' ? perf.now() : Number.NaN;
  }

  function isOverviewHeaderSlotMounted() {
    if (state.activeOverviewSlotElement instanceof HTMLElement) {
      return state.activeOverviewSlotElement.isConnected;
    }
    return Boolean(resolveMountedOverviewHeaderSlotElement());
  }

  function isOverviewHeaderSlot(detail) {
    const slotName = String(detail?.name || '')
      .trim()
      .toLowerCase();
    if (slotName !== 'header' && slotName !== 'overview.header') return false;

    const pathname = String(detail?.pathname || '').trim();
    if (pathname) return isOverviewPath(pathname);

    return isOverviewPath(window.location.pathname);
  }

  // Keep in step with is_overview_landing in layout/theme.liquid: it starts the
  // boot reads (#361) that a mount on this path renders.
  function isOverviewPath(pathname) {
    return String(pathname || '').trim().endsWith('/overview');
  }

  function resolveSlotElement(detail) {
    const candidates = [
      detail?.element,
      detail?.slotElement,
      detail?.container,
      detail?.slot,
      detail?.target,
    ];
    for (const candidate of candidates) {
      if (candidate instanceof HTMLElement) return candidate;
    }
    return null;
  }

  function hasRechargeColumnsVars(element) {
    if (!(element instanceof HTMLElement)) return false;
    return String(element.getAttribute('style') || '').indexOf('--rc-columns-total') !== -1;
  }

  function resolveOverviewColumnsWrapper(slotElement) {
    if (!(slotElement instanceof HTMLElement)) return null;

    const card = slotElement.closest('.recharge-card');
    let cursor = slotElement;
    while (cursor && cursor !== card && cursor instanceof HTMLElement) {
      if (hasRechargeColumnsVars(cursor)) return cursor;
      cursor = cursor.parentElement;
    }

    if (card instanceof HTMLElement) {
      if (hasRechargeColumnsVars(card.parentElement)) {
        return card.parentElement;
      }
      const fallback = card.closest('div[style*="--rc-columns-total"]');
      if (fallback instanceof HTMLElement) return fallback;
    }

    return null;
  }

  function getEtObservability() {
    return window.etObservability && typeof window.etObservability === 'object'
      ? window.etObservability
      : null;
  }

  // Overview hydration deliberately degrades instead of breaking the page, so
  // every swallowed failure must still reach Sentry to stay diagnosable.
  function reportOverviewSwallowedFailure(issue, error, extra) {
    // A page being navigated away from rejects every in-flight read ("Load
    // failed" on WebKit). The customer never sees the result, so it is not a
    // failure worth an event.
    if (isPortalPageUnloading()) return;

    // The portal link expired AND the Shopify App Proxy fallback in
    // recharge-utils could not stand in for it, so the shopper is logged out of
    // Shopify too and no code change makes this read succeed. It still reports,
    // because support needs the breadcrumb, but as its own warning-level issue:
    // on the shared error fingerprint it outnumbered every real hydration
    // defect and hid them (SHOPIFY-3HH, the same treatment as SHOPIFY-3H9).
    const sessionExpired = isExpiredPortalSession(error);

    // Ahead of the observability guard below: the shopper needs the
    // explanation even on a page where et-sentry never loaded.
    if (sessionExpired) notifyPortalSessionExpired();

    const observability = getEtObservability();
    if (!observability || typeof observability.captureException !== 'function') {
      return;
    }

    // Pass the raw value: et-sentry types non-Errors (the Recharge SDK rejects
    // with plain objects) and records what threw (SHOPIFY-3HD).
    observability.captureException(error, {
      level: sessionExpired ? 'warning' : 'error',
      fingerprint: sessionExpired
        ? ['portal-session-expired', 'overview-header-slot']
        : undefined,
      // Fixed when the fingerprint is fixed: a per-read-site label would title
      // the grouped issue after whichever read happened to fail first.
      // swallowedIssue keeps that queryable.
      fallbackMessage: sessionExpired
        ? '[update-meals] portal-session-expired'
        : `[update-meals] ${issue}`,
      tags: {
        surface: 'affinity-update-meals',
        component: 'overview-header-slot',
        issue: sessionExpired ? 'portal-session-expired' : issue,
        // Keeps the read that actually failed queryable: every read site
        // collapses into one fingerprint once the session is gone.
        ...(sessionExpired ? { swallowedIssue: issue } : null),
      },
      extra: Object.assign(
        { pathname: window.location.pathname || '' },
        extra || {}
      ),
    });
  }

  // Narrower than isPortalAuthError on purpose. That one is true for any bare
  // 401/403, which is equally the shape of a scope or permission fault -- and
  // classifying one of those here would tell the shopper to log in again for a
  // defect login cannot fix, then bury it at warning level under this
  // fingerprint. Exactly the burial this change exists to undo.
  //
  // A 401/403 counts only once a login in this page view PROVED the link dead
  // (422 "Invalid Token", with the App Proxy unable to stand in). That is the
  // common expired-link shape (#342): every read 401s, the re-login 422s, and
  // withPortalSessionRetry rethrows the read's 401 -- not the 422.
  function isExpiredPortalSession(error) {
    const utils = window.etRechargeUtils;
    if (utils?.isExpiredPortalToken?.(error) === true) return true;
    return (
      utils?.isPortalAuthError?.(error) === true &&
      utils?.isPortalLinkExpired?.() === true
    );
  }

  // Without this the shopper just gets the degraded "couldn't find your next
  // order" overview and no reason for it, which is what sends them to support.
  // Once per page view: a dead session fails several concurrent reads, and each
  // one routes through here.
  function notifyPortalSessionExpired() {
    if (state.notifiedPortalSessionExpired) return;
    state.notifiedPortalSessionExpired = true;

    // Reloading cannot help -- the expired credential is in the URL, so it
    // comes back dead. Logging into Shopify is what lets the App Proxy
    // fallback mint a session on the next load.
    showInlineErrorToast(
      'Your subscription session has expired. Please log in to your account again.'
    );
  }

  // Reasons that describe an account state we cannot fix in code -- the shopper
  // genuinely has no next order. They still report, because support needs the
  // breadcrumb, but at warning level so they stay out of the error budget and
  // out of the "most users impacted" ranking (SHOPIFY-3H9).
  // `var`: read by a hoisted builder, and the CDN minifier hoists function
  // declarations above `const` (SHOPIFY-398).
  var OVERVIEW_RETRY_ACTION_MARKUP = `
    <button
      type="button"
      class="et-update-meals-overview-slot__retry-button"
      data-action="et-retry-overview-hydration"
    >
      Try again
    </button>
  `;
  // An expired portal link cannot be retried back to life -- the credential is
  // in the URL -- so the only useful action is logging in again. A button, not
  // a link, so it matches "Try again" (a link takes the portal's body font).
  var OVERVIEW_LOGIN_ACTION_MARKUP = `
    <button
      type="button"
      class="et-update-meals-overview-slot__retry-button"
      data-action="et-portal-login"
    >
      Log in again
    </button>
  `;

  const EXPECTED_CHARGE_MISSING_REASONS = new Set([
    'payment-failed',
    'no-queued-charge',
    'portal-session-expired',
  ]);

  // Our lookup failed, not the account: the totals offer "Try again" rather
  // than telling the customer they have no next order. A function, not a
  // module-level Set: injectSlotTemplate() renders during boot, before any
  // `var` below it is assigned.
  function isRetryableChargeMissingReason(reason) {
    return reason === 'charge-lookup-failed' || reason === 'charge-details-load-failed';
  }

  // Account states the customer can act on, keyed by charge-missing reason:
  // each replaces the totals with a way forward instead of a dead end, and the
  // meals "Update" / "Apply rewards" buttons hide (no order to change). Built
  // per call for the same boot-order reason as above; null for any other reason.
  function resolveOverviewChargeEmptyState(reason, context) {
    if (reason === 'payment-failed') {
      const attemptedOn = formatDisplayDate(
        parseSubscriptionDateValue(context.chargeFailure?.scheduledAt)
      );
      const attempt = attemptedOn
        ? `We tried to charge your card on ${escapeHtml(attemptedOn)}.`
        : 'We couldn&#39;t charge your card for your next order.';
      return {
        heading: 'Your last payment didn&#39;t go through',
        body: `${attempt} Update your payment method and we&#39;ll retry your order.`,
        actions: [
          { label: 'Update payment', action: 'et-update-payment', primary: true },
          { label: 'Contact us', action: 'et-contact-us' },
        ],
      };
    }
    if (reason === 'no-queued-charge') {
      // Reschedule moves the subscription's existing next charge date; with
      // none it can only reply "can't be rescheduled, contact us", so hand off
      // to support directly instead of offering a button that dead-ends.
      if (!context.hasNextChargeDate) {
        return {
          heading: 'You don&#39;t have an upcoming order',
          body: 'Contact us and we&#39;ll help schedule your next order.',
          actions: [{ label: 'Contact us', action: 'et-contact-us', primary: true }],
        };
      }
      const actions = [
        { label: 'Choose a delivery date', action: 'et-reschedule', primary: true },
      ];
      if (context.showViewOtherSubscriptions) {
        actions.push({
          label: 'View other subscriptions',
          action: 'et-view-other-subscriptions',
        });
      }
      return {
        heading: 'You don&#39;t have an upcoming order',
        body: 'Choose a delivery date to schedule your next order.',
        actions,
      };
    }
    return null;
  }

  // Fired when the overview totals render the "couldn't find your next order"
  // state. Deduped per subscription+reason per page view so rerenders and
  // refresh cycles do not spam Sentry.
  function reportOverviewChargeMissing(reason, failure) {
    if (isPortalPageUnloading()) return;
    const observability = getEtObservability();
    if (!observability || typeof observability.captureException !== 'function') {
      return;
    }

    const subscriptionId = state.overviewSlotSubscriptionId || null;
    const reportKey = `${subscriptionId || 'unknown-subscription'}::${reason}`;
    if (state.reportedOverviewChargeMissingKeys.has(reportKey)) return;
    state.reportedOverviewChargeMissingKeys.add(reportKey);

    observability.captureException(
      new Error('Update meals overview has no upcoming charge for the next order'),
      {
        level: EXPECTED_CHARGE_MISSING_REASONS.has(reason) ? 'warning' : 'error',
        // One issue per reason. Every reason throws from this same line, so
        // Sentry's stack-trace grouping otherwise collapses them into a single
        // issue whose user count is dominated by the expected states and tells
        // us nothing about the defects hiding underneath.
        fingerprint: ['next-order-charge-missing', reason],
        tags: {
          surface: 'affinity-update-meals',
          component: 'overview-header-slot',
          issue: 'next-order-charge-missing',
          // Still tagged as well as fingerprinted: the tag is what makes the
          // reasons aggregatable in one Discover query across the split issues.
          chargeMissingReason: reason,
        },
        extra: {
          reason,
          subscriptionId,
          failedChargeId: state.overviewSlotChargeFailure?.chargeId || null,
          failedChargeScheduledAt:
            state.overviewSlotChargeFailure?.scheduledAt || null,
          addressId: state.overviewSlotAddressId || null,
          activeSubscriptionCount: state.overviewSlotActiveSubscriptionCount || 0,
          fulfillmentType: state.overviewSlotFulfillmentType || null,
          pathname: window.location.pathname || '',
          // Only for hydration-failed / portal-session-expired: which read
          // gave up, and how.
          failedStep: failure?.failedStep || null,
          errorClass: failure?.errorClass || null,
        },
      }
    );
  }

  // `failedStep` names the read that gave up (portal-session, list-subscriptions,
  // bundle-selection, ...); `errorClass` is network / auth / http-status /
  // parse / unknown. Together they make a hydration-failed event actionable.
  function describeHydrationFailure(step, error) {
    return {
      failedStep: step || 'unknown',
      errorClass: window.etRechargeUtils?.classifyPortalError?.(error) || 'unknown',
    };
  }


  function clearOverviewHydrationSpanState() {
    state.overviewSlotHydrationSpan = null;
    state.overviewSlotHydrationSpanToken = null;
  }

  function endOverviewHydrationSpan(mountToken, attributes = {}) {
    if (state.overviewSlotHydrationSpanToken !== mountToken) return;

    const span = state.overviewSlotHydrationSpan;
    clearOverviewHydrationSpanState();
    // The handle never throws and adds the request/byte counts itself.
    span?.end(attributes);
  }

  function endActiveOverviewHydrationSpan(attributes = {}) {
    const activeMountToken = state.overviewSlotHydrationSpanToken;
    if (!Number.isFinite(activeMountToken)) {
      clearOverviewHydrationSpanState();
      return;
    }
    endOverviewHydrationSpan(activeMountToken, attributes);
  }

  function startOverviewHydrationSpan(mountToken, attributes = {}, spanOptions = {}) {
    // Only one overview span should be active at a time. A new mount token
    // immediately closes the previous span so Sentry does not keep a stale
    // transaction open after a remount or refresh.
    endActiveOverviewHydrationSpan({
      'et.overview_hydration.outcome': 'superseded',
    });

    // The handle is held across the async hydration branches and ended from
    // the DOM-ready checkpoint (or whichever exit path gets there first).
    state.overviewSlotHydrationSpan =
      window.etRechargeUtils?.startPortalSpan?.('overview', {
        spanName: OVERVIEW_HYDRATION_SPAN.name,
        op: OVERVIEW_HYDRATION_SPAN.op,
        navigationTimes: spanOptions.navigationTimes === true,
        attributes: {
          'et.surface': 'affinity-update-meals',
          'et.component': 'overview-header-slot',
          'et.mount_token': mountToken,
          ...attributes,
        },
      }) || null;
    state.overviewSlotHydrationSpanToken = state.overviewSlotHydrationSpan ? mountToken : null;
  }

  // Why a rendered overview shows an empty state instead of meals or a next
  // order; null when it shows both.
  function resolveOverviewEmptyStateReason() {
    if (state.overviewSlotLoadFailure) return `load-failed:${state.overviewSlotLoadFailure}`;
    if (countOverviewSummaryRenderableItems(state.overviewSlotItems) === 0) {
      return state.overviewSlotItemsLoadFailed ? 'items-load-failed' : 'no-items';
    }
    if (state.overviewSlotChargeResolved && !state.overviewSlotCharge) {
      return state.overviewSlotChargeMissingReason || 'no-next-charge';
    }
    return null;
  }

  // Ends the editor-open span, if one is running.
  function endEditorOpenSpan(attributes) {
    const span = state.editorOpenSpan;
    state.editorOpenSpan = null;
    span?.end({
      'et.portal.catalog_products': Array.isArray(state.addableMealsCatalog)
        ? state.addableMealsCatalog.length
        : 0,
      ...attributes,
    });
  }

  function startEditorOpenSpan() {
    endEditorOpenSpan({ 'et.portal.outcome': 'superseded' });
    const utils = window.etRechargeUtils;
    state.editorOpenSpan =
      utils?.startPortalSpan?.('editor-open', {
        spanName: 'subscription.editor.open',
        attributes: {
          'et.surface': 'affinity-update-meals',
          'et.component': 'update-meals-editor',
          'et.portal.prefetch': describeEditorPrefetch(state.activeSubscriptionId),
        },
        counts: { 'et.portal.product_search': utils.PORTAL_RESOURCE_PATTERNS?.productSearch },
      }) || null;
  }

  // Frames to wait for the first real meal card before closing the span as
  // rendered-without-cards (an empty catalog never paints one), and how long
  // to wait for its on-screen images before closing it anyway.
  var EDITOR_FIRST_CARD_MAX_FRAMES = 120;
  var EDITOR_CARD_IMAGES_TIMEOUT_MS = 10000;

  function hasPaintedEditorMealCard() {
    return Boolean(
      document.querySelector(
        '.et-update-meals-add-card:not(.et-update-meals-add-card--skeleton)'
      )
    );
  }

  // Card images are all loading="lazy": the ones on screen load, the rest are
  // never requested until scrolled to, so only the on-screen ones can settle.
  // Resolves true once those finish (loaded or failed), false on timeout.
  function waitForOnScreenEditorCardImages() {
    const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
    const pending = Array.from(
      document.querySelectorAll('.et-update-meals-add-card__media img')
    ).filter((image) => {
      if (!(image instanceof HTMLImageElement) || image.complete) return false;
      const rect = image.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < viewportHeight;
    });
    if (!pending.length) return Promise.resolve(true);
    const settled = Promise.all(
      pending.map(
        (image) =>
          new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          })
      )
    ).then(() => true);
    const timeout = new Promise((resolve) => {
      window.setTimeout(() => resolve(false), EDITOR_CARD_IMAGES_TIMEOUT_MS);
    });
    return Promise.race([settled, timeout]);
  }

  // Closes the editor-open span once the first meal card is on screen with
  // its image. Waiting on the images matters for more than the timing:
  // Resource Timing lists a request only once it finishes, so ending as the
  // card is inserted would report ~0 image bytes on any real network.
  async function finalizeEditorOpenSpanWhenPainted(span, outcome) {
    const isCurrent = () => state.editorOpenSpan === span && state.editorOpen;
    // An empty catalog (including the catalog-failed path) never paints a
    // card, so polling for one would only pad the recorded duration.
    const expectCards =
      Array.isArray(state.addableMealsCatalog) && state.addableMealsCatalog.length > 0;
    let frames = 0;
    while (
      expectCards &&
      isCurrent() &&
      !hasPaintedEditorMealCard() &&
      frames < EDITOR_FIRST_CARD_MAX_FRAMES
    ) {
      frames += 1;
      await waitForAnimationFrame();
    }
    const painted = hasPaintedEditorMealCard();
    // Two frames after the insert the layout the viewport check reads is real.
    await waitForAnimationFrame();
    await waitForAnimationFrame();
    if (!isCurrent()) return;
    const imagesSettled = await waitForOnScreenEditorCardImages();
    if (!isCurrent()) return;
    endEditorOpenSpan({
      'et.portal.outcome': painted ? outcome : `${outcome}-no-cards`,
      'et.portal.images_settled': imagesSettled,
    });
  }

  function waitForAnimationFrame() {
    return new Promise((resolve) => {
      if (typeof window.requestAnimationFrame !== 'function') {
        window.setTimeout(resolve, 16);
        return;
      }
      window.requestAnimationFrame(() => resolve());
    });
  }

  function countOverviewSummaryRenderableItems(items) {
    return (Array.isArray(items) ? items : []).filter((item) => {
      return parseInteger(item?.quantity ?? 0) > 0;
    }).length;
  }

  function isOverviewSlotSummaryRenderReady(slotElement, expectedRowCount) {
    if (!(slotElement instanceof HTMLElement)) return false;

    const rows = Array.from(
      slotElement.querySelectorAll('.et-update-meals-overview-slot__item')
    );
    if (expectedRowCount === 0) {
      const emptyState = slotElement.querySelector(
        '.et-update-meals-overview-slot__empty'
      );
      return Boolean(String(emptyState?.textContent || '').trim());
    }
    if (!rows.length) return false;
    if (expectedRowCount > 0 && rows.length !== expectedRowCount) {
      return false;
    }

    return rows.every((row) => {
      if (!(row instanceof HTMLElement)) return false;
      if (row.classList.contains('et-update-meals-overview-slot__item--skeleton')) {
        return false;
      }

      const label = row.querySelector('.et-update-meals-overview-slot__item-label');
      const detail = row.querySelector('.et-update-meals-overview-slot__item-detail');
      const image = row.querySelector('.et-update-meals-overview-slot__item-image');
      const labelText = String(label?.textContent || '').trim();
      const detailText = String(detail?.textContent || '').trim();
      const imageSrc = String(
        image instanceof HTMLImageElement
          ? image.currentSrc || image.getAttribute('src') || ''
          : ''
      ).trim();

      return Boolean(labelText && detailText.includes(' x ') && imageSrc);
    });
  }

  function areOverviewSlotSummaryImagesSettled(slotElement) {
    if (!(slotElement instanceof HTMLElement)) return false;

    const images = Array.from(
      slotElement.querySelectorAll('.et-update-meals-overview-slot__item-image')
    ).filter((image) => image instanceof HTMLImageElement);

    // Lazy rows below the fold are never requested until scrolled to, so their
    // `complete` stays false and would hold the span open forever (#340).
    return images.every((image) => image.complete || image.loading === 'lazy');
  }

  // Once the overview is usable -- its rows rendered and the next charge
  // settled -- the next idle moment dispatches `ET::overview::prefetch` so
  // each action can warm what its click loads (#363). Called from both of
  // those points; announces once per mount, and never while the editor is
  // open. Narrower than the header's buttons on purpose: an overview that
  // failed to load, or has no next order, has nothing worth warming.
  function announceOverviewPrefetchWhenUsable(mountToken) {
    if (state.overviewSlotMountToken !== mountToken) return;
    if (state.overviewSlotUsableAnnouncedToken === mountToken) return;
    if (state.overviewSlotHydrationFinalizedToken !== mountToken) return;
    if (!state.overviewSlotChargeResolved || state.editorOpen) return;
    state.overviewSlotUsableAnnouncedToken = mountToken;

    window.etRechargeUtils?.runWhenIdleForPrefetch?.(() => {
      if (state.overviewSlotMountToken !== mountToken || state.editorOpen) return;
      const charge = state.overviewSlotCharge;
      const updateMeals =
        Boolean(charge) &&
        !state.overviewSlotLoadFailure &&
        !state.overviewSlotItemsLoadFailed;
      document.dispatchEvent(
        new CustomEvent('ET::overview::prefetch', {
          detail: {
            subscriptionId: state.overviewSlotSubscriptionId,
            updateMeals,
            applyRewards: updateMeals && !chargeHasDiscount(charge),
          },
        })
      );
    });
  }

  function handleOverviewPrefetch(event) {
    const detail = event?.detail || {};
    if (!detail.updateMeals) return;
    prefetchEditorData(parseOptionalSubscriptionId(detail.subscriptionId));
  }

  // Warms what "Update" loads before its first render (#363), once per
  // subscription until a refresh or navigation forgets it. The results live
  // only in the caches the click path already reads -- the read memo (the
  // subscription's reads for their TTL, the price and fee product_search for
  // the page view) and the Storefront product and catalog-page caches -- so
  // the click path runs unchanged and joins whatever is still in flight.
  function prefetchEditorData(subscriptionId) {
    if (!subscriptionId || state.editorOpen) return;
    if (state.editorPrefetch?.subscriptionId === subscriptionId) return;
    const entry = { subscriptionId, settled: false, failed: false };
    state.editorPrefetch = entry;
    (async () => {
      const { session, subscription, editableItems } =
        await loadEditableSubscription(subscriptionId);
      // An editor opened meanwhile loads its catalog itself.
      if (state.editorOpen) return;
      const [catalogState] = await Promise.all([
        loadAddableMealsCatalog(buildVariantProfile(editableItems), {
          portalSession: session,
          activeItems: editableItems,
        }),
        // As on the click path, the editor opens without it, so a failed fee
        // lookup does not stop the rest of the warm-up.
        resolveOperationalFeeContext(session, subscription, editableItems).catch(() => null),
      ]);
      // The click hydrates its items again from the catalog, and that pass
      // can look up another set of variants (e.g. with a meal off the menu).
      await hydrateBundleItemsFromStorefrontProductsById(
        hydrateBundleItemsFromCatalogMeals(editableItems, catalogState.meals),
        session
      );
    })().then(
      () => {
        entry.settled = true;
      },
      (error) => {
        // Kept, so remounts do not retry what will most likely fail again
        // (no bundle selection, no Storefront token) until a refresh or
        // navigation forgets it. The click path reads again and reports its
        // own failures.
        entry.failed = true;
        console.warn('[update-meals] Unable to prefetch the meals editor.', error);
      }
    );
  }

  function forgetEditorPrefetch() {
    state.editorPrefetch = null;
  }

  // How far the editor prefetch had got when "Update" was clicked, for the
  // editor-open span.
  function describeEditorPrefetch(subscriptionId) {
    const entry = state.editorPrefetch;
    if (!entry) return 'none';
    if (entry.subscriptionId !== subscriptionId) return 'other-subscription';
    if (entry.failed) return 'failed';
    return entry.settled ? 'settled' : 'in-flight';
  }

  async function finalizeOverviewHydrationSpanIfReady(mountToken) {
    if (state.overviewSlotMountToken !== mountToken) return;
    if (state.editorOpen) return;
    // The last render's rows look ready, but the span waits for fresh ones.
    if (state.overviewSlotHoldingRender) return;
    if (state.overviewSlotHydrationFinalizedToken === mountToken) return;
    if (state.overviewSlotHydrationFinalizePendingToken === mountToken) return;

    const expectedRowCount = countOverviewSummaryRenderableItems(
      state.overviewSlotItems
    );
    const slotElement = resolveMountedOverviewHeaderSlotElement();
    if (!isOverviewSlotSummaryRenderReady(slotElement, expectedRowCount)) {
      return;
    }

    state.overviewSlotHydrationFinalizePendingToken = mountToken;

    try {
      // The span closes only when the visible summary is ready, not when the
      // data promises resolve. That keeps the measurement aligned with what the
      // customer actually sees on screen.
      while (state.overviewSlotMountToken === mountToken && !state.editorOpen) {
        const currentExpectedRowCount = countOverviewSummaryRenderableItems(
          state.overviewSlotItems
        );
        const currentSlot = resolveMountedOverviewHeaderSlotElement();
        if (
          !isOverviewSlotSummaryRenderReady(
            currentSlot,
            currentExpectedRowCount
          )
        ) {
          return;
        }
        if (areOverviewSlotSummaryImagesSettled(currentSlot)) {
          break;
        }
        await waitForAnimationFrame();
      }

      if (state.overviewSlotMountToken !== mountToken || state.editorOpen) return;

      await waitForAnimationFrame();
      await waitForAnimationFrame();

      if (state.overviewSlotMountToken !== mountToken || state.editorOpen) return;

      const currentSlot = resolveMountedOverviewHeaderSlotElement();
      if (
        !isOverviewSlotSummaryRenderReady(
          currentSlot,
          countOverviewSummaryRenderableItems(state.overviewSlotItems)
        )
      ) {
        return;
      }
      if (!areOverviewSlotSummaryImagesSettled(currentSlot)) return;

      state.overviewSlotHydrationFinalizedToken = mountToken;
      // Record the final render outcome and useful summary attributes on the
      // same span before ending it.
      endOverviewHydrationSpan(mountToken, {
        'et.overview_hydration.outcome': 'rendered',
        'et.overview_hydration.subscription_id':
          state.overviewSlotSubscriptionId || 'unknown',
        'et.overview_hydration.item_count':
          countOverviewSummaryRenderableItems(state.overviewSlotItems),
        'et.overview_hydration.active_subscription_count':
          Number.isFinite(state.overviewSlotActiveSubscriptionCount)
            ? state.overviewSlotActiveSubscriptionCount
            : 0,
        'et.overview_hydration.has_delivery_context':
          Boolean(state.overviewSlotDeliveryContext),
        'et.overview_hydration.fulfillment_type':
          String(state.overviewSlotFulfillmentType || '').trim() || 'unknown',
        'et.portal.items': countOverviewSummaryRenderableItems(state.overviewSlotItems),
        'et.portal.reason': resolveOverviewEmptyStateReason(),
      });
      announceOverviewPrefetchWhenUsable(mountToken);
    } finally {
      if (state.overviewSlotHydrationFinalizePendingToken === mountToken) {
        state.overviewSlotHydrationFinalizePendingToken = null;
      }
    }
  }

  function markOverviewColumnsSingleLayout(slotElement) {
    const wrapper = resolveOverviewColumnsWrapper(slotElement);
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.setAttribute('data-et-overview-layout', 'single');

    wrapper.style.setProperty('--rc-columns-total', '1', 'important');
    wrapper.style.setProperty('--rc-columns-n', '1', 'important');
    wrapper.style.setProperty('--rc-columns-gap', '0', 'important');

    Array.from(wrapper.children).forEach((child, index) => {
      if (!(child instanceof HTMLElement)) return;
      if (index === 0) {
        child.style.setProperty('width', '100%', 'important');
        child.style.setProperty('max-width', 'none', 'important');
        child.style.removeProperty('display');
        return;
      }
      child.style.setProperty('display', 'none', 'important');
    });
  }

  /**
   * Hydrates overview slot data for summary mode.
   *
   * Data read order:
   * 1) Resolve portal session and subscriptions.
   * 2) Pick "primary" subscription for overview (closest upcoming active record).
   * 3) Resolve bundle selection and normalize bundle items.
   * 4) Filter to user-visible meal lines and resolve currency.
   * 5) Commit only if mount token still matches active mount.
   */
  function resolveChargeTimestamp(charge, subscription) {
    const sharedDateResolver = window.etRechargeUtils?.resolveChargeDateValue;
    const parsed =
      typeof sharedDateResolver === 'function'
        ? sharedDateResolver(charge, subscription)
        : parseSubscriptionDateValue(
            charge.scheduled_at || charge.charge_date || charge.created_at
          );
    return parsed ? parsed.getTime() : Number.MAX_SAFE_INTEGER;
  }

  /**
   * Charges belonging to one subscription, soonest scheduled first.
   *
   * Address match is preferred because a multi-address customer can hold several
   * charges for the same purchase item; the flat `subscription_id` shape is kept
   * as a fallback for older payload versions.
   */
  function findSubscriptionCharges(charges, subscriptionId, addressId, subscription) {
    if (!Array.isArray(charges) || charges.length === 0) return [];

    const byAddress = addressId
      ? charges.filter((charge) => {
          if (String(charge.address_id) !== String(addressId)) return false;
          if (!Array.isArray(charge.line_items)) return false;
          return charge.line_items.some((lineItem) => {
            if (!lineItem) return false;
            return String(lineItem.purchase_item_id) === String(subscriptionId);
          });
        })
      : [];

    const bySubscriptionId = charges.filter((charge) => {
      return String(charge.subscription_id) === String(subscriptionId);
    });

    const candidates = byAddress.length ? byAddress : bySubscriptionId;
    if (candidates.length < 2) return candidates;

    return candidates.slice().sort((left, right) => {
      return (
        resolveChargeTimestamp(left, subscription) -
        resolveChargeTimestamp(right, subscription)
      );
    });
  }

  /**
   * Looks for a failed (`error` status) charge on this subscription, so the
   * "no next order" state can name a failed payment as the cause.
   *
   * Runs only when no queued charge was found, so the happy path pays nothing.
   * Degrades to null on any lookup failure: a missing reason still renders the
   * existing generic copy.
   */
  async function resolveFailedChargeForSubscription(
    subscriptionId,
    addressId,
    subscription
  ) {
    const response = await withPortalSessionRetry((activeSession) =>
      listCharges(activeSession, {
        status: 'error',
        limit: 25,
        sort_by: 'scheduled_at-desc',
      })
    ).catch((error) => {
      console.warn('[update-meals] Unable to load failed charge data.', error);
      return null;
    });
    if (!response) return null;

    const charges = Array.isArray(response) ? response : response.charges || [];
    const matching = findSubscriptionCharges(
      charges,
      subscriptionId,
      addressId,
      subscription
    );
    if (!matching.length) return null;

    // Most recent failure is the one worth explaining to the customer.
    const charge = matching[matching.length - 1];
    return {
      chargeId: charge.id || null,
      scheduledAt: charge.scheduled_at || charge.charge_date || null,
    };
  }

  /**
   * Loads everything the overview slot renders, apart from rendering it
   * (#361), so the reads can start when this script runs instead of when
   * Affinity mounts the slot -- on a phone, seconds later. Boot and mount both
   * call it: every ReCharge read goes through the shared read memo and the
   * Storefront lookup through its own cache, so the mount's load joins the
   * boot's reads rather than repeating them.
   *
   * It touches no DOM and reports nothing of its own: the renderer reports, so
   * a load nobody renders stays silent and a rendered one reports each failure
   * once. (recharge-utils still reports a failed session refresh wherever it
   * happens, boot included.) Nothing in it rejects; failures are part of the
   * model.
   *
   * It settles in the stages the renderer consumes:
   * - `subscriptions`: the list read, or null when it failed. Separate so the
   *   no-active-subscription redirect never waits on the rest.
   * - `core`: the picked subscription and its bundle items, carrying two
   *   promises that settle later still -- `supplement` (address, next charge,
   *   delivery context) and `storefrontItems` (items with Storefront metadata).
   * `settled` turns true once all of them have.
   */
  function loadOverviewModel(preferredSubscriptionId) {
    const listing = listOverviewSubscriptions();
    const model = {
      preferredSubscriptionId,
      settled: false,
      // Set on the boot model once the read memo drops what it read.
      readsDiscarded: false,
      subscriptions: listing.then((listed) => listed.subscriptions),
      core: listing.then((listed) => loadOverviewModelCore(preferredSubscriptionId, listed)),
    };
    const markSettled = () => {
      model.settled = true;
    };
    model.core
      .then((core) => Promise.all([core.supplement, core.storefrontItems]))
      .then(markSettled, markSettled);
    return model;
  }

  // Resolves `{ session, subscriptions }`, or `{ subscriptions: null, failure }`
  // when the login or the list read gave up.
  async function listOverviewSubscriptions() {
    let session = null;
    // Which call a failure is recovering from, for the report. The session is
    // minted first, so until the list callback runs, a failure is the login
    // (SHOPIFY-3HC).
    let step = 'portal-session';
    try {
      const subscriptions = await withPortalSessionRetry((activeSession) => {
        // Bind outward: on a retry this is the freshly minted session, and
        // every later call in this load has to use it rather than the dead
        // token that just failed (SHOPIFY-3HH).
        session = activeSession;
        step = 'list-subscriptions';
        return getSubscriptions(activeSession);
      });
      return {
        session,
        subscriptions: Array.isArray(subscriptions) ? subscriptions : [],
        failure: null,
      };
    } catch (error) {
      return { session: null, subscriptions: null, failure: { step, error } };
    }
  }

  function isActiveSubscription(subscription) {
    return (
      String(subscription?.status || '')
        .trim()
        .toLowerCase() === 'active'
    );
  }

  // The cancel flow's redirect for a customer with no active subscription:
  // only on the overview, and only once recharge-subscription-cancel.js ran.
  function getNoActiveSubscriptionRedirect() {
    if (!isOverviewPath(window.location.pathname)) return null;
    const cancelApi = window.etRechargeSubscriptionCancel;
    if (!cancelApi || typeof cancelApi.redirectToSubscriptionCreateIfNoActive !== 'function') {
      return null;
    }
    return () => cancelApi.redirectToSubscriptionCreateIfNoActive(0);
  }

  async function loadOverviewModelCore(preferredSubscriptionId, listed) {
    const core = {
      subscriptionId: null,
      preferredSubscriptionMissing: false,
      addressId: null,
      fulfillmentType: null,
      items: [],
      currencyCode: DEFAULT_CURRENCY_CODE,
      supplement: null,
      storefrontItems: null,
      // Set when the bundle-selection lookup fails. Items fail on their own:
      // totals and the next-order date do not depend on them.
      itemsFailure: null,
      failure: listed.failure,
    };
    if (listed.failure) return core;

    const subscriptions = listed.subscriptions;
    let session = listed.session;
    let step = 'select-subscription';
    try {
      // The renderer redirects away; reading on would only race the navigation.
      if (!subscriptions.some(isActiveSubscription) && getNoActiveSubscriptionRedirect()) {
        return core;
      }

      let primarySubscription = null;
      if (preferredSubscriptionId) {
        primarySubscription =
          subscriptions.find((entry) => {
            const id = parseOptionalSubscriptionId(
              entry?.id || entry?.subscription_id
            );
            return id === preferredSubscriptionId;
          }) || null;
        core.preferredSubscriptionMissing = !primarySubscription;
      }

      if (!primarySubscription) {
        primarySubscription = pickPrimaryOverviewSubscription(subscriptions);
      }

      const forcedSubscriptionId = core.preferredSubscriptionMissing
        ? null
        : preferredSubscriptionId;
      const subscriptionId =
        forcedSubscriptionId ||
        parseOptionalSubscriptionId(
          primarySubscription?.id || primarySubscription?.subscription_id
        );
      core.subscriptionId = subscriptionId;

      if (subscriptionId && session) {
        let hydratedSubscription = null;
        if (!forcedSubscriptionId && primarySubscription) {
          const primaryId = parseOptionalSubscriptionId(primarySubscription.id);
          hydratedSubscription =
            primaryId === subscriptionId ? primarySubscription : null;
        }

        if (!hydratedSubscription) {
          // Usually served by the list's seed (#362), but a subscription the
          // list could not seed goes to the network, where it can meet a dead
          // token. Its .catch swallows the failure into a silent fallback, so
          // without the retry the overview quietly degrades instead of
          // recovering.
          hydratedSubscription = await withPortalSessionRetry((activeSession) => {
            session = activeSession;
            return getSubscriptionById(activeSession, subscriptionId);
          }).catch(() => {
            return primarySubscription || null;
          });
        }

        if (hydratedSubscription && typeof hydratedSubscription === 'object') {
          const addressId = resolveSubscriptionAddressId(hydratedSubscription) || null;
          core.addressId = addressId;

          // These three fire concurrently and every one of them swallows its
          // failure, so on a warm read memo -- which the slot-remount path
          // never clears -- the
          // first request to meet a dead token lands here and the customer just
          // gets a degraded overview. Each is retried; the shared refresh latch
          // collapses the three re-auths into one login.
          const addressPromise = withPortalSessionRetry((activeSession) => {
            session = activeSession;
            return resolveSubscriptionAddress(activeSession, hydratedSubscription);
          }).catch(() => null);
          core.fulfillmentType = extractFulfillmentTypeFromEntity(hydratedSubscription) || null;
          const fulfillmentTypePromise = core.fulfillmentType
            ? Promise.resolve(core.fulfillmentType)
            : addressPromise
                .then((address) => extractFulfillmentTypeFromEntity(address))
                .catch(() => null);
          // Kept for the renderer to report: the load itself reports nothing.
          let chargeListError = null;
          const chargesPromise = withPortalSessionRetry((activeSession) => {
            session = activeSession;
            return listCharges(activeSession, {
              status: 'queued',
              limit: 250,
              sort_by: 'scheduled_at-asc',
            });
          }).catch((chargeError) => {
            console.warn('[update-meals] Unable to load charge data.', chargeError);
            chargeListError = chargeError;
            // Sentinel keeps "lookup failed" distinguishable from "no queued
            // charges" so diagnostics do not misclassify the outcome.
            return { __chargeLookupFailed: true };
          });
          const bundleSelectionIdPromise = withPortalSessionRetry((activeSession) => {
            session = activeSession;
            return resolveBundleSelectionId(activeSession, hydratedSubscription);
          }).catch((error) => {
            core.itemsFailure = { step: 'list-bundle-selections', error };
            return null;
          });

          core.supplement = Promise.all([
            addressPromise,
            fulfillmentTypePromise,
            chargesPromise,
          ])
            .then(async ([address, fulfillmentType, chargesResponse]) => {
              let nextResolvedAddressId = addressId;
              if (!nextResolvedAddressId) {
                const fallbackAddressId = parseOptionalSubscriptionId(
                  address?.id || address?.address_id
                );
                if (fallbackAddressId) {
                  nextResolvedAddressId = fallbackAddressId;
                }
              }

              const chargeLookupFailed = Boolean(
                chargesResponse && chargesResponse.__chargeLookupFailed
              );
              const charges = chargeLookupFailed
                ? []
                : Array.isArray(chargesResponse)
                  ? chargesResponse
                  : (chargesResponse && chargesResponse.charges) || [];
              const matchingCharges = findSubscriptionCharges(
                charges,
                subscriptionId,
                nextResolvedAddressId,
                hydratedSubscription
              );
              // Soonest scheduled charge is the "next order".
              let nextOverviewCharge = matchingCharges.length
                ? matchingCharges[0]
                : null;

              // Only reached when nothing is queued. A failed payment leaves the
              // charge in `error` status, which the queued-only lookup above can
              // never return, so the totals used to render "we couldn't find your
              // next order" with no cause. Recharge's own payment recovery owns
              // the remedy -- we just name the reason and hand off to support.
              let chargeFailure = null;
              if (!nextOverviewCharge && !chargeLookupFailed) {
                chargeFailure = await resolveFailedChargeForSubscription(
                  subscriptionId,
                  nextResolvedAddressId,
                  hydratedSubscription
                );
              }

              let nextDeliveryContext = null;
              try {
                nextDeliveryContext = await resolveOverviewDeliveryContext(
                  session,
                  hydratedSubscription,
                  {
                    fulfillmentType,
                    address,
                  }
                );
              } catch (error) {
                console.warn(
                  '[update-meals] Unable to resolve overview delivery context.',
                  error
                );
              }

              return {
                details: {
                  address: address || null,
                  deliveryContext: nextDeliveryContext,
                  fulfillmentType: fulfillmentType || null,
                  nextCharge: nextOverviewCharge,
                  chargeFailure,
                  chargeLookupFailed,
                  hasNextChargeDate: Boolean(extractNextChargeDate(hydratedSubscription)),
                  resolvedAddressId: nextResolvedAddressId || null,
                },
                chargeListError,
              };
            })
            .catch((error) => {
              console.warn(
                '[update-meals] Unable to resolve deferred overview details.',
                error
              );
              return { details: null, chargeListError, detailsError: error };
            });

          const bundleSelectionId = await bundleSelectionIdPromise;

          if (bundleSelectionId) {
            step = 'bundle-selection';
            const bundleSelection = await withPortalSessionRetry((activeSession) => {
              session = activeSession;
              return getBundleSelectionById(activeSession, bundleSelectionId);
            }).catch((error) => {
              core.itemsFailure = { step: 'bundle-selection', error };
              return null;
            });
            if (bundleSelection && typeof bundleSelection === 'object') {
              step = 'normalize-bundle-items';
              const normalizedItems = normalizeEditableBundleItems(bundleSelection);
              core.storefrontItems =
                hydrateBundleItemsFromStorefrontProductsById(normalizedItems, session)
                  .then((hydratedItems) =>
                    Array.isArray(hydratedItems) ? hydratedItems : null
                  )
                  .catch((error) => {
                    console.warn(
                      '[update-meals] Deferred Storefront hydration failed.',
                      error
                    );
                    return null;
                  });
              core.items = normalizedItems;
              core.currencyCode = resolveCurrencyCode(
                hydratedSubscription,
                bundleSelection,
                normalizedItems
              );
            }
          } else {
            core.currencyCode = resolveCurrencyCode(hydratedSubscription, null, []);
          }
        }
      }
    } catch (error) {
      core.failure = { step, error };
    }
    return core;
  }

  /**
   * Renders a model from loadOverviewModel into the mounted slot: the same
   * three passes as always (items, then Storefront metadata, then the next
   * charge), each committed only while `mountToken` is still the current
   * mount, so a model that outlives its mount never renders into the next.
   */
  async function renderOverviewModel(mountToken, model) {
    // Reported whether or not this mount is still current, as they always
    // were: the reads did fail. Hooked ahead of every token check below.
    model.core.then((core) => {
      if (!core.supplement) return;
      core.supplement.then((supplement) => {
        if (supplement.chargeListError) {
          reportOverviewSwallowedFailure('charge-list-load-failed', supplement.chargeListError, {
            subscriptionId: core.subscriptionId,
            ...describeHydrationFailure('list-queued-charges', supplement.chargeListError),
          });
        }
        if (supplement.detailsError) {
          reportOverviewSwallowedFailure('overview-details-load-failed', supplement.detailsError, {
            subscriptionId: core.subscriptionId,
          });
        }
      });
    });

    const subscriptions = await model.subscriptions;
    if (state.overviewSlotMountToken !== mountToken) {
      return;
    }
    if (subscriptions) {
      const activeSubscriptionCount = subscriptions.filter(isActiveSubscription).length;
      state.overviewSlotActiveSubscriptionCount = activeSubscriptionCount;
      state.overviewSlotSubscriptions = subscriptions;

      const redirect =
        activeSubscriptionCount === 0 ? getNoActiveSubscriptionRedirect() : null;
      if (redirect) {
        redirect();
        endOverviewHydrationSpan(mountToken, {
          'et.overview_hydration.outcome': 'redirected-no-active-subscription',
        });
        return;
      }
    }

    const core = await model.core;
    const subscriptionId = core.subscriptionId;
    let hydrationFailure = null;
    let hydrationFailureKind = null;
    if (core.failure) {
      const error = core.failure.error;
      hydrationFailure = describeHydrationFailure(core.failure.step, error);
      hydrationFailureKind = isExpiredPortalSession(error)
        ? 'session-expired'
        : 'retryable';
      console.warn(
        '[update-meals] Unable to resolve overview header subscription.',
        error
      );
      reportOverviewSwallowedFailure('overview-hydration-failed', error, {
        subscriptionId,
        ...hydrationFailure,
      });
    }

    if (state.overviewSlotMountToken !== mountToken) {
      return;
    }
    // Unless a refresh has chosen another subscription since this load began.
    if (
      core.preferredSubscriptionMissing &&
      state.preferredOverviewSubscriptionId === model.preferredSubscriptionId
    ) {
      state.preferredOverviewSubscriptionId = null;
    }
    if (core.addressId) {
      state.overviewSlotAddressId = core.addressId;
    }
    state.overviewSlotFulfillmentType = core.fulfillmentType;
    // Only a failed lookup offers "Try again": a subscription that genuinely
    // has no bundle selection keeps its "No subscription items found." copy.
    // A whole-overview failure already says it for the items too.
    const itemsFailure = core.itemsFailure;
    const itemsLoadFailed = Boolean(itemsFailure) && !hydrationFailure;
    if (itemsLoadFailed) {
      reportOverviewSwallowedFailure('overview-items-load-failed', itemsFailure.error, {
        subscriptionId,
        ...describeHydrationFailure(itemsFailure.step, itemsFailure.error),
      });
    }
    state.overviewSlotItemsLoadFailed = itemsLoadFailed;
    state.overviewSlotLoadFailure = hydrationFailureKind;
    state.overviewSlotSubscriptionId = subscriptionId;
    state.overviewSlotItems = filterOverviewSummaryItems(core.items);
    state.overviewSlotCurrencyCode = core.currencyCode;
    window.etUpdateMealsOverviewSubscriptionId = subscriptionId;
    if (!core.supplement) {
      // Nothing else will resolve the upcoming charge for this mount: drop any
      // stale preserved charge and settle, so totals show the explicit "no next
      // order" state instead of an endless skeleton.
      state.overviewSlotCharge = null;
      state.overviewSlotChargeResolved = true;
      // No charge to compare a later write's re-reads against.
      state.serverChargeDiscount = null;
      state.chargeRecalculationRetriesLeft = 0;
      // An expired link is an account state, not our defect: its own reason
      // keeps it out of hydration-failed and at warning level.
      const chargeMissingReason = {
        'session-expired': 'portal-session-expired',
        retryable: 'hydration-failed',
      }[hydrationFailureKind] || 'charge-lookup-not-started';
      state.overviewSlotChargeMissingReason = chargeMissingReason;
      reportOverviewChargeMissing(chargeMissingReason, hydrationFailure);
    }
    renderOverviewHeaderSlot({
      isLoading: false,
      subscriptionId: state.overviewSlotSubscriptionId,
      items: state.overviewSlotItems,
      currencyCode: state.overviewSlotCurrencyCode,
      charge: null,
      deliveryContext: null,
      fulfillmentType: state.overviewSlotFulfillmentType,
    });
    finalizeOverviewHydrationSpanIfReady(mountToken);

    if (core.storefrontItems) {
      core.storefrontItems.then((hydratedItems) => {
        if (!Array.isArray(hydratedItems)) return;
        if (state.overviewSlotMountToken !== mountToken) return;
        if (state.editorOpen) return;

        const nextOverviewItems = filterOverviewSummaryItems(hydratedItems);
        state.overviewSlotItems = nextOverviewItems;
        renderOverviewHeaderSlot({
          isLoading: false,
          subscriptionId: state.overviewSlotSubscriptionId,
          items: state.overviewSlotItems,
          currencyCode: state.overviewSlotCurrencyCode,
          charge: state.overviewSlotCharge,
          deliveryContext: state.overviewSlotDeliveryContext,
          fulfillmentType: state.overviewSlotFulfillmentType,
        });
        finalizeOverviewHydrationSpanIfReady(mountToken);
      });
    }

    if (core.supplement) {
      core.supplement.then((supplement) => {
        if (state.overviewSlotMountToken !== mountToken) return;

        const resolvedDetails = supplement.details;
        if (resolvedDetails) {
          if (resolvedDetails.resolvedAddressId) {
            state.overviewSlotAddressId = resolvedDetails.resolvedAddressId;
          }
          state.overviewSlotAddress = resolvedDetails.address || null;
          state.overviewSlotFulfillmentType = resolvedDetails.fulfillmentType || null;
          state.overviewSlotCharge = resolvedDetails.nextCharge || null;
          state.overviewSlotChargeFailure = resolvedDetails.chargeFailure || null;
          state.overviewSlotHasNextChargeDate = Boolean(resolvedDetails.hasNextChargeDate);
          state.overviewSlotDeliveryContext = resolvedDetails.deliveryContext || null;
        }

        // The lookup has settled either way; renders may now replace the totals
        // skeleton with either real totals or the "no next order" message.
        state.overviewSlotChargeResolved = true;
        state.overviewSlotChargeMissingReason = null;
        if (!state.overviewSlotCharge) {
          let missingChargeReason = 'charge-details-load-failed';
          if (resolvedDetails) {
            if (resolvedDetails.chargeLookupFailed) {
              missingChargeReason = 'charge-lookup-failed';
            } else if (state.overviewSlotChargeFailure) {
              missingChargeReason = 'payment-failed';
            } else {
              missingChargeReason = 'no-queued-charge';
            }
          }
          state.overviewSlotChargeMissingReason = missingChargeReason;
          reportOverviewChargeMissing(missingChargeReason);
        }

        if (state.editorOpen) return;

        renderOverviewHeaderSlot({
          isLoading: false,
          subscriptionId: state.overviewSlotSubscriptionId,
          items: state.overviewSlotItems,
          currencyCode: state.overviewSlotCurrencyCode,
          charge: state.overviewSlotCharge,
          deliveryContext: state.overviewSlotDeliveryContext,
          fulfillmentType: state.overviewSlotFulfillmentType,
        });
        finalizeOverviewHydrationSpanIfReady(mountToken);
        announceOverviewPrefetchWhenUsable(mountToken);
        revalidateChargeWhileRecalculating(mountToken);
      });
    }
  }

  // ReCharge recalculates a charge's totals a moment after a discount is
  // added to or removed from its address. A read in between gets the new
  // code with the old totals -- seen live: code removed, total_discounts
  // still "5.00"; on apply, the code with "0.00" -- and nothing reads the
  // charge again, so the old discount stayed on screen until a reload. So
  // after a write, re-read the charge a few times until it has caught up.
  var CHARGE_RECALCULATION_RETRY_DELAYS_MS = [1500, 3000, 6000];

  // The discount a charge shows, as the totals render reads it: every code
  // (a second reward on a charge that already has one changes only the set),
  // keyed to the charge so another subscription's never compares equal.
  function describeChargeDiscount(charge) {
    const { totalAmount, codes } = resolveChargeDiscountMeta(charge || {});
    return {
      chargeId: String(charge?.id ?? ''),
      codes: codes.slice().sort().join(','),
      total: totalAmount,
    };
  }

  // Mid-recalculation is the code changed since the last charge ReCharge
  // returned before the write, with the total not yet following. Codes and
  // totals merely disagreeing is not enough: some charges always do (a code
  // attached with a 0.00 total), and treating that as a recalculation re-read
  // them three times after every write and held the overview for ~10 s
  // (production, 2026-09-28).
  function isChargeMidRecalculation(charge) {
    const before = state.serverChargeDiscount;
    if (state.chargeRecalculationRetriesLeft <= 0 || !before) return false;
    if (!charge || typeof charge !== 'object') return false;
    const now = describeChargeDiscount(charge);
    return now.chargeId === before.chargeId && now.codes !== before.codes && now.total === before.total;
  }

  // Only a refresh (which follows a write) arms these re-reads, against the
  // charge as ReCharge last returned it (state.serverChargeDiscount).
  function revalidateChargeWhileRecalculating(mountToken) {
    // Settled, or out of re-reads (isChargeMidRecalculation is false then):
    // this charge is the server's now. Without adopting it when re-reads run
    // out, a charge that never "settles" (a code whose total stays put) would
    // keep the stale comparison, and every later write would re-read and hold
    // the overview all over again.
    if (!isChargeMidRecalculation(state.overviewSlotCharge)) {
      state.chargeRecalculationRetriesLeft = 0;
      state.serverChargeDiscount = state.overviewSlotCharge
        ? describeChargeDiscount(state.overviewSlotCharge)
        : null;
      return;
    }
    const delay =
      CHARGE_RECALCULATION_RETRY_DELAYS_MS[
        CHARGE_RECALCULATION_RETRY_DELAYS_MS.length - state.chargeRecalculationRetriesLeft
      ];
    // A re-read still pending belongs to an earlier hydration (every
    // hydration takes a new mount token), so it would bail when it fires:
    // this hydration's re-read replaces it.
    if (state.chargeRecalculationTimerId !== null) {
      window.clearTimeout(state.chargeRecalculationTimerId);
    }
    state.chargeRecalculationRetriesLeft -= 1;
    state.chargeRecalculationTimerId = window.setTimeout(() => {
      state.chargeRecalculationTimerId = null;
      if (state.overviewSlotMountToken !== mountToken || state.editorOpen) return;
      if (!isOverviewHeaderSlotMounted()) return;
      window.etRechargeUtils?.invalidateRechargeReads?.('charge.');
      requestOverviewSlotHydration({ preserveExistingContent: true, holdExistingContent: true, trigger: 'charge-reread' });
    }, delay);
  }

  function filterOverviewSummaryItems(items) {
    const safeItems = Array.isArray(items) ? items : [];
    return safeItems.filter((item) => {
      const quantity = parseInteger(item?.quantity ?? 0);
      if (quantity <= 0 || item?.__isLockedFee) return false;

      const configuredFeeType = resolveConfiguredFeeType(item);
      return !configuredFeeType;
    });
  }

  /**
   * Chooses one subscription to represent "Your next order".
   *
   * Selection policy:
   * - Prefer active subscriptions.
   * - Prefer subscriptions with a resolvable address (more complete records).
   * - Sort by next charge timestamp and pick earliest upcoming.
   */
  function pickPrimaryOverviewSubscription(subscriptions) {
    const safeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];
    if (!safeSubscriptions.length) return null;

    const activeSubscriptions = safeSubscriptions.filter(isActiveSubscription);
    const prioritized = activeSubscriptions.length
      ? activeSubscriptions
      : safeSubscriptions;

    const withAddress = prioritized.filter((entry) =>
      Boolean(resolveSubscriptionAddressId(entry))
    );
    const sortable = (withAddress.length ? withAddress : prioritized).slice();

    sortable.sort((left, right) => {
      const leftTimestamp = parseSubscriptionNextChargeTimestamp(left);
      const rightTimestamp = parseSubscriptionNextChargeTimestamp(right);
      return leftTimestamp - rightTimestamp;
    });

    return sortable[0] || null;
  }

  function parseSubscriptionNextChargeTimestamp(subscription) {
    const candidates = [
      subscription?.next_charge_date,
      subscription?.next_charge_scheduled_at,
      subscription?.next_charge_at,
      subscription?.nextChargeDate,
      subscription?.nextChargeScheduledAt,
      subscription?.nextChargeAt,
    ];

    for (const candidate of candidates) {
      const parsed = parseSubscriptionDateValue(candidate);
      if (parsed) return parsed.getTime();
    }

    return Number.MAX_SAFE_INTEGER;
  }

  function parseSubscriptionDateValue(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = parseInteger(match[1], Number.NaN);
      const monthIndex = parseInteger(match[2], Number.NaN) - 1;
      const day = parseInteger(match[3], Number.NaN);
      if (
        Number.isFinite(year) &&
        Number.isFinite(monthIndex) &&
        Number.isFinite(day)
      ) {
        const parsed = new Date(year, monthIndex, day);
        if (
          parsed.getFullYear() === year &&
          parsed.getMonth() === monthIndex &&
          parsed.getDate() === day
        ) {
          return parsed;
        }
      }
    }

    const timestamp = Date.parse(raw);
    if (!Number.isFinite(timestamp)) return null;
    const parsed = new Date(timestamp);
    if (!Number.isFinite(parsed.getTime())) return null;
    return parsed;
  }

  function buildOverviewHeaderSlotLoadingSkeleton() {
    const placeholderRows = 3;
    const rows = Array(placeholderRows)
      .fill(null)
      .map(() => buildOverviewHeaderSlotItemSkeletonMarkup())
      .join('');
    return `<ul class="et-update-meals-overview-slot__items et-update-meals-overview-slot__items--skeleton" aria-hidden="true">${rows}</ul>
      <p class="et-update-meals-overview-slot__loading et-update-meals-overview-slot__loading--sr-only" aria-live="polite">Loading subscription contents...</p>`;
  }

  function buildOverviewHeaderSlotItemSkeletonMarkup() {
    return `
      <li class="et-update-meals-overview-slot__item et-update-meals-overview-slot__item--skeleton">
        <div class="et-update-meals-overview-slot__item-media">
          <span class="et-update-meals-overview-slot__item-image-placeholder et-update-meals-overview-slot__skeleton-block"></span>
        </div>
        <div class="et-update-meals-overview-slot__item-content">
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-label"></span>
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-desc"></span>
        </div>
        <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-detail"></span>
      </li>
    `;
  }

  function buildOverviewHeaderSlotTotalSkeleton() {
    return `
      <div class="et-update-meals-overview-slot__total et-update-meals-overview-slot__total--skeleton" aria-hidden="true">
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-label"></span>
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-value"></span>
        </div>
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-label"></span>
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-value"></span>
        </div>
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-label"></span>
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-value"></span>
        </div>
        <div class="et-update-meals-overview-slot__total-row et-update-meals-overview-slot__total-row--final">
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-label"></span>
          <span class="et-update-meals-overview-slot__skeleton-block et-update-meals-overview-slot__skeleton-total-value"></span>
        </div>
      </div>
    `;
  }

  // Fallback when the lookup settles without a charge for a reason that has no
  // designed state (resolveOverviewChargeEmptyState covers payment-failed and
  // no-queued-charge), e.g. no subscription to look a charge up for.
  function buildOverviewHeaderSlotTotalUnavailableMarkup() {
    return `
      <div class="et-update-meals-overview-slot__total et-update-meals-overview-slot__total--unavailable" role="status">
        <p class="et-update-meals-overview-slot__total-unavailable-message">
          We couldn&#39;t find your next order.
          Please <a href="#" class="et-update-meals-overview-slot__total-unavailable-link" data-action="et-contact-us">contact us</a>
          and we&#39;ll help get your subscription back on track.
        </p>
      </div>
    `;
  }

  function buildOverviewHeaderSlotChargeEmptyStateMarkup(emptyState, subscriptionId) {
    const subscriptionAttribute = subscriptionId
      ? ` data-subscription-id="${String(subscriptionId)}"`
      : '';
    const actionsMarkup = emptyState.actions
      .map((entry) => {
        const buttonClass = entry.primary
          ? 'et-update-meals-overview-slot__button'
          : 'et-update-meals-overview-slot__retry-button';
        return `<button type="button" class="${buttonClass}" data-action="${entry.action}"${subscriptionAttribute}>${entry.label}</button>`;
      })
      .join('');
    return `
      <div class="et-update-meals-overview-slot__empty-state" role="status">
        <p class="et-update-meals-overview-slot__empty-state-heading">${emptyState.heading}</p>
        <p class="et-update-meals-overview-slot__empty-state-body">${emptyState.body}</p>
        <div class="et-update-meals-overview-slot__empty-state-actions">${actionsMarkup}</div>
      </div>
    `;
  }

  // Rendered wherever a hydration read failed even after its automatic retry.
  // "Try again" re-runs the whole hydration; reads that succeeded within the
  // read memo's TTL (30s) come back from it, anything older is re-fetched.
  function buildOverviewHeaderSlotFailureMarkup(message, actionMarkup) {
    return `
      <div class="et-update-meals-overview-slot__retry" role="alert">
        <p class="et-update-meals-overview-slot__retry-message">${message}</p>
        ${actionMarkup || OVERVIEW_RETRY_ACTION_MARKUP}
      </div>
    `;
  }

  /**
   * Builds overview slot markup for both display modes.
   *
   * Modes:
   * - Summary mode (`isEditing=false`): title + Update button + item preview list.
   * - Editing mode (`isEditing=true`): title + inline editor containers.
   *
   * The editing mode intentionally keeps body/footer containers empty because
   * `renderEditorForm` owns those subtrees and rerenders them frequently.
   */
  function buildFulfillmentManagementLink(fulfillmentType, subscriptionId) {
    const inertMarkup = '<span class="et-update-meals-overview-slot__menu-link" role="link" aria-disabled="true">Manage delivery / pickup</span>';
    if (!subscriptionId) return inertMarkup;

    const normalizedType = normalizeFulfillmentType(fulfillmentType);
    if (normalizedType === 'delivery') {
      return `<a href="#" class="et-update-meals-overview-slot__menu-link" role="link" data-action="et-manage-delivery" data-subscription-id="${String(subscriptionId)}">Manage Delivery</a>`;
    } else if (normalizedType === 'pickup') {
      return `<a href="#" class="et-update-meals-overview-slot__menu-link" role="link" data-action="et-manage-pickup" data-subscription-id="${String(subscriptionId)}">Manage Pickup</a>`;
    }
    return inertMarkup;
  }

  function buildOverviewHeaderSlotMarkup(options = {}) {
    const isLoading = Boolean(options.isLoading);
    const isEditing = Boolean(options.isEditing);
    // A last render shown while fresh data loads (#364): inert, so nothing in
    // it can be clicked, focused or opened, with its buttons disabled too.
    const isStale = Boolean(options.isStale);
    const subscriptionId = parseOptionalSubscriptionId(options.subscriptionId);
    const items = Array.isArray(options.items) ? options.items : [];
    const currencyCode =
      normalizeCurrencyCode(options.currencyCode) || DEFAULT_CURRENCY_CODE;
      const charge = options.charge || null;
      const hasDiscount = chargeHasDiscount(charge);
  
      const activeSubscriptionCount =
        typeof options.activeSubscriptionCount === 'number'
          ? options.activeSubscriptionCount
          : 0;
      const showViewOtherSubscriptions = activeSubscriptionCount > 1;

    const deliveryContext = options.deliveryContext || null;

    const fulfillmentType = options.fulfillmentType ?? state.overviewSlotFulfillmentType;
    const isDisabled = isLoading || isStale || !subscriptionId;
    const buttonLabel = isLoading ? 'Loading...' : 'Update';

    const hasDelivery = hasCompleteDeliveryContext(deliveryContext);
    let headerTitleText = '';
    if (hasDelivery) {
      const typeLabel =
        deliveryContext.fulfillmentType === 'pickup' ? 'pickup' : 'delivery';
      const isDeliveryWithTime =
        typeLabel === 'delivery' && deliveryContext.arrivalTimeText;
      headerTitleText = isDeliveryWithTime
        ? `Next ${typeLabel}: ${deliveryContext.arrivalTimeText}, ${deliveryContext.arrivalDateText}`
        : `Next ${typeLabel}: ${deliveryContext.arrivalDateText}`;
    } else {
      if (fulfillmentType === 'pickup') {
        headerTitleText = 'Next pickup';
      } else if (fulfillmentType === 'delivery') {
        headerTitleText = 'Next delivery';
      } else {
        headerTitleText = 'Next order';
      }
    }

    let infoMarkup = '';
    if (hasDelivery) {
      const infoTooltipText = `Your card will be charged on ${deliveryContext.chargeDateText}, and your ${deliveryContext.fulfillmentType} order will be ${deliveryContext.arrivalVerb} on ${deliveryContext.arrivalDateText}.`;
      infoMarkup = `
            <button
              type="button"
              class="et-update-meals-overview-slot__info"
              aria-label="Billing details"
            >
              i
              <span class="et-update-meals-overview-slot__info-tooltip">
                ${escapeHtml(infoTooltipText)}
              </span>
            </button>
          `;
    }

    if (isEditing) {
      return `
        <div class="et-update-meals-overview-slot et-update-meals-overview-slot--editing" data-et-update-meals-overview-slot="true">
          <div class="et-update-meals-overview-slot__header">
            <div class="et-update-meals-overview-slot__title_container">
              <p class="et-update-meals-overview-slot__title">${escapeHtml(headerTitleText)}</p>
              ${infoMarkup}
            </div>
          </div>
          <footer class="et-update-meals-modal__footer et-update-meals-inline-editor__footer"></footer>
          <section class="et-update-meals-modal__body et-update-meals-inline-editor__body"></section>
          ${scroll_to_up}
        </div>
      `;
    }
    const loadFailure = isLoading ? null : state.overviewSlotLoadFailure;
    let itemsMarkup;
    if (isLoading) {
      itemsMarkup = buildOverviewHeaderSlotLoadingSkeleton();
    } else if (loadFailure) {
      // The totals block below carries the one message for the whole slot.
      itemsMarkup = '';
    } else if (state.overviewSlotItemsLoadFailed) {
      itemsMarkup = buildOverviewHeaderSlotFailureMarkup(
        'We couldn&#39;t load your meals.'
      );
    } else {
      itemsMarkup = buildOverviewHeaderSlotItemsMarkup(items, currencyCode);
    }
    const chargeResolved = Boolean(options.chargeResolved);
    // Not while loading: "Try again" renders a loading frame before the reset.
    const chargeMissingReason =
      !isLoading && !charge ? state.overviewSlotChargeMissingReason : null;
    // Not over a load failure either: the totals render that failure instead,
    // so hiding "Update" / "Apply rewards" would leave neither on screen.
    const chargeEmptyState =
      loadFailure || state.overviewSlotItemsLoadFailed
        ? null
        : resolveOverviewChargeEmptyState(chargeMissingReason, {
            showViewOtherSubscriptions,
            chargeFailure: state.overviewSlotChargeFailure,
            hasNextChargeDate: state.overviewSlotHasNextChargeDate,
          });
    let totalMarkup;
    if (isLoading) {
      totalMarkup = buildOverviewHeaderSlotTotalSkeleton();
    } else if (loadFailure === 'session-expired') {
      totalMarkup = buildOverviewHeaderSlotFailureMarkup(
        'Your subscription session has expired.',
        OVERVIEW_LOGIN_ACTION_MARKUP
      );
    } else if (state.overviewSlotItemsLoadFailed) {
      // The subtotal is summed from the items, so without them any total is
      // wrong (it rendered "Subtotal $0.00"). The items' "Try again" covers
      // both; the header still shows the next-delivery date.
      totalMarkup = '';
    } else if (
      loadFailure ||
      isRetryableChargeMissingReason(chargeMissingReason)
    ) {
      totalMarkup = buildOverviewHeaderSlotFailureMarkup(
        'We couldn&#39;t load your next order.'
      );
    } else if (!charge && !chargeResolved) {
      totalMarkup = buildOverviewHeaderSlotTotalSkeleton();
    } else if (chargeEmptyState) {
      totalMarkup = buildOverviewHeaderSlotChargeEmptyStateMarkup(
        chargeEmptyState,
        subscriptionId
      );
    } else if (!charge) {
      totalMarkup = buildOverviewHeaderSlotTotalUnavailableMarkup();
    } else {
      totalMarkup = buildOverviewHeaderSlotTotalMarkup(items, charge, currencyCode);
    }

    return `
      <div class="et-update-meals-overview-slot" data-et-update-meals-overview-slot="true"${
        isStale ? ' data-et-stale="true" inert' : ''
      }>
        <div class="et-update-meals-overview-slot__header">
          <div class="et-update-meals-overview-slot__title_container">
            <p class="et-update-meals-overview-slot__title">${escapeHtml(headerTitleText)}</p>
            ${infoMarkup}
          </div>
          <div class="et-update-meals-overview-slot__header-actions">
            ${
              chargeEmptyState
                ? ''
                : `<button
              type="button"
              class="et-update-meals-overview-slot__button"
              data-action="et-open-update-meals"
              ${
                subscriptionId
                  ? `data-subscription-id="${String(subscriptionId)}"`
                  : ''
              }
              ${isDisabled ? 'disabled aria-disabled="true"' : ''}
            >
              ${buttonLabel}
            </button>`
            }
            ${
              chargeEmptyState
                ? ''
                : hasDiscount
                ? `<button
                    type="button"
                    class="et-update-meals-overview-slot__button"
                    disabled
                    aria-disabled="true"
                  >
                    Apply rewards
                  </button>`
                : `<button
                    type="button"
                    class="et-update-meals-overview-slot__button"
                    data-action="et-apply-rewards"
                    ${
                      subscriptionId
                        ? `data-subscription-id="${String(subscriptionId)}"`
                        : ''
                    }
                    ${isDisabled ? 'disabled' : ''}
                    ${isDisabled ? 'aria-disabled="true"' : ''}
                  >
                    Apply rewards
                  </button>`
            }
            <details class="et-update-meals-overview-slot__menu">
              <summary class="et-update-meals-overview-slot__menu-trigger" aria-label="More options">${subscription_menu_icon_svg}</summary>
              <div class="et-update-meals-overview-slot__menu-panel" role="menu" aria-label="Subscription actions">
                <a 
                  ${isDisabled ? '' : 'href="#"'}
                  class="et-update-meals-overview-slot__menu-link" 
                  role="menuitem"
                  ${isDisabled ? 'aria-disabled="true" tabindex="-1"' : `data-action="et-reschedule"${subscriptionId ? ` data-subscription-id="${String(subscriptionId)}"` : ''}`}
                >
                  Reschedule
                </a>
                <a 
                  ${!showViewOtherSubscriptions ? '' : 'href="#"'}
                  class="et-update-meals-overview-slot__menu-link" 
                  role="menuitem"
                  ${!showViewOtherSubscriptions ? 'aria-disabled="true" tabindex="-1"' : `data-action="et-view-other-subscriptions"${subscriptionId ? ` data-subscription-id="${String(subscriptionId)}"` : ''}`}
                >
                  View other subscriptions
                </a>
                <a href="#" class="et-update-meals-overview-slot__menu-link" role="link" data-action="et-update-payment">Update Payment</a>
                ${buildFulfillmentManagementLink(options.fulfillmentType, subscriptionId)}
                <a href="#" class="et-update-meals-overview-slot__menu-link" role="link" data-action="et-contact-us">Contact us</a>
              </div>
            </details>
          </div>
        </div>
        ${itemsMarkup}
        ${totalMarkup}
      </div>
    `;
  }

  /**
   * Renders overview slot markup into the currently mounted Affinity slot node.
   *
   * Rendering strategy:
   * - Prefer the known mounted slot element when available.
   * - Fallback to querying current slot root and replace it atomically.
   *
   * This keeps slot refreshes resilient when Affinity internally swaps container
   * nodes between mount events.
   */
  function renderOverviewHeaderSlot(options = {}) {
    const isLoading = Boolean(options.isLoading);
    const isEditing =
      typeof options.isEditing === 'boolean'
        ? options.isEditing
        : state.editorOpen;
    const subscriptionId = parseOptionalSubscriptionId(
      options.subscriptionId ?? state.overviewSlotSubscriptionId
    );
    const items = Array.isArray(options.items)
      ? options.items
      : state.overviewSlotItems;
    const currencyCode =
      normalizeCurrencyCode(options.currencyCode) ||
      state.overviewSlotCurrencyCode ||
      DEFAULT_CURRENCY_CODE;
      const charge = options.charge !== undefined ? options.charge : state.overviewSlotCharge;
      const activeSubscriptionCount =
        typeof options.activeSubscriptionCount === 'number'
          ? options.activeSubscriptionCount
          : state.overviewSlotActiveSubscriptionCount || 0;
    const deliveryContext =
      options.deliveryContext ?? state.overviewSlotDeliveryContext ?? null;
    const fulfillmentType = options.fulfillmentType ?? state.overviewSlotFulfillmentType;
    const markupOptions = {
      isLoading,
      isEditing,
      subscriptionId,
      items,
      currencyCode,
      charge,
      chargeResolved:
        typeof options.chargeResolved === 'boolean'
          ? options.chargeResolved
          : state.overviewSlotChargeResolved,
      activeSubscriptionCount,
      deliveryContext,
      fulfillmentType,
    };
    // A held slot (a remount's last render, or a refresh's current one)
    // keeps what it shows until its next charge settles -- a render before
    // that would drop the totals to a skeleton (#364) -- and, after a write,
    // while that charge is still mid-recalculation with re-reads left, so
    // the discount just removed or added never flashes back. State still
    // updates underneath, and the first render allowed draws it all.
    if (
      state.overviewSlotHoldingRender &&
      !isLoading &&
      !isEditing &&
      !state.overviewSlotLoadFailure &&
      (!markupOptions.chargeResolved ||
        (state.chargeRecalculationRetriesLeft > 0 &&
          isChargeMidRecalculation(markupOptions.charge)))
    ) {
      return;
    }
    const markup = buildOverviewHeaderSlotMarkup(markupOptions);
    rememberOverviewRender(markupOptions);
    state.overviewSlotHoldingRender = false;
    writeOverviewSlotMarkup(markup);
  }

  function writeOverviewSlotMarkup(markup) {
    const slotElement = state.activeOverviewSlotElement;
    if (slotElement instanceof HTMLElement && slotElement.isConnected) {
      slotElement.innerHTML = markup;
      return;
    }

    const mountedSlot = resolveMountedOverviewHeaderSlotElement();
    if (mountedSlot instanceof HTMLElement) {
      const nextSlot = createElementFromMarkup(markup);
      if (nextSlot instanceof HTMLElement) {
        mountedSlot.replaceWith(nextSlot);
      }
    }
  }

  // Keeps a complete render -- data loaded, the next charge settled, nothing
  // failed -- for a later remount of the slot to show at once (#364). The
  // same builder draws it, inert. Not while a refresh is pending: a render
  // from reads that began before the write would outlive the forget.
  function rememberOverviewRender(markupOptions) {
    if (markupOptions.isLoading || markupOptions.isEditing) return;
    if (!markupOptions.subscriptionId || !markupOptions.chargeResolved) return;
    if (state.overviewSlotLoadFailure || state.overviewSlotItemsLoadFailed) return;
    if (state.overviewRefreshTimerId !== null) return;
    if (state.chargeRecalculationRetriesLeft > 0 && isChargeMidRecalculation(markupOptions.charge)) return;
    if (
      !markupOptions.charge &&
      isRetryableChargeMissingReason(state.overviewSlotChargeMissingReason)
    ) {
      return;
    }
    state.lastOverviewRender = {
      markup: buildOverviewHeaderSlotMarkup({ ...markupOptions, isStale: true }),
      preferredSubscriptionId: parseOptionalSubscriptionId(
        state.preferredOverviewSubscriptionId
      ),
    };
  }

  function forgetLastOverviewRender() {
    state.lastOverviewRender = null;
  }

  // A slot remount's first paint (#364): the last complete render, if it was
  // made under the subscription choice still in effect. Returns whether it
  // painted; renderOverviewHeaderSlot keeps it up until fresh data can
  // replace it.
  function showLastOverviewRender() {
    const last = state.lastOverviewRender;
    if (!last || !isOverviewHeaderSlotMounted()) return false;
    const preferred = parseOptionalSubscriptionId(state.preferredOverviewSubscriptionId);
    if (last.preferredSubscriptionId !== preferred) return false;
    writeOverviewSlotMarkup(last.markup);
    return true;
  }

  function resolveMountedOverviewHeaderSlotElement() {
    const slotElement = state.activeOverviewSlotElement;
    if (slotElement instanceof HTMLElement && slotElement.isConnected) {
      const mounted = slotElement.querySelector(CONFIG.slotRootSelector);
      if (mounted instanceof HTMLElement) return mounted;
    }

    const fallback = document.querySelector(CONFIG.slotRootSelector);
    if (fallback instanceof HTMLElement) return fallback;

    return null;
  }

  /**
   * Renders the compact overview list used in non-editing mode.
   *
   * This list is intentionally "read-only summary" UI and therefore:
   * - excludes zero-quantity lines
   * - relies on normalized image/label helpers
   * - avoids heavy interactive controls
   */
  function buildOverviewHeaderSlotItemsMarkup(items, currencyCode) {
    const safeItems = Array.isArray(items) ? items : [];
    if (!safeItems.length) {
      return '<p class="et-update-meals-overview-slot__empty">No subscription items found.</p>';
    }

    const rows = safeItems
      .map((item) => resolveOverviewHeaderSlotItemRenderData(item, currencyCode))
      .filter((renderData) => renderData && renderData.quantity > 0)
      .map((renderData, rowIndex) => {
        if (!renderData.isReady) {
          return buildOverviewHeaderSlotItemSkeletonMarkup();
        }

        return `
          <li class="et-update-meals-overview-slot__item">
            <div class="et-update-meals-overview-slot__item-media">
              <img
                class="et-update-meals-overview-slot__item-image"
                ${buildThumbnailImageAttributes(renderData.imageUrl)}
                alt="${escapeHtml(renderData.label)}"
                loading="${
                  rowIndex < OVERVIEW_SLOT_EAGER_IMAGE_COUNT ? 'eager' : 'lazy'
                }"
                decoding="async"
              >
            </div>
            <div class="et-update-meals-overview-slot__item-content">
              <span class="et-update-meals-overview-slot__item-label">${escapeHtml(renderData.label)}</span>
            </div>
            <span class="et-update-meals-overview-slot__item-detail">${escapeHtml(
              renderData.detail
            )}</span>
          </li>
        `;
      })
      .filter(Boolean)
      .join('');

    if (!rows) {
      return '<p class="et-update-meals-overview-slot__empty">No subscription items found.</p>';
    }

    return `<ul class="et-update-meals-overview-slot__items">${rows}</ul>`;
  }

  function resolveOverviewHeaderSlotItemRenderData(item, currencyCode) {
    const quantity = parseInteger(item?.quantity ?? 0);
    if (quantity <= 0) return null;

    const productId =
      normalizeExternalResourceId(
        item?.external_product_id || item?.product_id || null
      ) || '';
    const variantId =
      normalizeExternalResourceId(
        item?.external_variant_id || item?.variant_id || null
      ) || '';
    const label = resolveBundleItemDisplayLabel(item);
    const imageUrl = String(resolveBundleItemImageUrl(item) || '').trim();
    const unitPrice = Number.isFinite(item?.__unitPrice) ? item.__unitPrice : null;
    const unitPriceText =
      unitPrice !== null && unitPrice >= 0
        ? formatCurrencyValue(unitPrice, currencyCode)
        : null;
    const hasHumanReadableLabel = !isPlaceholderMealLabel(
      label,
      productId,
      variantId
    );
    const isReady = Boolean(hasHumanReadableLabel && imageUrl && unitPriceText);

    if (hasHumanReadableLabel && !imageUrl) {
      reportMissingOverviewItemImage(item, label);
    }

    return {
      quantity,
      label,
      imageUrl,
      detail: unitPriceText ? `${quantity} x ${unitPriceText}` : `${quantity} x`,
      isReady,
    };
  }

  // A discount already on the next order disables "Apply rewards".
  function chargeHasDiscount(charge) {
    const discountMeta = resolveChargeDiscountMeta(charge);
    return discountMeta.totalAmount > 0 || Boolean(discountMeta.primaryCode);
  }

  function resolveChargeDiscountMeta(charge) {
    const meta = {
      totalAmount: 0,
      primaryCode: null,
      codes: [],
    };
    if (!charge || typeof charge !== 'object') return meta;

    const rawTotal =
      parseFloat(
        charge.total_discounts ||
          charge.discount_total ||
          (Array.isArray(charge.discounts)
            ? charge.discounts.reduce(
                (sum, entry) => sum + (parseFloat(entry.amount || entry.value || 0) || 0),
                0
              )
            : 0)
      ) || 0;

    const codes = new Set();
    const candidateArrays = [charge.discounts, charge.applied_discounts];
    candidateArrays.forEach((collection) => {
      if (!Array.isArray(collection)) return;
      collection.forEach((entry) => {
        const code = String(
          entry?.code || entry?.coupon_code || entry?.discount_code || ''
        ).trim();
        if (code) codes.add(code);
      });
    });
    if (charge.discount_code) {
      const code = String(charge.discount_code).trim();
      if (code) codes.add(code);
    }

    meta.totalAmount = Math.max(0, rawTotal);
    meta.codes = Array.from(codes);
    meta.primaryCode = meta.codes.length ? meta.codes[0] : null;
    return meta;
  }


  function buildOverviewHeaderSlotTotalMarkup(items, charge, currencyCode) {
    if (!charge || typeof charge !== 'object') return '';

    const lineItems = Array.isArray(charge.line_items) ? charge.line_items : [];

    let subtotal = 0;
    items.forEach((item) => {
      const quantity = parseInteger(item?.quantity ?? 0);
      const unitPrice = Number.isFinite(item?.__unitPrice) ? item.__unitPrice : 0;
      if (quantity > 0 && unitPrice > 0) {
        subtotal += quantity * unitPrice;
      }
    });

    const discountMeta = resolveChargeDiscountMeta(charge);
    const totalDiscounts = discountMeta.totalAmount;

    let packagingFee = 0;
    let deliveryFee = 0;

    lineItems.forEach((lineItem) => {
      const configuredFeeType = resolveConfiguredFeeType(lineItem);
      const qty = Math.max(0, parseInteger(lineItem.quantity ?? 1));
      const unitPrice =
        parseFloat(lineItem.price || lineItem.line_price || 0) || 0;
      const lineTotal =
        parseFloat(lineItem.total_price || lineItem.line_price || 0);
      const price =
        Number.isFinite(lineTotal) && lineTotal >= 0
          ? lineTotal
          : (qty || 1) * unitPrice;

      if (configuredFeeType === 'packaging') {
        packagingFee += price;
        return;
      }
      if (configuredFeeType === 'delivery') {
        deliveryFee += price;
        return;
      }
    });

    const totalFees = packagingFee + deliveryFee;
    const totalTax =
      parseFloat(charge.total_tax || charge.tax_due || charge.tax_total || 0) ||
      0;
    const total = subtotal - totalDiscounts + totalFees + totalTax;

    const discountCode = discountMeta.primaryCode;

    return `
      <div class="et-update-meals-overview-slot__total">
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__total-label">Subtotal</span>
          <span class="et-update-meals-overview-slot__total-value">${formatCurrencyValue(subtotal, currencyCode)}</span>
        </div>
        ${(totalDiscounts > 0 || discountCode) ? `
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__total-label">
            Rewards applied
            ${discountCode
          ? `<span class="et-update-meals-overview-slot__discount-chip">
                    ${escapeHtml(discountCode)}
                    <button type="button" class="et-update-meals-overview-slot__discount-remove" data-action="et-remove-discount" aria-label="Remove discount ${escapeHtml(
            discountCode
          )}">&times;</button>
                  </span>`
          : ''
        }
          </span>
          <span class="et-update-meals-overview-slot__total-value et-update-meals-overview-slot__total-value--discount">${totalDiscounts > 0 ? '-' + formatCurrencyValue(totalDiscounts, currencyCode) : 'Applying'}</span>
        </div>
        ` : ''}
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__total-label">Services and Fees</span>
          <span class="et-update-meals-overview-slot__total-value">${formatCurrencyValue(totalFees, currencyCode)}</span>
        </div>
        <div class="et-update-meals-overview-slot__total-row">
          <span class="et-update-meals-overview-slot__total-label">Taxes</span>
          <span class="et-update-meals-overview-slot__total-value">${formatCurrencyValue(totalTax, currencyCode)}</span>
        </div>
        <div class="et-update-meals-overview-slot__total-row et-update-meals-overview-slot__total-row--final">
          <span class="et-update-meals-overview-slot__total-label">Total</span>
          <span class="et-update-meals-overview-slot__total-value et-update-meals-overview-slot__total-value--final">${formatCurrencyValue(total, currencyCode)}</span>
        </div>
      </div>
    `;
  }

  function resolveBundleItemDisplayLabel(item) {
    const fallbackLabel = 'Meal item';
    const rawLabel = String(
      item?.__label || item?.product_title || item?.title || item?.name || fallbackLabel
    ).trim();
    if (!rawLabel) return fallbackLabel;

    const variantCandidates = [
      item?.__subtitle,
      item?.variant_title,
      ...(Array.isArray(item?.__optionValues) ? item.__optionValues : []),
      ...(Array.isArray(item?.option_values) ? item.option_values : []),
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .filter((value) => value.toLowerCase() !== 'default title');

    let displayLabel = rawLabel;
    for (const variantCandidate of variantCandidates) {
      displayLabel = stripBundleVariantSuffix(displayLabel, variantCandidate);
    }

    return displayLabel || rawLabel || fallbackLabel;
  }

  function stripBundleVariantSuffix(label, variant) {
    const normalizedLabel = String(label || '').trim();
    const normalizedVariant = String(variant || '').trim();
    if (!normalizedLabel || !normalizedVariant) return normalizedLabel;

    const escapedVariant = escapeRegExpLiteral(normalizedVariant);
    if (!escapedVariant) return normalizedLabel;

    const patterns = [
      new RegExp(`\\s*[-–—|/:]\\s*${escapedVariant}\\s*$`, 'i'),
      new RegExp(`\\s+${escapedVariant}\\s*$`, 'i'),
      new RegExp(`\\s*\\(${escapedVariant}\\)\\s*$`, 'i'),
      new RegExp(`^${escapedVariant}\\s*[-–—|/:]\\s*`, 'i'),
    ];

    let nextLabel = normalizedLabel;
    for (const pattern of patterns) {
      nextLabel = nextLabel.replace(pattern, '').trim();
    }

    return nextLabel || normalizedLabel;
  }

  function escapeRegExpLiteral(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function resolveBundleItemImageUrl(item) {
    if (!item || typeof item !== 'object') return null;

    const candidates = [
      item.__imageUrl,
      item.image,
      item.image_url,
      item.featured_image,
      item.featuredImage,
      item.product_image,
      item.productImage,
      item.thumbnail,
    ];

    for (const candidate of candidates) {
      const normalized = normalizeAssetUrl(candidate);
      if (normalized) return normalized;
    }

    return null;
  }

  function reportMissingOverviewItemImage(item, label) {
    const observability = getEtObservability();
    if (!observability || typeof observability.captureException !== 'function') {
      return;
    }

    const normalizedLabel = String(label || '').trim() || 'Meal item';
    const subscriptionId = parseOptionalSubscriptionId(state.overviewSlotSubscriptionId);
    const productId = String(item?.external_product_id || item?.product_id || '').trim();
    const variantId = String(item?.external_variant_id || item?.variant_id || '').trim();
    const reportKey = [
      subscriptionId || 'unknown-subscription',
      productId || 'unknown-product',
      variantId || 'unknown-variant',
      normalizedLabel,
    ].join('::');

    if (state.reportedOverviewMissingImageKeys.has(reportKey)) {
      return;
    }
    state.reportedOverviewMissingImageKeys.add(reportKey);

    observability.captureException(
      new Error('Update meals overview item image missing'),
      {
        level: 'error',
        tags: {
          surface: 'affinity-update-meals',
          component: 'overview-header-slot',
          issue: 'missing-item-image',
        },
        extra: {
          subscriptionId: subscriptionId || null,
          productId: productId || null,
          variantId: variantId || null,
          label: normalizedLabel,
          quantity: parseInteger(item?.quantity ?? 0),
          imageFieldPresence: {
            __imageUrl: Boolean(item?.__imageUrl),
            image: Boolean(item?.image),
            image_url: Boolean(item?.image_url),
            featured_image: Boolean(item?.featured_image),
            featuredImage: Boolean(item?.featuredImage),
            product_image: Boolean(item?.product_image),
            productImage: Boolean(item?.productImage),
            thumbnail: Boolean(item?.thumbnail),
          },
        },
      }
    );
  }

  function hydrateStorefrontCollectionTitleCache() {
    let raw = null;
    try {
      raw = window.EtStorage.session.getItem(
        CONFIG.storefrontCollectionTitlesSessionKey
      );
    } catch (error) {
      return;
    }
    if (!raw) return;

    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return;
    }
    if (!parsed || typeof parsed !== 'object') return;

    const entries = Object.entries(parsed)
      .map(([id, title]) => [String(id || '').trim(), String(title || '').trim()])
      .filter(([id, title]) => id && title);
    if (!entries.length) return;

    state.storefrontCollectionNamesById = new Map(entries);
  }

  function persistStorefrontCollectionTitleCache(map) {
    if (!(map instanceof Map)) return;
    const serializable = {};
    for (const [id, title] of map.entries()) {
      const normalizedId = String(id || '').trim();
      const normalizedTitle = String(title || '').trim();
      if (!normalizedId || !normalizedTitle) continue;
      serializable[normalizedId] = normalizedTitle;
    }
    try {
      window.EtStorage.session.setItem(
        CONFIG.storefrontCollectionTitlesSessionKey,
        JSON.stringify(serializable)
      );
    } catch (error) {
      // Ignore storage errors (private mode/quota/etc).
    }
  }

  function cacheStorefrontCollectionTitles(collections) {
    const currentMap =
      state.storefrontCollectionNamesById instanceof Map
        ? new Map(state.storefrontCollectionNamesById)
        : new Map();

    let changed = false;
    const safeCollections = Array.isArray(collections) ? collections : [];
    for (const collection of safeCollections) {
      const collectionId = String(
        extractShopifyResourceId(collection?.id) || ''
      ).trim();
      const collectionTitle = String(collection?.title || '').trim();
      if (!collectionId || !collectionTitle) continue;
      if (currentMap.get(collectionId) === collectionTitle) continue;
      currentMap.set(collectionId, collectionTitle);
      changed = true;
    }

    if (!changed) return;
    state.storefrontCollectionNamesById = currentMap;
    persistStorefrontCollectionTitleCache(currentMap);

    if (
      state.editorOpen &&
      state.activeSubscription &&
      state.activeBundleSelection &&
      state.addableMealsLoading === false
    ) {
      renderActiveEditor();
    }
  }

  async function prefetchStorefrontCollectionsWithCache() {
    if (Array.isArray(state.storefrontCollectionsCache)) {
      return state.storefrontCollectionsCache;
    }
    if (state.storefrontCollectionTitlePrefetchPromise) {
      return state.storefrontCollectionTitlePrefetchPromise;
    }

    const storefrontConfig = resolveStorefrontConfig();
    if (!storefrontConfig.storefrontAccessToken) {
      return [];
    }

    state.storefrontCollectionTitlePrefetchPromise = (async () => {
      const collections = await fetchStorefrontCollections(storefrontConfig);
      state.storefrontCollectionsCache = Array.isArray(collections)
        ? collections
        : [];
      cacheStorefrontCollectionTitles(state.storefrontCollectionsCache);
      return state.storefrontCollectionsCache;
    })()
      .catch((error) => {
        state.storefrontCollectionsCache = null;
        throw error;
      })
      .finally(() => {
        state.storefrontCollectionTitlePrefetchPromise = null;
      });

    return state.storefrontCollectionTitlePrefetchPromise;
  }

  // Parse IDs defensively because Recharge payloads may return numbers, strings,
  // nulls, or empty values depending on endpoint and serialization behavior.
  function parseInteger(rawValue, fallback = 0) {
    const sharedParser = window.etRechargeUtils?.parseInteger;
    if (typeof sharedParser === 'function') {
      return sharedParser(rawValue, fallback);
    }
    const parsed = Number.parseInt(String(rawValue), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseOptionalSubscriptionId(rawValue) {
    if (rawValue === null || typeof rawValue === 'undefined') return null;
    const parsed = parseInteger(rawValue, Number.NaN);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  }

  function normalizeExternalResourceId(rawValue) {
    if (rawValue === null || typeof rawValue === 'undefined') return null;

    let candidate = rawValue;
    if (typeof rawValue === 'object') {
      if (
        Object.prototype.hasOwnProperty.call(rawValue, 'ecommerce') &&
        rawValue.ecommerce !== null &&
        typeof rawValue.ecommerce !== 'undefined'
      ) {
        candidate = rawValue.ecommerce;
      } else if (
        Object.prototype.hasOwnProperty.call(rawValue, 'id') &&
        rawValue.id !== null &&
        typeof rawValue.id !== 'undefined'
      ) {
        candidate = rawValue.id;
      } else {
        return null;
      }
    }

    const normalized = normalizeCollectionId(candidate);
    if (!normalized) return null;
    const extracted = extractShopifyResourceId(normalized);
    const resolved = extracted || normalized;

    return resolved;
  }

  function normalizeOptionalThreshold(rawValue, fallback) {
    const parsed = Number.parseFloat(String(rawValue ?? '').trim());
    if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
    return parsed;
  }

  /**
   * Operational fee model
   * ---------------------
   * Recharge bundle selections may include non-meal lines (packaging/delivery).
   * We model those fees explicitly so quantity edits never accidentally remove
   * required operational lines.
   *
   * This metadata table allows fee logic to stay generic:
   * - each fee type has configured product/variant IDs
   * - helper functions can iterate fee types without hard-coded branches
   */
  const OPERATIONAL_FEE_TYPE_META = Object.freeze({
    packaging: Object.freeze({
      label: 'Service Fee',
      variantIdKey: 'packagingFeeVariantId',
      productIdKey: 'packagingFeeProductId',
    }),
    delivery: Object.freeze({
      label: 'Delivery Fee',
      variantIdKey: 'deliveryFeeVariantId',
      productIdKey: 'deliveryFeeProductId',
    }),
  });
  const OPERATIONAL_FEE_TYPES = Object.freeze(['packaging', 'delivery']);

  function createEmptyOperationalFeeTemplates() {
    return { packaging: null, delivery: null };
  }

  function pickFirstTruthyValue(candidates) {
    for (const candidate of candidates) {
      if (candidate) return candidate;
    }
    return null;
  }

  function pickFirstDefinedValue(candidates) {
    for (const candidate of candidates) {
      if (candidate !== null && typeof candidate !== 'undefined') {
        return candidate;
      }
    }
    return null;
  }

  function getOperationalFeeTypeMeta(feeType) {
    if (!feeType || typeof feeType !== 'string') return null;
    return OPERATIONAL_FEE_TYPE_META[feeType] || null;
  }

  function getOperationalFeeIds(feeType, feeConfig) {
    const meta = getOperationalFeeTypeMeta(feeType);
    if (!meta || !feeConfig || typeof feeConfig !== 'object') {
      return { variantId: null, productId: null };
    }
    const rawVariantId = feeConfig[meta.variantIdKey] || null;
    const rawProductId = feeConfig[meta.productIdKey] || null;

    return {
      variantId: normalizeExternalResourceId(rawVariantId),
      productId: normalizeExternalResourceId(rawProductId),
    };
  }

  function getOperationalFeeCollectionId(feeConfig) {
    return normalizeCollectionId(feeConfig?.feesCollectionId || null) || 'other';
  }

  /**
   * Resolves fee configuration from global runtime sources.
   *
   * Source precedence is intentionally broad because theme/runtime deployments
   * expose config through different globals over time. This function normalizes
   * all known aliases into one stable object contract.
   */
  function resolveOperationalFeeConfig() {
    const globalConfig =
      window.etUpdateMealsConfig && typeof window.etUpdateMealsConfig === 'object'
        ? window.etUpdateMealsConfig
        : {};
    const rechargeGridSettings =
      window.rechargeGridSettings &&
      typeof window.rechargeGridSettings === 'object'
        ? window.rechargeGridSettings
        : {};

    const deliveryFeeVariantId = normalizeExternalResourceId(
      pickFirstTruthyValue([
        globalConfig.deliveryFeeVariantId,
        globalConfig.delivery_fee_variant_id,
        rechargeGridSettings.delivery_fee_variant_id,
        window.ET_DELIVERY_FEE_VARIANT_ID,
      ])
    );
    const packagingFeeVariantId = normalizeExternalResourceId(
      pickFirstTruthyValue([
        globalConfig.packagingFeeVariantId,
        globalConfig.packaging_fee_variant_id,
        rechargeGridSettings.packaging_fee_variant_id,
        window.ET_PACKAGING_FEE_VARIANT_ID,
      ])
    );
    const deliveryFeeProductId = normalizeExternalResourceId(
      pickFirstTruthyValue([
        globalConfig.deliveryFeeProductId,
        globalConfig.delivery_fee_product_id,
        rechargeGridSettings.delivery_fee_product_id,
        window.ET_DELIVERY_FEE_PRODUCT_ID,
      ])
    );
    const packagingFeeProductId = normalizeExternalResourceId(
      pickFirstTruthyValue([
        globalConfig.packagingFeeProductId,
        globalConfig.packaging_fee_product_id,
        rechargeGridSettings.packaging_fee_product_id,
        window.ET_PACKAGING_FEE_PRODUCT_ID,
      ])
    );

    const feesCollectionId = normalizeCollectionId(
      pickFirstTruthyValue([
        globalConfig.feesCollectionId,
        globalConfig.fees_collection_id,
        rechargeGridSettings.fees_collection_id,
        rechargeGridSettings.collection_id,
      ])
    );

    const subscriptionDeliveryFeeThreshold = normalizeOptionalThreshold(
      pickFirstDefinedValue([
        globalConfig.subscriptionDeliveryFeeThreshold,
        globalConfig.subscription_delivery_fee_threshold,
        globalConfig.deliveryFeeThreshold,
        globalConfig.delivery_fee_threshold,
        rechargeGridSettings.subscription_delivery_fee_waiver_threshold,
        window.ET_SUBSCRIPTION_DELIVERY_FEE_THRESHOLD,
      ]),
      CONFIG.defaultSubscriptionDeliveryFeeThreshold
    );

    return {
      deliveryFeeVariantId,
      packagingFeeVariantId,
      deliveryFeeProductId,
      packagingFeeProductId,
      feesCollectionId,
      subscriptionDeliveryFeeThreshold,
    };
  }

  function resolveConfiguredFeeType(item, feeConfig = null) {
    if (!item || typeof item !== 'object') return null;
    const config = feeConfig || state.activeFeeConfig || resolveOperationalFeeConfig();
    if (!config || typeof config !== 'object') return null;

    const variantId = normalizeExternalResourceId(
      item.external_variant_id || item.variant_id || null
    );
    const productId = normalizeExternalResourceId(
      item.external_product_id || item.product_id || null
    );

    // Preserve precedence:
    // 1) variant-id matches (packaging first, then delivery)
    // 2) product-id matches (packaging first, then delivery)
    for (const feeType of OPERATIONAL_FEE_TYPES) {
      const { variantId: configuredVariantId } = getOperationalFeeIds(
        feeType,
        config
      );
      if (configuredVariantId && variantId && variantId === configuredVariantId) {
        return feeType;
      }
    }
    for (const feeType of OPERATIONAL_FEE_TYPES) {
      const { productId: configuredProductId } = getOperationalFeeIds(
        feeType,
        config
      );
      if (configuredProductId && productId && productId === configuredProductId) {
        return feeType;
      }
    }

    return null;
  }

  function normalizeFulfillmentType(rawValue) {
    const normalized = String(rawValue || '')
      .trim()
      .toLowerCase();
    if (!normalized) return null;
    if (normalized.includes('delivery')) return 'delivery';
    if (
      normalized.includes('pickup') ||
      normalized.includes('pick up') ||
      normalized.includes('pick-up')
    ) {
      return 'pickup';
    }
    return null;
  }

  function normalizeCustomAttributeKey(rawValue) {
    return String(rawValue || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function readNamedAttributeValue(attributes, targetKey) {
    const normalizedTargetKey = normalizeCustomAttributeKey(targetKey);
    if (!normalizedTargetKey || !attributes) return null;

    if (Array.isArray(attributes)) {
      for (const entry of attributes) {
        if (Array.isArray(entry) && entry.length >= 2) {
          const key = normalizeCustomAttributeKey(entry[0]);
          if (key === normalizedTargetKey) {
            return String(entry[1] || '').trim() || null;
          }
          continue;
        }

        if (!entry || typeof entry !== 'object') continue;
        const key = normalizeCustomAttributeKey(
          entry.key || entry.name || entry.first || entry.attribute || ''
        );
        if (key !== normalizedTargetKey) continue;
        const value = String(
          entry.value ?? entry.last ?? entry.val ?? entry.text ?? ''
        ).trim();
        if (value) return value;
      }
      return null;
    }

    if (typeof attributes === 'object') {
      for (const [rawKey, rawValue] of Object.entries(attributes)) {
        if (normalizeCustomAttributeKey(rawKey) !== normalizedTargetKey) continue;
        const value = String(rawValue ?? '').trim();
        if (value) return value;
      }
    }

    return null;
  }

  function extractFulfillmentTypeFromEntity(entity) {
    if (!entity || typeof entity !== 'object') return null;

    const directCandidates = [
      entity.fulfillment_type,
      entity.fulfillmentType,
      entity.delivery_type,
      entity.deliveryType,
    ];
    for (const candidate of directCandidates) {
      const normalized = normalizeFulfillmentType(candidate);
      if (normalized) return normalized;
    }

    const attributeSources = [
      entity.custom_attributes,
      entity.customAttributes,
      entity.attributes,
      entity.address_attributes,
      entity.addressAttributes,
      entity.order_attributes,
      entity.orderAttributes,
      entity.properties,
    ];
    for (const source of attributeSources) {
      const rawValue = readNamedAttributeValue(source, 'Fulfillment Type');
      const normalized = normalizeFulfillmentType(rawValue);
      if (normalized) return normalized;
    }

    if (entity.address && typeof entity.address === 'object') {
      return extractFulfillmentTypeFromEntity(entity.address);
    }

    return null;
  }

  function resolveSubscriptionAddressId(subscription) {
    const sharedResolver = window.etRechargeUtils?.resolveSubscriptionAddressId;
    if (typeof sharedResolver === 'function') {
      return sharedResolver(subscription);
    }
    const candidates = [
      subscription?.address_id,
      subscription?.addressId,
      subscription?.address?.id,
      subscription?.address?.address_id,
      subscription?.shipping_address_id,
      subscription?.shippingAddressId,
    ];
    for (const candidate of candidates) {
      const parsed = parseOptionalSubscriptionId(candidate);
      if (parsed) return parsed;
    }
    return null;
  }

  function normalizeAddressList(response) {
    if (!response || typeof response !== 'object') return [];
    if (Array.isArray(response.addresses)) return response.addresses;
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.results)) return response.results;
    return [];
  }

  async function resolveSubscriptionAddress(session, subscription) {
    const addressId = resolveSubscriptionAddressId(subscription);
    if (!addressId || !session) return null;

    try {
      const response = await getAddress(session, addressId);
      if (response?.address && typeof response.address === 'object') {
        return response.address;
      }
      if (
        response &&
        typeof response === 'object' &&
        parseOptionalSubscriptionId(response.id) === addressId
      ) {
        return response;
      }
    } catch (error) {
      // Some SDK footprints do not expose address.getAddress; fallback to list.
    }

    try {
      const response = await listAddresses(session, { limit: 250 });
      const addresses = normalizeAddressList(response);
      const match = addresses.find(
        (entry) =>
          parseOptionalSubscriptionId(entry?.id) === addressId ||
          parseOptionalSubscriptionId(entry?.address_id) === addressId
      );
      if (match && typeof match === 'object') return match;
    } catch (error) {
      // If address APIs are unavailable, we gracefully continue without fee enforcement.
    }

    return null;
  }

  async function resolveSubscriptionFulfillmentType(session, subscription) {
    const fromSubscription = extractFulfillmentTypeFromEntity(subscription);
    if (fromSubscription) return fromSubscription;

    const address = await resolveSubscriptionAddress(session, subscription);
    const fromAddress = extractFulfillmentTypeFromEntity(address);
    if (fromAddress) return fromAddress;

    return null;
  }

  function extractNextChargeDate(subscription) {
    if (!subscription || typeof subscription !== 'object') return null;

    const parsed = parseSubscriptionDateValue(subscription?.next_charge_scheduled_at);
    if (parsed) return parsed;

    return null;
  }

  function addDays(dateValue, daysToAdd) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }

    const days = Number.parseInt(String(daysToAdd || 0), 10);
    const safeDays = Number.isFinite(days) ? days : 0;
    const result = new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate()
    );
    result.setDate(result.getDate() + safeDays);
    return result;
  }

  function formatDisplayDate(dateValue) {
    if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
      return null;
    }

    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(dateValue);
    } catch (error) {
      return null;
    }
  }

  // Extracts time from Recharge's Fulfillment Date attribute value
  function formatDisplayTimeFromFulfillmentDateValue(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    // ISO date-time with optional timezone, e.g. "2026-02-24T08:45:00-08:00" or "2025-02-25T14:00:00"
    const embedded24h = raw.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::\d{2})?(?:[+-]\d{2}:?\d{2}|Z)?/);
    if (embedded24h) {
      const hours = parseInt(embedded24h[1], 10);
      const minutes = parseInt(embedded24h[2], 10);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
      const d = new Date(2000, 0, 1, hours, minutes, 0);
      try {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
      } catch (_) {
        const pad = (n) => String(n).padStart(2, '0');
        const h12 = hours === 0 ? 12 : hours > 12 ? hours % 12 : hours;
        const ampm = hours < 12 ? 'AM' : 'PM';
        return `${h12}:${pad(minutes)} ${ampm}`;
      }
    }

    // 12-hour time with AM/PM only, e.g. "8:45 AM" or "2:00 PM"
    const plain12h = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])$/);
    if (plain12h) {
      const hours = parseInt(plain12h[1], 10);
      const minutes = parseInt(plain12h[2], 10);
      const ampm = plain12h[3].toUpperCase();
      if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
      return `${hours}:${String(minutes).padStart(2, '0')} ${ampm === 'AM' ? 'AM' : 'PM'}`;
    }

    // 24-hour time only, e.g. "08:45" or "14:30"
    const plain24h = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (plain24h) {
      const hours = parseInt(plain24h[1], 10);
      const minutes = parseInt(plain24h[2], 10);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
      const d = new Date(2000, 0, 1, hours, minutes, 0);
      try {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
      } catch (_) {
        const pad = (n) => String(n).padStart(2, '0');
        const h12 = hours === 0 ? 12 : hours > 12 ? hours % 12 : hours;
        const ampm = hours < 12 ? 'AM' : 'PM';
        return `${h12}:${pad(minutes)} ${ampm}`;
      }
    }

    return null;
  }

  async function resolveOverviewDeliveryContext(session, subscription, options = {}) {
    if (!session || !subscription) return null;

    const chargeDate = extractNextChargeDate(subscription);
    if (!chargeDate) return null;

    const fulfillmentType =
      options?.fulfillmentType ??
      (await resolveSubscriptionFulfillmentType(session, subscription));
    const normalizedFulfillmentType = normalizeFulfillmentType(fulfillmentType);
    if (!normalizedFulfillmentType) return null;

    const leadDays =
      Number.isFinite(CONFIG.expectedLeadDaysFromChargeDate) && CONFIG.expectedLeadDaysFromChargeDate > 0
        ? CONFIG.expectedLeadDaysFromChargeDate
        : 2;
    const arrivalDate = addDays(chargeDate, leadDays);
    if (!arrivalDate) return null;

    const chargeDateText = formatDisplayDate(chargeDate);
    const arrivalDateText = formatDisplayDate(arrivalDate);
    if (!chargeDateText || !arrivalDateText) return null;

    let arrivalTimeText = null;
    if (normalizedFulfillmentType === 'delivery') {
      try {
        const address =
          options?.address ??
          (await resolveSubscriptionAddress(session, subscription));
        const orderAttributes = address?.order_attributes ?? address?.orderAttributes ?? address?.attributes;
        const fulfillmentDateValue = readNamedAttributeValue(orderAttributes, 'Fulfillment Date');
        arrivalTimeText = formatDisplayTimeFromFulfillmentDateValue(fulfillmentDateValue) || null;
      } catch (_) {
        arrivalTimeText = null;
      }
    }

    return {
      fulfillmentType: normalizedFulfillmentType,
      arrivalVerb:
        normalizedFulfillmentType === 'pickup' ? 'ready' : 'delivered',
      chargeDateText,
      arrivalDateText,
      arrivalTimeText: arrivalTimeText || undefined,
    };
  }

  function hasCompleteDeliveryContext(deliveryContext) {
    if (!deliveryContext || typeof deliveryContext !== 'object') return false;

    return Boolean(
      String(deliveryContext.fulfillmentType || '').trim() &&
        String(deliveryContext.arrivalVerb || '').trim() &&
        String(deliveryContext.chargeDateText || '').trim() &&
        String(deliveryContext.arrivalDateText || '').trim()
    );
  }


  function createOperationalFeeTemplateFromItem(item, fallback = {}) {
    const variantId = normalizeExternalResourceId(
      item?.external_variant_id || fallback.variantId || null
    );
    const productId = normalizeExternalResourceId(
      item?.external_product_id || fallback.productId || null
    );
    if (!variantId && !productId) return null;

    const collectionId =
      normalizeCollectionId(
        item?.collection_id || item?.__collectionId || fallback.collectionId
      ) || 'other';
    const collectionSource =
      String(
        item?.collection_source ||
          item?.__collectionSource ||
          fallback.collectionSource ||
          'shopify'
      ).trim() || 'shopify';
    const label =
      String(
        item?.__label ||
          item?.product_title ||
          item?.title ||
          item?.name ||
          fallback.label ||
          'Fee'
      ).trim() || 'Fee';

    const unitPriceCandidate = Number.isFinite(item?.__unitPrice)
      ? item.__unitPrice
      : resolveItemUnitPrice(item);
    const retailPriceCandidate = Number.isFinite(item?.__retailPrice)
      ? item.__retailPrice
      : resolveItemRetailPrice(item);

    const template = {
      quantity: 0,
      external_product_id: productId || '',
      external_variant_id: variantId || '',
      collection_id: collectionId,
      collection_source: collectionSource,
      __collectionId: collectionId,
      __collectionSource: collectionSource,
      __label: label,
      __subtitle: item?.__subtitle || item?.variant_title || null,
      __optionValues: normalizeOptionValues(
        item?.__optionValues || item?.option_values || []
      ),
      __imageUrl: resolveBundleItemImageUrl(item),
      __retailPrice:
        Number.isFinite(retailPriceCandidate) && retailPriceCandidate >= 0
          ? retailPriceCandidate
          : null,
      __unitPrice:
        Number.isFinite(unitPriceCandidate) && unitPriceCandidate >= 0
          ? unitPriceCandidate
          : null,
      __isLockedFee: true,
    };

    if (Number.isFinite(template.__unitPrice) && template.__unitPrice >= 0) {
      template.price = template.__unitPrice.toFixed(2);
    }

    return template;
  }

  function createFallbackOperationalFeeTemplate(feeType, feeConfig) {
    const feeMeta = getOperationalFeeTypeMeta(feeType);
    if (!feeMeta) return null;
    const { variantId, productId } = getOperationalFeeIds(feeType, feeConfig);

    return createOperationalFeeTemplateFromItem(null, {
      label: feeMeta.label,
      variantId,
      productId,
      collectionId: getOperationalFeeCollectionId(feeConfig),
      collectionSource: 'shopify',
    });
  }

  function buildOperationalFeeTemplatesFromItems(items, feeConfig) {
    const templates = createEmptyOperationalFeeTemplates();
    const safeItems = Array.isArray(items) ? items : [];
    const feeCollectionId = getOperationalFeeCollectionId(feeConfig);

    for (const item of safeItems) {
      if (!item || typeof item !== 'object') continue;
      const feeType = resolveConfiguredFeeType(item, feeConfig);
      if (!feeType || templates[feeType]) continue;
      const { variantId, productId } = getOperationalFeeIds(feeType, feeConfig);
      templates[feeType] = createOperationalFeeTemplateFromItem(item, {
        variantId,
        productId,
        collectionId: feeCollectionId,
      });
    }

    return templates;
  }

  // The one productSearch shape every catalog lookup uses. Identical params
  // are what let the shared read memo collapse the metadata, fee and price
  // lookups for the same variants into one request (#341). `limit` caps
  // PRODUCTS, of which a variant-id filter can return at most one per id.
  function searchProductsByVariantIds(session, variantIds) {
    return productSearch(session, {
      format_version: '2022-06',
      external_variant_ids: variantIds,
      include_deleted: false,
      // Bundle items can point to hidden variants, so keep them visible.
      exclude_hidden: false,
      limit: Math.min(250, variantIds.length * 8),
    });
  }

  async function fetchRechargeFeeTemplatesByVariantId(
    session,
    variantIds,
    feeConfig
  ) {
    const safeVariantIds = Array.from(
      new Set(
        (Array.isArray(variantIds) ? variantIds : [])
          .map((value) => normalizeExternalResourceId(value))
          .filter(Boolean)
      )
    );
    if (!session || !safeVariantIds.length) {
      return new Map();
    }

    const response = await searchProductsByVariantIds(session, safeVariantIds);

    const templatesByVariantId = new Map();
    const products = Array.isArray(response?.products) ? response.products : [];
    for (const product of products) {
      const productId = normalizeExternalResourceId(
        product?.external_product_id || product?.id || null
      );
      const productTitle = String(product?.title || '').trim();
      const productImage =
        pickImageUrl(Array.isArray(product?.images) ? product.images[0] : null) ||
        null;
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      for (const variant of variants) {
        const variantId = normalizeExternalResourceId(
          variant?.external_variant_id || variant?.id || null
        );
        if (!variantId || !safeVariantIds.includes(variantId)) continue;
        if (templatesByVariantId.has(variantId)) continue;

        const unitPrice =
          resolveRechargeDiscountUnitPrice(variant) ||
          resolveRechargeDiscountUnitPrice(product);

        const template = createOperationalFeeTemplateFromItem(
          {
            external_product_id: productId || '',
            external_variant_id: variantId,
            product_title: productTitle || null,
            title: productTitle || null,
            variant_title: variant?.title || null,
            image: productImage,
            price: unitPrice,
            unit_price: unitPrice,
          },
          {
            collectionId: getOperationalFeeCollectionId(feeConfig),
            collectionSource: 'shopify',
          }
        );
        if (!template) continue;
        templatesByVariantId.set(variantId, template);
      }
    }

    return templatesByVariantId;
  }

  async function resolveOperationalFeeContext(session, subscription, items) {
    const feeConfig = resolveOperationalFeeConfig();
    const fulfillmentType = await resolveSubscriptionFulfillmentType(
      session,
      subscription
    );
    const templatesByType = buildOperationalFeeTemplatesFromItems(items, feeConfig);

    const missingVariantsByType = OPERATIONAL_FEE_TYPES.map((feeType) => {
      const { variantId } = getOperationalFeeIds(feeType, feeConfig);
      return { feeType, variantId: variantId || null };
    }).filter(
      ({ feeType, variantId }) => Boolean(variantId) && !templatesByType[feeType]
    );
    const missingVariantIds = missingVariantsByType.map(
      ({ variantId }) => variantId
    );

    if (missingVariantIds.length && session) {
      try {
        const fetchedByVariantId = await fetchRechargeFeeTemplatesByVariantId(
          session,
          missingVariantIds,
          feeConfig
        );
        for (const { feeType, variantId } of missingVariantsByType) {
          if (!templatesByType[feeType] && variantId) {
            templatesByType[feeType] = fetchedByVariantId.get(variantId) || null;
          }
        }
      } catch (error) {
        console.warn('[update-meals] Unable to hydrate fee templates from Recharge.', error);
      }
    }

    for (const feeType of OPERATIONAL_FEE_TYPES) {
      const { variantId } = getOperationalFeeIds(feeType, feeConfig);
      if (!templatesByType[feeType] && variantId) {
        templatesByType[feeType] = createFallbackOperationalFeeTemplate(
          feeType,
          feeConfig
        );
      }
    }

    return {
      feeConfig,
      fulfillmentType,
      templatesByType,
    };
  }

  function setActiveOperationalFeeContext(feeContext) {
    const context = feeContext && typeof feeContext === 'object' ? feeContext : {};
    state.activeFeeConfig =
      context.feeConfig && typeof context.feeConfig === 'object'
        ? context.feeConfig
        : resolveOperationalFeeConfig();
    state.activeFulfillmentType = normalizeFulfillmentType(
      context.fulfillmentType || null
    );
    state.activeFeeTemplatesByType =
      context.templatesByType && typeof context.templatesByType === 'object'
        ? context.templatesByType
        : createEmptyOperationalFeeTemplates();
  }

  function calculateSubtotalExcludingOperationalFees(items, feeConfig) {
    const safeItems = Array.isArray(items) ? items : [];
    return safeItems.reduce((sum, item) => {
      if (!item || typeof item !== 'object') return sum;
      if (isFeeItem(item, feeConfig)) return sum;

      const quantity = parseInteger(item.quantity ?? 0);
      if (quantity <= 0) return sum;

      const unitPrice = Number.isFinite(item.__unitPrice)
        ? item.__unitPrice
        : resolveItemUnitPrice(item);
      if (!(Number.isFinite(unitPrice) && unitPrice >= 0)) return sum;

      return sum + quantity * unitPrice;
    }, 0);
  }

  function setOperationalFeeLineQuantity(
    items,
    feeType,
    shouldInclude,
    feeConfig,
    templatesByType
  ) {
    const { variantId: desiredVariantId, productId: desiredProductId } =
      getOperationalFeeIds(feeType, feeConfig);
    if (!desiredVariantId && !desiredProductId) return;

    const matchingIndexes = [];
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      if (resolveConfiguredFeeType(item, feeConfig) !== feeType) continue;
      matchingIndexes.push(index);
    }

    if (matchingIndexes.length) {
      matchingIndexes.forEach((index, matchIndex) => {
        const item = items[index];
        if (!item || typeof item !== 'object') return;
        item.__isLockedFee = true;
        item.quantity = shouldInclude && matchIndex === 0 ? 1 : 0;
        if (desiredVariantId && !item.external_variant_id) {
          item.external_variant_id = desiredVariantId;
        }
        if (desiredProductId && !item.external_product_id) {
          item.external_product_id = desiredProductId;
        }
      });
      return;
    }

    if (!shouldInclude) return;

    const templateFromContext =
      templatesByType && typeof templatesByType === 'object'
        ? templatesByType[feeType]
        : null;
    const fallbackTemplate = createFallbackOperationalFeeTemplate(feeType, feeConfig);
    const template =
      templateFromContext && typeof templateFromContext === 'object'
        ? templateFromContext
        : fallbackTemplate;
    if (!template || typeof template !== 'object') return;

    const nextItem = {
      ...template,
      quantity: 1,
      external_product_id:
        template.external_product_id || desiredProductId || '',
      external_variant_id:
        template.external_variant_id || desiredVariantId || '',
      __isLockedFee: true,
    };
    if (Number.isFinite(nextItem.__unitPrice) && nextItem.__unitPrice >= 0) {
      nextItem.price = nextItem.__unitPrice.toFixed(2);
    }
    items.push(nextItem);
  }

  function applyOperationalFeeRulesToItems(items, feeContext) {
    if (!Array.isArray(items)) return [];

    const context =
      feeContext && typeof feeContext === 'object' ? feeContext : {};
    const feeConfig =
      context.feeConfig && typeof context.feeConfig === 'object'
        ? context.feeConfig
        : state.activeFeeConfig || resolveOperationalFeeConfig();
    const fulfillmentType = normalizeFulfillmentType(
      context.fulfillmentType || state.activeFulfillmentType
    );
    const templatesByType =
      context.templatesByType && typeof context.templatesByType === 'object'
        ? context.templatesByType
        : state.activeFeeTemplatesByType;

    const hasAnyConfiguredFee = OPERATIONAL_FEE_TYPES.some((feeType) => {
      const { variantId, productId } = getOperationalFeeIds(feeType, feeConfig);
      return Boolean(variantId || productId);
    });
    if (!hasAnyConfiguredFee) {
      return items;
    }

    const subtotal = calculateSubtotalExcludingOperationalFees(items, feeConfig);
    const isDelivery = fulfillmentType === 'delivery';
    const isPickup = fulfillmentType === 'pickup';
    const hasExistingPackagingFee = items.some(
      (item) =>
        resolveConfiguredFeeType(item, feeConfig) === 'packaging' &&
        parseInteger(item?.quantity ?? 0) > 0
    );

    // If fulfillment type cannot be resolved, preserve existing packaging fee state
    // so delivery subscriptions do not lose mandatory fees due to metadata drift.
    const shouldIncludePackaging = isDelivery
      ? true
      : isPickup
        ? false
        : hasExistingPackagingFee;
    const shouldIncludeDeliveryFee = isDelivery
      ? subtotal < feeConfig.subscriptionDeliveryFeeThreshold
      : false;

    setOperationalFeeLineQuantity(
      items,
      'packaging',
      shouldIncludePackaging,
      feeConfig,
      templatesByType
    );
    setOperationalFeeLineQuantity(
      items,
      'delivery',
      shouldIncludeDeliveryFee,
      feeConfig,
      templatesByType
    );

    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      item.__isLockedFee = isFeeItem(item, feeConfig);
    }

    return items;
  }

  function applyActiveOperationalFeeRules() {
    state.activeBundleItems = applyOperationalFeeRulesToItems(
      state.activeBundleItems,
      {
        feeConfig: state.activeFeeConfig,
        fulfillmentType: state.activeFulfillmentType,
        templatesByType: state.activeFeeTemplatesByType,
      }
    );
  }

  function beginEditorLoadForSubscription(subscriptionId) {
    // Centralize inline-editor bootstrapping so open flow has one pre-load path.
    // Opening the editor abandons overview hydration, so close any active span
    // with an explicit outcome instead of letting it drift.
    endActiveOverviewHydrationSpan({
      'et.overview_hydration.outcome': 'editor-opened',
    });
    state.activeSubscriptionId = subscriptionId;
    state.collectionTitleFallbackAtMs = Date.now() + 800;
    state.editorOpen = true;
    renderOverviewHeaderSlot({
      isLoading: true,
      isEditing: true,
      subscriptionId,
    });
    setEditorLoadingState(LOADING_MEALS_LABEL);
  }

  function syncActiveBundleComputedState(options = {}) {
    // Keep derived bundle state in one place to avoid drift during async updates.
    // Ordering snapshots are captured only at editor-open time so list positions
    // remain fixed while users adjust quantities.
    if (options.captureInitialOrdering === true) {
      state.activeBundleInitialQuantityByMealKey = buildInitialBundleQuantityByMealKey(
        state.activeBundleItems
      );
      state.activeBundleInitialCollectionStatsById = buildBundleCollectionStatsById(
        state.activeBundleItems
      );
    }
    state.activeCurrencyCode = resolveCurrencyCode(
      state.activeSubscription,
      state.activeBundleSelection,
      state.activeBundleItems
    );
  }

  function resetAddableMealsState() {
    // Reset addable catalog state before each fresh catalog fetch.
    state.addableMealsCatalog = [];
    state.addableMealsSource = null;
    state.addableMealsLoading = true;
    state.addableMealsError = null;
  }

  function initializeActiveSubscriptionState({
    subscription,
    bundleSelectionId,
    bundleSelection,
    editableItems,
  }) {
    // Persist active session context for rerenders and save/update actions.
    state.activeSubscription = subscription;
    state.activeBundleSelectionId = bundleSelectionId;
    state.activeBundleSelection = bundleSelection;
    state.activeBundleItems = editableItems;
    state.activeVariantProfile = buildVariantProfile(editableItems);
    state.activeFulfillmentType = null;
    state.activeFeeConfig = resolveOperationalFeeConfig();
    state.activeFeeTemplatesByType = createEmptyOperationalFeeTemplates();
    resetAddableMealsState();
    syncActiveBundleComputedState({ captureInitialOrdering: true });
  }

  function isStaleAddMealsRequest(requestId) {
    // Ignore async responses that belong to superseded editor loads.
    return requestId !== state.addMealsRequestId;
  }

  function renderActiveEditor(options = {}) {
    // Thin wrapper keeps editor render call sites concise and consistent.
    renderEditorForm(
      state.activeBundleItems,
      state.activeCurrencyCode,
      options
    );
  }

  // The subscription and its bundle, hydrated into the editor's item shape:
  // what the editor reads before it can build a catalog. Shared with the idle
  // prefetch so both ask the read memo the same questions.
  async function loadEditableSubscription(subscriptionId) {
    // Session bootstrapping is memoized, so repeated opens are fast after first run.
    const session = await getPortalSession();
    const subscription = await getSubscriptionById(session, subscriptionId);
    const bundleSelectionId = await resolveBundleSelectionId(
      session,
      subscription
    );

    if (!bundleSelectionId) {
      throw new Error(
        `No bundle selection found for subscription ${subscriptionId}.`
      );
    }

    const bundleSelection = await getBundleSelectionById(
      session,
      bundleSelectionId
    );
    // Normalize incoming bundle items to the local editing shape expected by UI.
    let editableItems = normalizeEditableBundleItems(bundleSelection);
    // Hydrate variant titles/options before building profile so addable variants
    // align with the active subscription's existing variant title preference.
    editableItems = await hydrateBundleItemsFromStorefrontProductsById(
      editableItems,
      session
    );
    return {
      session,
      subscription,
      bundleSelectionId,
      bundleSelection,
      editableItems,
    };
  }

  /**
   * Main editor-open orchestration path.
   *
   * High-level flow:
   * 1) Switch slot into editing mode and show loading UI.
   * 2) Resolve session/subscription/bundle selection.
   * 3) Normalize existing bundle items into UI shape.
   * 4) Hydrate product metadata (labels/images/pricing hints).
   * 5) Begin parallel work:
   *    - addable catalog load from Storefront/Recharge pricing
   *    - operational fee context resolution
   * 6) Ignore stale async responses using request IDs.
   * 7) Render final interactive editor with optional progressive hydration.
   *
   * UX goal:
   * - Users should either see a complete editor or an explicit fallback/error
   *   state, never a half-hydrated ambiguous edit surface.
   */
  async function openUpdateMealsFlow(subscriptionId) {
    if (!subscriptionId) return;
    beginEditorLoadForSubscription(subscriptionId);
    startEditorOpenSpan();
    const editorOpenSpan = state.editorOpenSpan;

    try {
      const {
        session,
        subscription,
        bundleSelectionId,
        bundleSelection,
        editableItems,
      } = await loadEditableSubscription(subscriptionId);
      initializeActiveSubscriptionState({
        subscription,
        bundleSelectionId,
        bundleSelection,
        editableItems,
      });
      const feeContextPromise = resolveOperationalFeeContext(
        session,
        subscription,
        editableItems
      ).catch((error) => {
        console.warn('[update-meals] Unable to resolve operational fee context.', error);
        return null;
      });
      // Bump request ID so out-of-order async responses cannot overwrite newer UI.
      const addMealsRequestId = ++state.addMealsRequestId;

      try {
        const [catalogState, feeContext] = await Promise.all([
          loadAddableMealsCatalog(state.activeVariantProfile, {
            portalSession: session,
            activeItems: state.activeBundleItems,
          }),
          feeContextPromise,
        ]);
        // Ignore stale responses from prior editor instances or rapid reopen cycles.
        if (isStaleAddMealsRequest(addMealsRequestId)) return;

        setActiveOperationalFeeContext(feeContext);
        state.addableMealsCatalog = catalogState.meals;
        state.addableMealsSource = catalogState.source;
        state.activeBundleItems = hydrateBundleItemsFromCatalogMeals(
          state.activeBundleItems,
          state.addableMealsCatalog
        );
        state.activeBundleItems =
          await hydrateBundleItemsFromStorefrontProductsById(
            state.activeBundleItems,
            session
          );
        applyActiveOperationalFeeRules();
        syncActiveBundleComputedState({ captureInitialOrdering: true });
        state.addableMealsLoading = false;
        state.addableMealsError = null;
        renderActiveEditor({ progressiveHydration: true });
        finalizeEditorOpenSpanWhenPainted(editorOpenSpan, 'rendered');
      } catch (catalogError) {
        // Ignore stale failures as well; only latest request should affect UI.
        if (isStaleAddMealsRequest(addMealsRequestId)) return;
        console.warn('[update-meals] Unable to load addable meals catalog:', catalogError);
        const feeContext = await feeContextPromise;
        setActiveOperationalFeeContext(feeContext);
        applyActiveOperationalFeeRules();
        syncActiveBundleComputedState({ captureInitialOrdering: true });
        state.addableMealsCatalog = [];
        state.addableMealsSource = null;
        state.addableMealsLoading = false;
        state.addableMealsError =
          'Unable to load meals right now. You can still update current quantities.';
        renderActiveEditor();
        finalizeEditorOpenSpanWhenPainted(editorOpenSpan, 'catalog-failed');
      }

    } catch (error) {
      console.error('[update-meals] Failed to open Update Meals flow:', error);
      if (state.editorOpenSpan === editorOpenSpan) {
        endEditorOpenSpan({ 'et.portal.outcome': 'error' });
      }
      renderEditorError(
        'We could not load the subscription editor. Please close this editor and try again.'
      );
    }
  }

  // Returns a memoized portal session promise so concurrent callers share one
  // auth/bootstrap sequence instead of racing duplicate login requests.
  async function getPortalSession() {
    const sharedGetPortalSession = window.etRechargeUtils?.getPortalSession;
    if (typeof sharedGetPortalSession === 'function') {
      return sharedGetPortalSession();
    }
    if (state.sessionPromise) return state.sessionPromise;

    state.sessionPromise = (async () => {
      await ensureSdkInitialized();
      return loginCustomerPortal();
    })();

    try {
      return await state.sessionPromise;
    } catch (error) {
      state.sessionPromise = null;
      throw error;
    }
  }

  // Session re-auth is owned by the shared util, but this asset still has to
  // work if it loads without one -- same fallback contract as getPortalSession
  // above. Degrades to a single attempt, which is the behaviour before this.
  async function withPortalSessionRetry(run) {
    const sharedWithRetry = window.etRechargeUtils?.withPortalSessionRetry;
    if (typeof sharedWithRetry === 'function') {
      return sharedWithRetry(run);
    }
    return run(await getPortalSession());
  }

  function isPortalPageUnloading() {
    return window.etRechargeUtils?.isPageUnloading?.() === true;
  }

  // Initializes Recharge client once per page lifecycle.
  // Shared SDK bootstrap is the single owner of Recharge load/init concerns.
  async function ensureSdkInitialized() {
    if (state.sdkInitialized) return;
    const sharedSdk = getSharedRechargeSdk();
    if (typeof sharedSdk.ready !== 'function') {
      throw new Error(
        'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js initializes before affinity-update-meals.js.'
      );
    }
    await sharedSdk.ready();
    state.sdkInitialized = true;
  }

  // Served from the shared read memo, which writes and Affinity:refresh clear.
  async function getSubscriptions(session) {
    const response = await listSubscriptions(session, { limit: 250 });
    return normalizeSubscriptionList(response);
  }

  // Recharge endpoints are inconsistent on envelope shape (`data`, `results`, etc.).
  // Normalize to a single array contract for upstream callers.
  function normalizeSubscriptionList(response) {
    if (!response || typeof response !== 'object') return [];
    if (Array.isArray(response.subscriptions)) return response.subscriptions;
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.results)) return response.results;
    return [];
  }

  // Some SDK versions return `{ subscription: {...} }`, others return raw object.
  async function getSubscriptionById(session, subscriptionId) {
    const response = await getSubscription(session, subscriptionId);

    if (response?.subscription && typeof response.subscription === 'object') {
      return response.subscription;
    }
    if (response && typeof response === 'object') {
      return response;
    }
    throw new Error(`Subscription ${subscriptionId} was not returned by API.`);
  }

  // Resolve bundle selection ID with progressive fallbacks:
  // 1) direct fields on subscription
  // 2) list lookup by purchase_item_id/subscription id
  // 3) first selection for single-subscription edge cases
  async function resolveBundleSelectionId(session, subscription) {
    // Preferred path: a direct ID on the subscription payload.
    const directIdCandidates = [
      subscription?.bundle_selection_id,
      subscription?.bundleSelectionId,
      subscription?.bundle_selection?.id,
      subscription?.bundleSelection?.id,
    ];
    for (const candidate of directIdCandidates) {
      const parsed = parseOptionalSubscriptionId(candidate);
      if (parsed) return parsed;
    }

    // Fallback path: list bundle selections and match by purchase_item_id.
    const response = await listBundleSelections(session, { limit: 250 });

    const selections = normalizeBundleSelectionList(response);
    if (!selections.length) return null;

    const subscriptionId = parseOptionalSubscriptionId(subscription?.id);
    const purchaseItemId = parseOptionalSubscriptionId(
      subscription?.purchase_item_id
    );

    const match = selections.find((entry) => {
      const entryPurchaseItemId = parseOptionalSubscriptionId(
        entry?.purchase_item_id
      );
      if (!entryPurchaseItemId) return false;
      return (
        (subscriptionId && entryPurchaseItemId === subscriptionId) ||
        (purchaseItemId && entryPurchaseItemId === purchaseItemId)
      );
    });

    if (match) {
      return parseOptionalSubscriptionId(match.id);
    }

    // Final fallback: first bundle selection. This is better than failing hard
    // for single-subscription customers whose payload shape differs slightly.
    return parseOptionalSubscriptionId(selections[0]?.id);
  }

  // Normalization helper for bundle selection list envelopes.
  function normalizeBundleSelectionList(response) {
    if (!response || typeof response !== 'object') return [];
    if (Array.isArray(response.bundle_selections)) return response.bundle_selections;
    if (Array.isArray(response.bundleSelections)) return response.bundleSelections;
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.results)) return response.results;
    return [];
  }

  // Similar defensive envelope handling as subscription-by-id call.
  async function getBundleSelectionById(session, bundleSelectionId) {
    const response = await getBundleSelection(session, bundleSelectionId);

    if (response?.bundle_selection && typeof response.bundle_selection === 'object') {
      return response.bundle_selection;
    }
    if (response && typeof response === 'object') {
      return response;
    }

    throw new Error(`Bundle selection ${bundleSelectionId} was not returned by API.`);
  }

  // Converts bundle selection items into UI-editable view models with
  // normalized quantity, label, pricing, and lock metadata.
  function normalizeEditableBundleItems(bundleSelection) {
    const sourceItems = Array.isArray(bundleSelection?.items)
      ? bundleSelection.items
      : [];

    return sourceItems.map((item) => {
      const normalizedExternalProductId =
        normalizeExternalResourceId(item?.external_product_id || null) || '';
      const normalizedExternalVariantId =
        normalizeExternalResourceId(item?.external_variant_id || null) || '';
      const fallbackProductId =
        normalizeExternalResourceId(item?.product_id || null) || '';
      const fallbackVariantId =
        normalizeExternalResourceId(item?.variant_id || null) || '';
      const label =
        item.product_title ||
        item.title ||
        item.name ||
        normalizedExternalProductId ||
        normalizedExternalVariantId ||
        fallbackProductId ||
        fallbackVariantId ||
        'Meal item';

      const nextItem = {
        ...item,
        external_product_id: normalizedExternalProductId,
        external_variant_id: normalizedExternalVariantId,
        quantity: parseInteger(item.quantity ?? 0),
        __label: label,
        __subtitle: item.variant_title || null,
        __optionValues: normalizeOptionValues(item.option_values || []),
        __imageUrl: resolveBundleItemImageUrl(item),
        __retailPrice: resolveItemRetailPrice(item),
        // Keep unit price as a numeric value so we can calculate row totals
        // and subscription totals without reparsing on every interaction.
        __unitPrice: resolveItemUnitPrice(item),
      };

      // Fee lines (Packaging/Delivery/Fee) should remain visible but
      // immutable so customers cannot accidentally alter operational charges.
      nextItem.__isLockedFee = isFeeItem(nextItem);
      return nextItem;
    });
  }

  // The normalized, deduped ids `idOf` reads from `items`.
  function collectBundleItemIds(items, idOf) {
    return Array.from(
      new Set(items.map((item) => normalizeExternalResourceId(idOf(item) || null)).filter(Boolean))
    );
  }

  function isBundleItemMetadataIncomplete(item) {
    if (!item || typeof item !== 'object' || item.__isLockedFee) return false;

    const label = String(item.__label || '').trim();
    const productId =
      normalizeExternalResourceId(
        item.external_product_id || item.product_id || null
      ) || '';
    const variantId =
      normalizeExternalResourceId(
        item.external_variant_id || item.variant_id || null
      ) || '';
    const missingImage = !String(resolveBundleItemImageUrl(item) || '').trim();
    const missingSubtitle = !String(item.__subtitle || item.variant_title || '').trim();
    const missingOptions = !normalizeOptionValues(
      item.__optionValues || item.option_values || []
    ).length;
    const missingUnitPrice =
      !Number.isFinite(item.__unitPrice) || item.__unitPrice < 0;
    const placeholderLabel = isPlaceholderMealLabel(
      label,
      productId,
      variantId
    );

    return (
      missingImage ||
      missingSubtitle ||
      missingOptions ||
      missingUnitPrice ||
      placeholderLabel
    );
  }

  // Enrich bundle line-item display metadata using already-loaded catalog meals.
  // This avoids a second network fetch for "current subscription" item details.
  function hydrateBundleItemsFromCatalogMeals(items, catalogMeals) {
    const safeItems = Array.isArray(items) ? items : [];
    const safeCatalogMeals = Array.isArray(catalogMeals) ? catalogMeals : [];
    if (!safeItems.length || !safeCatalogMeals.length) return safeItems;

    const mealByVariantId = new Map();
    const mealByProductId = new Map();

    for (const meal of safeCatalogMeals) {
      if (!meal || typeof meal !== 'object') continue;
      const variantId = String(meal.external_variant_id || '').trim();
      const productId = String(meal.external_product_id || '').trim();
      if (variantId && !mealByVariantId.has(variantId)) {
        mealByVariantId.set(variantId, meal);
      }
      if (productId && !mealByProductId.has(productId)) {
        mealByProductId.set(productId, meal);
      }
    }

    return safeItems.map((item) => {
      if (!item || typeof item !== 'object' || item.__isLockedFee) {
        return item;
      }

      const variantId = String(item.external_variant_id || '').trim();
      const productId = String(item.external_product_id || '').trim();
      const catalogMeal =
        (variantId ? mealByVariantId.get(variantId) : null) ||
        (productId ? mealByProductId.get(productId) : null) ||
        null;

      if (!catalogMeal) return item;

      const catalogCollectionId = String(catalogMeal.__collectionId || '').trim();
      const nextCollectionId = catalogCollectionId || String(item.collection_id || '').trim();
      const nextCollectionSource = String(
        catalogMeal.__collectionSource || item.collection_source || 'shopify'
      ).trim();

      const nextItem = {
        ...item,
        collection_id: nextCollectionId || item.collection_id,
        collection_source: nextCollectionSource || item.collection_source,
        __collectionId: nextCollectionId || item.__collectionId || null,
        __collectionSource: nextCollectionSource || item.__collectionSource || null,
        __collectionName:
          String(catalogMeal.__collectionName || '').trim() ||
          item.__collectionName ||
          null,
        __label: catalogMeal.__label || item.__label,
        __subtitle: catalogMeal.__subtitle || item.__subtitle || null,
        __optionValues: normalizeOptionValues(
          catalogMeal.__optionValues || item.__optionValues || item.option_values || []
        ),
        __imageUrl: resolveBundleItemImageUrl(catalogMeal) || resolveBundleItemImageUrl(item),
        __retailPrice:
          Number.isFinite(catalogMeal.__retailPrice) && catalogMeal.__retailPrice >= 0
            ? catalogMeal.__retailPrice
            : item.__retailPrice,
        __unitPrice:
          Number.isFinite(item.__unitPrice) && item.__unitPrice >= 0
            ? item.__unitPrice
            : Number.isFinite(catalogMeal.__unitPrice) && catalogMeal.__unitPrice >= 0
              ? catalogMeal.__unitPrice
              : item.__unitPrice,
      };

      nextItem.__isLockedFee = isFeeItem(nextItem);
      return nextItem;
    });
  }

  async function hydrateBundleItemsFromStorefrontProductsById(items, session = null) {
    const safeItems = Array.isArray(items) ? items : [];
    if (!safeItems.length) return safeItems;

    const incompleteItems = safeItems.filter(isBundleItemMetadataIncomplete);

    // Start the ReCharge metadata lookup now, alongside Storefront, instead of
    // after it (#362). That is only free because the Storefront query in
    // requestStorefrontProductsByIds selects no `selectedOptions`: an item
    // missing option values stays incomplete after the Storefront pass, so the
    // lookup would run anyway. Whoever adds that field should drop this early
    // start in the same change (#377) -- otherwise it
    // becomes a wasted request on every load. It asks for every variant
    // incomplete before the Storefront pass, a superset of those still
    // incomplete after it, since that pass only fills fields in.
    const speculativeVariantIds = session
      ? collectBundleItemIds(incompleteItems, (item) => item.external_variant_id || item.variant_id)
      : [];
    // Settles either way, so a lookup the Storefront pass makes unnecessary
    // never surfaces as an unhandled rejection -- or as a misleading warning.
    const rechargeMetadataPromise = speculativeVariantIds.length
      ? fetchRechargeBundleItemMetadataByVariantIds(session, speculativeVariantIds).then(
          (metadata) => ({ metadata }),
          (error) => ({ error })
        )
      : null;

    const unresolvedProductIds = collectBundleItemIds(
      incompleteItems,
      (item) => item.external_product_id || item.product_id
    );

    let hydratedItems = safeItems;
    const storefrontConfig = resolveStorefrontConfig();
    if (unresolvedProductIds.length && storefrontConfig.storefrontAccessToken) {
      let storefrontProductsById = new Map();
      try {
        storefrontProductsById = await fetchStorefrontProductsByIds(
          storefrontConfig,
          unresolvedProductIds
        );
      } catch (error) {
        console.warn(
          '[update-meals] Unable to hydrate existing subscription meals from Storefront API.',
          error
        );
      }

      if (storefrontProductsById instanceof Map && storefrontProductsById.size) {
        hydratedItems = hydratedItems.map((item) => {
          if (!item || typeof item !== 'object' || item.__isLockedFee) return item;

          const productId =
            normalizeExternalResourceId(
              item.external_product_id || item.product_id || null
            ) || '';
          if (!productId) return item;

          const product = storefrontProductsById.get(productId);
          if (!product || typeof product !== 'object') return item;

          const variantId =
            normalizeExternalResourceId(
              item.external_variant_id || item.variant_id || null
            ) || '';
          const matchingVariant =
            variantId && Array.isArray(product.variants)
              ? product.variants.find(
                  (variant) => String(variant?.id || '').trim() === variantId
                ) || null
              : null;

          const nextLabel = String(item.__label || '').trim();
          const next = {
            ...item,
            external_product_id: productId,
            external_variant_id: variantId,
          };
          if (
            isPlaceholderMealLabel(nextLabel, productId, variantId) &&
            String(product.title || '').trim()
          ) {
            next.__label = String(product.title || '').trim();
          }
          if (!String(item.__imageUrl || '').trim()) {
            next.__imageUrl = resolveBundleProductImage(product, matchingVariant);
          }
          if (
            !String(item.__subtitle || '').trim() &&
            String(matchingVariant?.title || '').trim()
          ) {
            next.__subtitle = String(matchingVariant.title || '').trim();
          }
          if (
            !normalizeOptionValues(item.__optionValues || item.option_values || []).length &&
            Array.isArray(matchingVariant?.options)
          ) {
            next.__optionValues = normalizeOptionValues(matchingVariant.options);
          }
          if (
            (!Number.isFinite(item.__retailPrice) || item.__retailPrice < 0) &&
            Number.isFinite(matchingVariant?.price) &&
            matchingVariant.price >= 0
          ) {
            next.__retailPrice = matchingVariant.price;
          }
          if (
            (!Number.isFinite(item.__unitPrice) || item.__unitPrice < 0) &&
            Number.isFinite(matchingVariant?.price) &&
            matchingVariant.price >= 0
          ) {
            next.__unitPrice = matchingVariant.price;
          }
          const hasNoItemTags =
            item.__tags == null ||
            (Array.isArray(item.__tags) && item.__tags.length === 0);
          const hasProductTags = Array.isArray(product.tags) && product.tags.length > 0;

          if (hasNoItemTags && hasProductTags) {
            next.__tags = product.tags;
          }
          next.__isLockedFee = isFeeItem(next);

          return next;
        });
      }
    }

    if (!rechargeMetadataPromise || !hydratedItems.some(isBundleItemMetadataIncomplete)) {
      return hydratedItems;
    }

    const { metadata: rechargeMetadataByVariantId, error: rechargeMetadataError } =
      await rechargeMetadataPromise;
    if (rechargeMetadataError) {
      console.warn(
        '[update-meals] Unable to hydrate existing subscription meals from Recharge API.',
        rechargeMetadataError
      );
      return hydratedItems;
    }
    if (!(rechargeMetadataByVariantId instanceof Map) || !rechargeMetadataByVariantId.size) {
      return hydratedItems;
    }

    return hydratedItems.map((item) => {
      if (!item || typeof item !== 'object' || item.__isLockedFee) return item;

      const variantId =
        normalizeExternalResourceId(
          item.external_variant_id || item.variant_id || null
        ) || '';
      if (!variantId) return item;

      const metadata = rechargeMetadataByVariantId.get(variantId);
      if (!metadata || typeof metadata !== 'object') return item;

      const next = {
        ...item,
        external_variant_id: variantId,
      };
      if (!String(next.external_product_id || '').trim() && metadata.productId) {
        next.external_product_id = metadata.productId;
      }

      const productId = String(next.external_product_id || '').trim();
      const nextLabel = String(item.__label || '').trim();
      if (
        isPlaceholderMealLabel(nextLabel, productId, variantId) &&
        metadata.productTitle
      ) {
        next.__label = metadata.productTitle;
      }
      if (!String(item.__imageUrl || '').trim() && metadata.imageUrl) {
        next.__imageUrl = metadata.imageUrl;
      }
      if (!String(item.__subtitle || '').trim() && metadata.variantTitle) {
        next.__subtitle = metadata.variantTitle;
      }
      if (
        !normalizeOptionValues(item.__optionValues || item.option_values || []).length &&
        metadata.optionValues.length
      ) {
        next.__optionValues = metadata.optionValues;
      }
      if (
        (!Number.isFinite(item.__retailPrice) || item.__retailPrice < 0) &&
        Number.isFinite(metadata.retailPrice) &&
        metadata.retailPrice >= 0
      ) {
        next.__retailPrice = metadata.retailPrice;
      }
      if (
        (!Number.isFinite(item.__unitPrice) || item.__unitPrice < 0) &&
        Number.isFinite(metadata.retailPrice) &&
        metadata.retailPrice >= 0
      ) {
        next.__unitPrice = metadata.retailPrice;
      }
      next.__isLockedFee = isFeeItem(next);

      return next;
    });
  }

  function normalizeOptionValues(values) {
    if (!Array.isArray(values)) return [];
    return values
      .map((value) => String(value || '').trim())
      .filter(Boolean);
  }

  function normalizeOptionSignature(values) {
    const normalized = normalizeOptionValues(values).map((value) =>
      value.toLowerCase()
    );
    if (!normalized.length) return null;
    return normalized.join('|');
  }

  function normalizeVariantTitle(value) {
    if (!value) return null;
    const normalized = String(value).trim().toLowerCase();
    return normalized || null;
  }

  // Produces a compact profile of the customer's existing variant preferences.
  // We later use this to pick the best hidden variant for addable products.
  function buildVariantProfile(items) {
    const profile = {
      preferredVariantTitles: [],
      preferredOptionSignatures: [],
      uniformVariantTitle: null,
    };

    if (!Array.isArray(items) || !items.length) {
      return profile;
    }

    const variantTitleCounts = new Map();
    const optionSignatureCounts = new Map();

    for (const item of items) {
      if (item?.__isLockedFee) continue;

      const normalizedTitle = normalizeVariantTitle(
        item?.__subtitle || item?.variant_title || null
      );
      if (normalizedTitle) {
        variantTitleCounts.set(
          normalizedTitle,
          (variantTitleCounts.get(normalizedTitle) || 0) + 1
        );
      }

      const optionSignature = normalizeOptionSignature(
        item?.__optionValues || item?.option_values || []
      );
      if (optionSignature) {
        optionSignatureCounts.set(
          optionSignature,
          (optionSignatureCounts.get(optionSignature) || 0) + 1
        );
      }
    }

    const sortedVariantTitleEntries = Array.from(variantTitleCounts.entries()).sort(
      (a, b) => b[1] - a[1]
    );
    profile.preferredVariantTitles = sortedVariantTitleEntries.map(
      ([title]) => title
    );
    if (sortedVariantTitleEntries.length === 1) {
      profile.uniformVariantTitle = sortedVariantTitleEntries[0][0];
    }

    profile.preferredOptionSignatures = Array.from(optionSignatureCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([signature]) => signature);

    return profile;
  }

  /**
   * Addable meal catalog pipeline
   * -----------------------------
   * This function builds the catalog users can add from.
   *
   * Pipeline:
   * - Create a paged source abstraction.
   * - Pull all pages incrementally.
   * - Overlay Recharge-specific discounted pricing per variant.
   * - Return a normalized meal list plus source metadata.
   */
  async function loadAddableMealsCatalog(variantProfile, options = {}) {
    const source = await createAddableMealsCatalogSource(variantProfile, options);
    const meals = await pullAllMealsFromCatalogSource(source);
    const mealsWithRechargePricing =
      await overlayAddableMealsWithRechargeDiscountPricing(
        source?.portalSession || null,
        meals,
        source?.rechargeDiscountPriceByVariantId instanceof Map
          ? source.rechargeDiscountPriceByVariantId
          : new Map()
      );

    return {
      source,
      meals: mealsWithRechargePricing,
    };
  }

  function clearPendingScrollAnchor() {
    state.pendingScrollAnchor = null;
  }

  // Captures the clicked card position so rerenders can keep it pinned in place.
  function buildPendingScrollAnchor(actionNode) {
    const body = getEditorBody();
    if (!(body instanceof HTMLElement)) return null;
    if (!(actionNode instanceof HTMLElement)) return null;

    const card = actionNode.closest('.et-update-meals-add-card[data-meal-key]');
    if (!(card instanceof HTMLElement)) return null;

    const mealKey = String(card.getAttribute('data-meal-key') || '').trim();
    if (!mealKey) return null;

    const bodyRect = body.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    return {
      mealKey,
      offsetWithinBody: cardRect.top - bodyRect.top,
      remainingApplies: CONFIG.scrollAnchorRenderCount,
    };
  }

  function applyPendingScrollAnchor(body) {
    if (!(body instanceof HTMLElement)) return false;

    const anchor = state.pendingScrollAnchor;
    if (!anchor || typeof anchor !== 'object') return false;

    let anchorCard = null;
    const cards = body.querySelectorAll('.et-update-meals-add-card[data-meal-key]');
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      if (String(card.getAttribute('data-meal-key') || '').trim() === anchor.mealKey) {
        anchorCard = card;
        break;
      }
    }

    const decrementRemaining = () => {
      anchor.remainingApplies = Math.max(
        0,
        parseInteger(anchor.remainingApplies ?? 0) - 1
      );
      if (anchor.remainingApplies <= 0) {
        clearPendingScrollAnchor();
      }
    };

    if (!(anchorCard instanceof HTMLElement)) {
      decrementRemaining();
      return false;
    }

    const bodyRect = body.getBoundingClientRect();
    const cardRect = anchorCard.getBoundingClientRect();
    const delta = cardRect.top - bodyRect.top - anchor.offsetWithinBody;
    if (Math.abs(delta) > 0.5) {
      body.scrollTop += delta;
    }

    decrementRemaining();
    return true;
  }

  /**
   * Catalog source factory.
   *
   * The returned object provides a `loadNextPage()` method so higher-level code
   * can consume pages without knowing Storefront query specifics.
   */
  async function createAddableMealsCatalogSource(variantProfile, options = {}) {
    return createStorefrontAddableMealsCatalogSource(variantProfile, options);
  }

  /**
   * Builds the addable meals from the Storefront catalog pages.
   *
   * Important behavior:
   * - If Recharge session is unavailable, catalog can still load but discount
   *   overlays may be limited.
   * - Collection ordering is pre-sorted so likely-relevant collections appear first.
   * - `seenMealKeys` prevents duplicates when products appear in multiple collections.
   * - `options.activeItems` are the bundle's items, which rank the collections
   *   and pin the variants the bundle already holds.
   */
  async function createStorefrontAddableMealsCatalogSource(
    variantProfile,
    options = {}
  ) {
    const storefrontConfig = resolveStorefrontConfig();
    if (!storefrontConfig.storefrontAccessToken) {
      throw new Error(
        'No Shopify Storefront token configured (set settings.storefront_api_access_token).'
      );
    }

    let portalSession = options?.portalSession || null;
    if (!portalSession) {
      try {
        portalSession = await getPortalSession();
      } catch (error) {
        console.warn(
          '[update-meals] Unable to initialize Recharge session for catalog discount pricing.',
          error
        );
      }
    }

    const storefrontCollections = await prefetchStorefrontCollectionsWithCache();
    if (!storefrontCollections.length) {
      return createArrayCatalogSource([]);
    }
    cacheStorefrontCollectionTitles(storefrontCollections);
    const pagesByHandle = await loadStorefrontCatalogPages(
      storefrontConfig,
      storefrontCollections
    );

    const activeItems = Array.isArray(options.activeItems) ? options.activeItems : [];
    const sortedCollections = sortStorefrontCollectionsForLoading(
      storefrontCollections,
      buildBundleCollectionStatsById(activeItems)
    );
    const activeBundleVariantByProductId = buildActiveBundleVariantByProductId(activeItems);
    const seenMealKeys = new Set();
    const items = [];
    // Every collection's first page in sorted order, then every second page,
    // and so on: the order the serial loop fetched them in, so each meal still
    // lands in the collection it always did.
    const pagesInOrder = sortedCollections.map(
      (collection) => pagesByHandle.get(String(collection?.handle || '').trim()) || []
    );
    const deepestPageCount = Math.max(0, ...pagesInOrder.map((pages) => pages.length));
    for (let pageIndex = 0; pageIndex < deepestPageCount; pageIndex += 1) {
      for (const pages of pagesInOrder) {
        const page = pages[pageIndex];
        if (!page) continue;

        const normalizedProducts = (Array.isArray(page.products) ? page.products : [])
          .map((product) => {
            const productId = String(
              extractShopifyResourceId(product?.id) || ''
            ).trim();
            return normalizeStorefrontProduct(product, {
              allowWithoutSellingPlans:
                Boolean(productId) &&
                activeBundleVariantByProductId.has(productId),
            });
          })
          .filter(Boolean);

        items.push(
          ...buildAddableMealsFromCollectionProducts({
            collectionId: page.collectionId,
            collectionName: page.collectionName,
            products: normalizedProducts,
            variantProfile,
            activeBundleVariantByProductId,
            seenMealKeys,
          })
        );
      }
    }

    return Object.assign(createArrayCatalogSource(items), {
      type: 'storefront',
      collections: sortedCollections,
      portalSession,
      rechargeDiscountPriceByVariantId: new Map(),
    });
  }

  function createArrayCatalogSource(items) {
    const catalogItems = Array.isArray(items) ? items : [];
    let emitted = false;

    return {
      type: 'array',
      hasMore: catalogItems.length > 0,
      async loadNextPage() {
        if (emitted) {
          this.hasMore = false;
          return { items: [], hasMore: false };
        }

        emitted = true;
        this.hasMore = false;
        return { items: catalogItems.slice(), hasMore: false };
      },
    };
  }

  async function pullAllMealsFromCatalogSource(source) {
    if (!source || typeof source.loadNextPage !== 'function') return [];

    let results = [];

    while (source.hasMore) {
      const page = await source.loadNextPage();
      if (!page || typeof page !== 'object') {
        source.hasMore = false;
        break;
      }

      if (Array.isArray(page.items) && page.items.length) {
        results = mergeAddableMealsCatalog(results, page.items);
      }

      source.hasMore = Boolean(page.hasMore);
    }

    return results;
  }

  function sortStorefrontCollectionsForLoading(
    collections,
    activeBundleCollectionStatsById
  ) {
    const storefrontCollections = Array.isArray(collections) ? collections : [];
    if (!storefrontCollections.length) return [];

    const storefrontOrderById = new Map();
    storefrontCollections.forEach((collection, index) => {
      const collectionId = String(
        extractShopifyResourceId(collection?.id) || ''
      ).trim();
      if (!collectionId || storefrontOrderById.has(collectionId)) return;
      storefrontOrderById.set(collectionId, index);
    });

    return storefrontCollections.slice().sort((a, b) => {
      const aCollectionId = String(
        extractShopifyResourceId(a?.id) || ''
      ).trim();
      const bCollectionId = String(
        extractShopifyResourceId(b?.id) || ''
      ).trim();

      const priorityComparison = compareCollectionDisplayPriority(
        aCollectionId || 'other',
        bCollectionId || 'other',
        activeBundleCollectionStatsById,
        storefrontOrderById
      );
      if (priorityComparison !== 0) return priorityComparison;

      return String(a?.title || '').localeCompare(String(b?.title || ''), undefined, {
        sensitivity: 'base',
      });
    });
  }

  function mergeAddableMealsCatalog(existingMeals, nextMeals) {
    const merged = [];
    const seenKeys = new Set();

    const pushUnique = (meal) => {
      if (!meal || typeof meal !== 'object') return;
      const mealKey = getCatalogMealKey(meal);
      if (!mealKey || seenKeys.has(mealKey)) return;
      seenKeys.add(mealKey);
      merged.push(meal);
    };

    for (const meal of existingMeals || []) pushUnique(meal);
    for (const meal of nextMeals || []) pushUnique(meal);

    return merged;
  }

  function resolveStorefrontConfig() {
    const globalConfig =
      window.etUpdateMealsConfig && typeof window.etUpdateMealsConfig === 'object'
        ? window.etUpdateMealsConfig
        : {};

    const storefrontAccessToken = String(
      globalConfig.storefrontAccessToken ||
        window.ET_SHOPIFY_STOREFRONT_ACCESS_TOKEN ||
        ''
    ).trim();

    const apiVersion = normalizeStorefrontApiVersion(
      globalConfig.storefrontApiVersion || CONFIG.storefrontApiVersion
    );

    const categoryInternalIdKey = String(
      globalConfig.storefrontCategoryInternalIdKey ||
        CONFIG.storefrontCategoryInternalIdKey
    ).trim();

    const categoryInternalIdNamespace = normalizeStorefrontNamespace(
      globalConfig.storefrontCategoryInternalIdNamespace ||
        CONFIG.storefrontCategoryInternalIdNamespace
    );

    return {
      storefrontAccessToken,
      apiVersion,
      categoryInternalIdKey:
        categoryInternalIdKey || CONFIG.storefrontCategoryInternalIdKey,
      categoryInternalIdNamespace,
    };
  }

  function normalizeStorefrontApiVersion(value) {
    const normalized = String(value || '').trim();
    if (/^\d{4}-\d{2}$/.test(normalized)) return normalized;
    return CONFIG.storefrontApiVersion;
  }

  function normalizeStorefrontNamespace(value) {
    if (Array.isArray(value)) {
      const fromArray = value
        .map((entry) => String(entry || '').trim())
        .find(Boolean);
      return fromArray || CONFIG.storefrontCategoryInternalIdNamespace;
    }

    const normalized = String(value || '').trim();
    if (normalized.includes(',')) {
      const first = normalized
        .split(',')
        .map((entry) => String(entry || '').trim())
        .find(Boolean);
      return first || CONFIG.storefrontCategoryInternalIdNamespace;
    }

    return normalized || CONFIG.storefrontCategoryInternalIdNamespace;
  }

  function buildCategoryInternalIdMetafieldIdentifiers(config) {
    const key = String(
      config?.categoryInternalIdKey || CONFIG.storefrontCategoryInternalIdKey
    ).trim();
    if (!key) return [];

    const namespace = normalizeStorefrontNamespace(
      config?.categoryInternalIdNamespace
    );
    if (!namespace) return [];

    return [
      {
        namespace,
        key,
      },
    ];
  }

  function hasCategoryInternalIdMetafield(node, config) {
    const key = String(
      config?.categoryInternalIdKey || CONFIG.storefrontCategoryInternalIdKey
    ).trim();
    if (!key) return false;

    const expectedNamespace = normalizeStorefrontNamespace(
      config?.categoryInternalIdNamespace
    );
    const metafields = Array.isArray(node?.metafields) ? node.metafields : [];

    return metafields.some((metafield) => {
      if (!metafield || typeof metafield !== 'object') return false;
      if (String(metafield?.key || '').trim() !== key) return false;
      const namespace = String(metafield?.namespace || '').trim();
      if (namespace !== expectedNamespace) return false;
      const value = String(metafield?.value || '').trim();
      return Boolean(value);
    });
  }

  /**
   * Thin Storefront GraphQL transport wrapper.
   *
   * Responsibilities:
   * - Build endpoint from API version.
   * - Attach required Storefront access token header.
   * - Normalize transport-level and GraphQL-level errors into thrown Error objects.
   */
  async function executeStorefrontGraphQLRequest(config, query, variables) {
    const endpoint = `/api/${encodeURIComponent(config.apiVersion)}/graphql.json`;
    let response = null;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
        },
        body: JSON.stringify({
          query,
          variables: variables || {},
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const apiError =
          payload?.errors?.[0]?.message ||
          payload?.error ||
          `HTTP ${response.status}`;
        throw new Error(`Storefront GraphQL request failed: ${apiError}`);
      }

      if (Array.isArray(payload?.errors) && payload.errors.length) {
        throw new Error(
          `Storefront GraphQL returned errors: ${payload.errors
            .map((entry) => entry?.message)
            .filter(Boolean)
            .join('; ')}`
        );
      }
      return payload?.data || null;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches all eligible storefront collections used as catalog sources.
   *
   * Eligibility rule:
   * - Collection must expose configured category_internal_id metafield.
   * This lets merchandising control which collections participate without
   * hard-coding handles in theme code.
   */
  async function fetchStorefrontCollections(config) {
    const identifiers = buildCategoryInternalIdMetafieldIdentifiers(config);
    if (!identifiers.length) {
      return [];
    }

    const query = `
      query UpdateMealsCollections(
        $first: Int!
        $after: String
        $metafieldIdentifiers: [HasMetafieldsIdentifier!]!
      ) {
        collections(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            handle
            title
            metafields(identifiers: $metafieldIdentifiers) {
              namespace
              key
              value
            }
          }
        }
      }
    `;

    const collections = [];
    let afterCursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await executeStorefrontGraphQLRequest(config, query, {
        first: CONFIG.storefrontCollectionsPageSize,
        after: afterCursor,
        metafieldIdentifiers: identifiers,
      });
      const connection = data?.collections;
      const nodes = Array.isArray(connection?.nodes) ? connection.nodes : [];
      for (const node of nodes) {
        if (!node || typeof node !== 'object') continue;
        if (!String(node.handle || '').trim()) continue;
        if (!hasCategoryInternalIdMetafield(node, config)) continue;
        collections.push(node);
      }
      hasNextPage = Boolean(connection?.pageInfo?.hasNextPage);
      afterCursor = connection?.pageInfo?.endCursor || null;
      if (!afterCursor) hasNextPage = false;
    }

    return collections;
  }

  // A fetched catalog serves every open for this long, so a portal left open
  // across a menu change still picks it up; before #363 each open re-read it.
  var STOREFRONT_CATALOG_PAGES_TTL_MS = 15 * 60 * 1000;
  // An open joins a catalog fetch still in flight for at most this long. Past
  // it, the request most likely stalled while the tab slept (iOS keeps them
  // pending for minutes), so the open asks again instead of waiting on it.
  var STOREFRONT_CATALOG_PAGES_JOIN_MS = 10000;

  // Every catalog collection's product pages, in cursor order, by the
  // collection's handle (see state.storefrontCatalogPages). `collections` is
  // the page view's cached collection list
  // (prefetchStorefrontCollectionsWithCache). A fetch that lost a batch is not
  // kept, so the next editor open asks again instead of showing the gap.
  function loadStorefrontCatalogPages(config, collections) {
    const cached = state.storefrontCatalogPages;
    const now = Date.now();
    if (
      cached &&
      (cached.settledAt === null
        ? now - cached.startedAt < STOREFRONT_CATALOG_PAGES_JOIN_MS
        : now - cached.settledAt < STOREFRONT_CATALOG_PAGES_TTL_MS)
    ) {
      return cached.promise;
    }
    const entry = { promise: null, startedAt: now, settledAt: null };
    const forget = () => {
      if (state.storefrontCatalogPages === entry) state.storefrontCatalogPages = null;
    };
    entry.promise = fetchStorefrontCatalogPages(config, collections).then(
      ({ pagesByHandle, complete }) => {
        entry.settledAt = Date.now();
        if (!complete) forget();
        return pagesByHandle;
      },
      (error) => {
        forget();
        throw error;
      }
    );
    state.storefrontCatalogPages = entry;
    return entry.promise;
  }

  // Fetches the collections' product pages in batches, up to
  // storefrontCollectionBatchConcurrency at once (#363). A collection's next
  // page is queued as its page arrives, so each collection's pages stay in
  // cursor order. A batch that fails is warned about and left out, as the
  // serial loop did.
  async function fetchStorefrontCatalogPages(config, collections) {
    const pagesByHandle = new Map();
    const queue = [];
    for (const collection of collections) {
      const handle = String(collection?.handle || '').trim();
      if (!handle || pagesByHandle.has(handle)) continue;
      const pages = [];
      pagesByHandle.set(handle, pages);
      queue.push({
        pages,
        request: {
          handle,
          afterCursor: null,
          collectionId:
            extractShopifyResourceId(collection?.id) ||
            normalizeCollectionId(handle) ||
            'other',
          collectionName: String(collection?.title || '').trim(),
        },
      });
    }

    let complete = true;
    // A worker that finds the queue empty stops; one whose batch queues next
    // pages is still looping, so it takes them.
    const worker = async () => {
      while (queue.length) {
        const batch = queue.splice(0, Math.max(1, CONFIG.storefrontCollectionBatchSize));
        let batchPages;
        try {
          batchPages = await fetchStorefrontCollectionProductsBatch(
            config,
            batch.map((entry) => entry.request)
          );
        } catch (error) {
          complete = false;
          console.warn(
            '[update-meals] Failed to fetch collection product batch from Storefront API.',
            error
          );
          continue;
        }
        batch.forEach(({ pages }, index) => {
          const page = batchPages[index];
          if (!page) return;
          pages.push(page);
          if (page.hasNextPage && page.endCursor) {
            queue.push({
              pages,
              request: {
                handle: page.handle,
                afterCursor: page.endCursor,
                collectionId: page.collectionId,
                collectionName: page.collectionName,
              },
            });
          }
        });
      }
    };
    await Promise.all(
      Array.from({ length: CONFIG.storefrontCollectionBatchConcurrency }, worker)
    );

    return { pagesByHandle, complete };
  }

  /**
   * Fetches one page of products for multiple collections in a single query.
   *
   * Why aliases:
   * - Shopify Storefront GraphQL does not support "array of handles" collection
   *   lookups in one field. Aliases let us batch N collection(handle: ...) reads
   *   into one network request to reduce round trips.
   */
  async function fetchStorefrontCollectionProductsBatch(config, requests) {
    const safeRequests = Array.isArray(requests)
      ? requests.filter(
          (entry) =>
            entry &&
            typeof entry === 'object' &&
            String(entry.handle || '').trim()
        )
      : [];
    if (!safeRequests.length) return [];

    const aliasEntries = safeRequests.map((entry, index) => {
      const alias = `c${index}`;
      const handle = String(entry.handle || '').trim();
      const afterCursor = String(entry.afterCursor || '').trim();
      return {
        alias,
        handle,
        afterLiteral: afterCursor ? JSON.stringify(afterCursor) : 'null',
        collectionId:
          String(entry.collectionId || '').trim() ||
          normalizeCollectionId(handle) ||
          'other',
        collectionName: String(entry.collectionName || '').trim(),
      };
    });

    const queryBody = aliasEntries
      .map(
        (entry) => `
          ${entry.alias}: collection(handle: ${JSON.stringify(entry.handle)}) {
            id
            title
            handle
            products(first: $first, after: ${entry.afterLiteral}) {
              pageInfo {
                hasNextPage
                endCursor
              }
              nodes {
                id
                title
                handle
                tags
                sellingPlanGroups(first: 1) {
                  nodes {
                    name
                  }
                }
                featuredImage {
                  url
                }
                variants(first: $variantsFirst) {
                  nodes {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        `
      )
      .join('\n');

    const query = `
      query UpdateMealsCollectionProductsBatch(
        $first: Int!
        $variantsFirst: Int!
      ) {
        ${queryBody}
      }
    `;

    const data = await executeStorefrontGraphQLRequest(config, query, {
      first: CONFIG.storefrontCollectionProductsPageSize,
      variantsFirst: CONFIG.storefrontProductVariantsPageSize,
    });

    const pages = aliasEntries.map((entry) => {
      const collectionNode = data?.[entry.alias];
      const productConnection = collectionNode?.products;
      const nodes = Array.isArray(productConnection?.nodes)
        ? productConnection.nodes
        : [];
      const hasNextPage = Boolean(productConnection?.pageInfo?.hasNextPage);
      const endCursor = productConnection?.pageInfo?.endCursor || null;

      return {
        handle: String(collectionNode?.handle || entry.handle || '').trim(),
        collectionId:
          extractShopifyResourceId(collectionNode?.id) || entry.collectionId,
        collectionName: String(
          collectionNode?.title || entry.collectionName || ''
        ).trim(),
        products: nodes,
        hasNextPage,
        endCursor,
      };
    });

    return pages;
  }

  /**
   * Bulk-hydrates product details for known product IDs.
   *
   * Used to enrich already-selected bundle lines with canonical Storefront data
   * (title/image/variant info) when Recharge payloads are sparse.
   *
   * Each product is requested once per page view (#361): product data does
   * not change inside one, and this GraphQL is outside the ReCharge read memo.
   * Only ids not yet requested go out; callers share an in-flight request,
   * and a failed one is forgotten so the next caller retries. The product
   * objects are shared between callers, so treat them as read-only.
   *
   * Keyed by id alone: the query and its page sizes are fixed here, and what
   * `config` adds -- the Storefront API version and access token -- is set
   * when the page renders, so it cannot change inside the page view this
   * cache lives for.
   */
  async function fetchStorefrontProductsByIds(config, productIds) {
    const safeProductIds = Array.from(
      new Set(
        (Array.isArray(productIds) ? productIds : [])
          .map((id) => String(id || '').trim())
          .filter(Boolean)
      )
    );
    if (!safeProductIds.length) return new Map();

    const cache = state.storefrontProductsById;
    const missingIds = safeProductIds.filter((id) => !cache.has(id));
    if (missingIds.length) {
      const request = requestStorefrontProductsByIds(config, missingIds);
      for (const id of missingIds) {
        const product = request.then((productsById) => productsById.get(id) || null);
        cache.set(id, product);
        product.catch(() => {
          if (cache.get(id) === product) cache.delete(id);
        });
      }
    }

    const products = await Promise.all(safeProductIds.map((id) => cache.get(id)));
    const productsById = new Map();
    products.forEach((product, index) => {
      if (product) productsById.set(safeProductIds[index], product);
    });
    return productsById;
  }

  async function requestStorefrontProductsByIds(config, safeProductIds) {
    const ids = safeProductIds.map((productId) => `gid://shopify/Product/${productId}`);
    const query = `
      query UpdateMealsProductsByIds(
        $ids: [ID!]!
        $variantsFirst: Int!
      ) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            tags
            featuredImage {
              url
            }
            variants(first: $variantsFirst) {
              nodes {
                id
                title
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;

    const data = await executeStorefrontGraphQLRequest(config, query, {
      ids,
      variantsFirst: CONFIG.storefrontProductVariantsPageSize,
    });

    const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
    const productsById = new Map();
    for (const node of nodes) {
      const normalized = normalizeStorefrontProduct(node, {
        allowWithoutSellingPlans: true,
      });
      if (!normalized) continue;
      productsById.set(String(normalized.id || '').trim(), normalized);
    }

    return productsById;
  }

  function extractShopifyResourceId(value) {
    const normalized = normalizeCollectionId(value);
    if (!normalized) return null;
    const gidMatch = normalized.match(/\/(\d+)(?:\?.*)?$/);
    if (gidMatch?.[1]) return gidMatch[1];
    return normalized;
  }

  function normalizeStorefrontProductVariant(variant) {
    if (!variant || typeof variant !== 'object') return null;

    const variantId = extractShopifyResourceId(variant.id);
    if (!variantId) return null;

    return {
      id: variantId,
      title: variant.title || null,
      options: normalizeOptionValues(
        Array.isArray(variant.selectedOptions)
          ? variant.selectedOptions.map((option) => option?.value)
          : []
      ),
      available: variant.availableForSale !== false,
      price: parseMoneyAmount(variant.price?.amount),
      compare_at_price: parseMoneyAmount(variant.compareAtPrice?.amount),
      featured_image: normalizeAssetUrl(variant.image?.url || null),
    };
  }

  function normalizeStorefrontProduct(product, options = {}) {
    if (!product || typeof product !== 'object') return null;

    const productId = extractShopifyResourceId(product.id);
    if (!productId) return null;

    const allowWithoutSellingPlans = Boolean(
      options && options.allowWithoutSellingPlans
    );

    const hasSellingPlans =
      Array.isArray(product.sellingPlanGroups?.nodes) &&
      product.sellingPlanGroups.nodes.length > 0;
    if (!hasSellingPlans && !allowWithoutSellingPlans) return null;

    const variants = Array.isArray(product.variants?.nodes)
      ? product.variants.nodes
          .map((variant) => normalizeStorefrontProductVariant(variant))
          .filter(Boolean)
      : [];
    if (!variants.length) return null;

    let tags = [];

    if (Array.isArray(product.tags)) {
      tags = product.tags;
    } else if (typeof product.tags === 'string') {
      tags = [product.tags];
    }
    return {
      id: productId,
      title: product.title || productId,
      handle: product.handle || null,
      available: product.availableForSale !== false,
      featured_image: normalizeAssetUrl(product.featuredImage?.url || null),
      images: normalizeCollectionProductImages(product.images?.nodes || []),
      variants,
      tags: tags.length ? tags : [],
    };
  }

  function normalizeCollectionProductImages(images) {
    if (!Array.isArray(images)) return [];
    return images
      .map((image) => {
        if (!image) return null;
        if (typeof image === 'string') return image;
        if (typeof image === 'object') {
          return (
            image.src ||
            image.url ||
            image.original ||
            image.large ||
            image.medium ||
            image.small ||
            null
          );
        }
        return null;
      })
      .filter(Boolean);
  }

  function normalizeCollectionId(value) {
    if (value === null || typeof value === 'undefined') return null;
    const normalized = String(value).trim();
    return normalized || null;
  }

  function normalizeExternalId(candidates) {
    for (const candidate of candidates) {
      const normalized = normalizeCollectionId(candidate);
      if (normalized) return normalized;
    }
    return null;
  }

  function buildActiveBundleVariantByProductId(items) {
    const variantByProductId = new Map();
    const activeItems = Array.isArray(items) ? items : [];

    for (const item of activeItems) {
      if (!item || item.__isLockedFee) continue;
      const productId = String(item.external_product_id || '').trim();
      const variantId = String(item.external_variant_id || '').trim();
      if (!productId || !variantId) continue;
      if (variantByProductId.has(productId)) continue;
      variantByProductId.set(productId, variantId);
    }

    return variantByProductId;
  }

  function buildAddableMealsFromCollectionProducts({
    collectionId,
    collectionName,
    products,
    variantProfile,
    activeBundleVariantByProductId,
    seenMealKeys,
  }) {
    const normalizedCollectionId =
      normalizeCollectionId(collectionId) || 'other';
    const normalizedCollectionName = String(collectionName || '').trim();

    // "All Items" duplicates the cross-collection "All" filter and creates
    // redundant meal entries, so we intentionally suppress it.
    if (normalizedCollectionName.toLowerCase() === 'all items') {
      return [];
    }

    const safeProducts = Array.isArray(products) ? products : [];
    const addableMeals = [];

    for (const product of safeProducts) {
      if (!product || typeof product !== 'object') continue;
      if (product.available === false) continue;

      const productId = String(product.id || '').trim();
      if (!productId) continue;

      const label = product.title || productId;
      if (
        isFeeItem({
          __label: label,
          title: product.title,
          name: product.handle,
          external_product_id: productId,
          product_id: productId,
        })
      ) {
        continue;
      }

      const preferredVariantId = String(
        activeBundleVariantByProductId?.get(productId) || ''
      ).trim();
      const preferredVariant =
        preferredVariantId && Array.isArray(product.variants)
          ? product.variants.find(
              (candidate) =>
                String(candidate?.id || '').trim() === preferredVariantId
            ) || null
          : null;
      const variant =
        preferredVariant ||
        resolveHiddenVariantForBundleProduct(product, variantProfile);
      if (!variant) continue;

      const variantId = String(variant.id || '').trim();
      if (!variantId) continue;

      const shopifyRetailPrice = resolveShopifyRetailPrice(variant);

      let productTags = [];

      if (Array.isArray(product.tags)) {
        productTags = product.tags;
      } else if (typeof product.tags === 'string') {
        productTags = [product.tags];
      }
      const addableMeal = {
        external_product_id: productId,
        external_variant_id: variantId,
        __collectionId: normalizedCollectionId,
        __collectionName:
          normalizedCollectionName ||
          (normalizedCollectionId === 'other' ? 'Other meals' : ''),
        __collectionSource: 'shopify',
        __label: label,
        __subtitle: variant.title || null,
        __optionValues: normalizeOptionValues(variant.options || []),
        __imageUrl: resolveBundleProductImage(product, variant),
        __retailPrice: shopifyRetailPrice,
        __unitPrice: shopifyRetailPrice,
        __tags: productTags.length ? productTags : undefined,
      };

      if (seenMealKeys) {
        const mealKey = getCatalogMealKey(addableMeal);
        if (mealKey) {
          if (seenMealKeys.has(mealKey)) continue;
          seenMealKeys.add(mealKey);
        }
      }

      addableMeals.push(addableMeal);
    }

    return addableMeals;
  }

  function resolveShopifyRetailPrice(variant) {
    if (!variant || typeof variant !== 'object') return null;

    const compareAtPrice = parseMoneyAmount(variant.compare_at_price);
    const directPrice = parseMoneyAmount(variant.price);

    if (
      Number.isFinite(compareAtPrice) &&
      compareAtPrice >= 0 &&
      (!Number.isFinite(directPrice) || compareAtPrice > directPrice)
    ) {
      return compareAtPrice;
    }

    if (Number.isFinite(directPrice) && directPrice >= 0) {
      return directPrice;
    }

    const fallbackPrice = resolveItemUnitPrice(variant);
    if (Number.isFinite(fallbackPrice) && fallbackPrice >= 0) {
      return fallbackPrice;
    }

    return null;
  }

  async function fetchRechargeBundleItemMetadataByVariantIds(session, variantIds) {
    const safeVariantIds = Array.from(
      new Set(
        (Array.isArray(variantIds) ? variantIds : [])
          .map((variantId) => normalizeExternalResourceId(variantId))
          .filter(Boolean)
      )
    );
    if (!session || !safeVariantIds.length) {
      return new Map();
    }

    const metadataByVariantId = new Map();
    const chunkSize = 200;
    for (let index = 0; index < safeVariantIds.length; index += chunkSize) {
      const chunk = safeVariantIds.slice(index, index + chunkSize);
      const response = await searchProductsByVariantIds(session, chunk);

      const products = Array.isArray(response?.products) ? response.products : [];
      for (const product of products) {
        const productId =
          normalizeExternalResourceId(
            product?.external_product_id || product?.id || null
          ) || '';
        const productTitle = String(product?.title || '').trim();
        const productImage =
          resolveBundleItemImageUrl(product) ||
          pickImageUrl(Array.isArray(product?.images) ? product.images[0] : null) ||
          null;
        const productRetailPrice = resolveItemRetailPrice(product);
        const variants = Array.isArray(product?.variants) ? product.variants : [];

        for (const variant of variants) {
          const variantId =
            normalizeExternalResourceId(
              variant?.external_variant_id || variant?.id || null
            ) || '';
          if (!variantId || !chunk.includes(variantId)) continue;

          const variantImage = resolveBundleItemImageUrl(variant) || productImage;
          const variantRetailPrice = resolveItemRetailPrice(variant);
          metadataByVariantId.set(variantId, {
            productId,
            productTitle,
            imageUrl: variantImage,
            variantTitle: String(variant?.title || '').trim(),
            optionValues: normalizeOptionValues(
              variant?.option_values || variant?.options || []
            ),
            retailPrice:
              Number.isFinite(variantRetailPrice) && variantRetailPrice >= 0
                ? variantRetailPrice
                : Number.isFinite(productRetailPrice) && productRetailPrice >= 0
                  ? productRetailPrice
                  : null,
          });
        }
      }
    }

    return metadataByVariantId;
  }

  async function overlayAddableMealsWithRechargeDiscountPricing(
    session,
    meals,
    discountPriceByVariantId
  ) {
    if (!session || !Array.isArray(meals) || !meals.length) {
      return Array.isArray(meals) ? meals : [];
    }

    const cache =
      discountPriceByVariantId instanceof Map
        ? discountPriceByVariantId
        : new Map();
    const discountMultiplier = resolveRechargeDiscountMultiplier();

    const variantIdsToLoad = Array.from(
      new Set(
        meals
          .map((meal) => String(meal?.external_variant_id || '').trim())
          .filter((variantId) => variantId && !cache.has(variantId))
      )
    );

    if (variantIdsToLoad.length) {
      let fetchedPriceByVariantId = new Map();
      try {
        fetchedPriceByVariantId = await fetchRechargeDiscountPriceByVariantId(
          session,
          variantIdsToLoad
        );
      } catch (error) {
        console.warn(
          '[update-meals] Unable to load Recharge discount pricing for addable meals.',
          error
        );
      }

      for (const variantId of variantIdsToLoad) {
        if (fetchedPriceByVariantId.has(variantId)) {
          cache.set(variantId, fetchedPriceByVariantId.get(variantId));
        } else {
          // Cache misses to avoid re-querying the same IDs every render page.
          cache.set(variantId, null);
        }
      }
    }

    return meals.map((meal) => {
      if (!meal || typeof meal !== 'object') return meal;
      const variantId = String(meal.external_variant_id || '').trim();
      if (!variantId) return meal;

      const retailPrice =
        Number.isFinite(meal.__retailPrice) && meal.__retailPrice >= 0
          ? meal.__retailPrice
          : Number.isFinite(meal.__unitPrice) && meal.__unitPrice >= 0
            ? meal.__unitPrice
            : null;

      let discountPrice = cache.get(variantId);
      if (!(Number.isFinite(discountPrice) && discountPrice >= 0)) {
        if (Number.isFinite(retailPrice) && retailPrice > 0) {
          discountPrice = roundCurrencyAmount(retailPrice * discountMultiplier);
        } else {
          discountPrice = null;
        }
      }

      if (
        Number.isFinite(discountPrice) &&
        Number.isFinite(retailPrice) &&
        discountPrice >= retailPrice
      ) {
        discountPrice = roundCurrencyAmount(retailPrice * discountMultiplier);
      }

      let normalizedRetailPrice = retailPrice;
      if (
        !(Number.isFinite(normalizedRetailPrice) && normalizedRetailPrice >= 0) &&
        Number.isFinite(discountPrice) &&
        discountPrice >= 0 &&
        discountMultiplier > 0 &&
        discountMultiplier < 1
      ) {
        normalizedRetailPrice = roundCurrencyAmount(discountPrice / discountMultiplier);
      }

      if (
        !(Number.isFinite(normalizedRetailPrice) && normalizedRetailPrice >= 0) &&
        !(Number.isFinite(discountPrice) && discountPrice >= 0)
      ) {
        return meal;
      }

      return {
        ...meal,
        __retailPrice:
          Number.isFinite(normalizedRetailPrice) && normalizedRetailPrice >= 0
            ? normalizedRetailPrice
            : null,
        __unitPrice:
          Number.isFinite(discountPrice) && discountPrice >= 0
            ? discountPrice
            : null,
      };
    });
  }

  async function fetchRechargeDiscountPriceByVariantId(session, variantIds) {
    const safeVariantIds = Array.from(
      new Set(
        (Array.isArray(variantIds) ? variantIds : [])
          .map((variantId) => String(variantId || '').trim())
          .filter(Boolean)
      )
    );
    if (!safeVariantIds.length) {
      return new Map();
    }

    const priceByVariantId = new Map();
    const chunkSize = 200;
    for (let index = 0; index < safeVariantIds.length; index += chunkSize) {
      const chunk = safeVariantIds.slice(index, index + chunkSize);
      const response = await searchProductsByVariantIds(session, chunk);

      const products = Array.isArray(response?.products) ? response.products : [];
      for (const product of products) {
        const productUnitPrice = resolveRechargeDiscountUnitPrice(product);
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        for (const variant of variants) {
          const variantId = String(variant?.external_variant_id || '').trim();
          if (!variantId) continue;

          const variantUnitPrice = resolveRechargeDiscountUnitPrice(variant);
          if (Number.isFinite(variantUnitPrice) && variantUnitPrice >= 0) {
            priceByVariantId.set(variantId, variantUnitPrice);
            continue;
          }
          if (Number.isFinite(productUnitPrice) && productUnitPrice >= 0) {
            priceByVariantId.set(variantId, productUnitPrice);
          }
        }
      }
    }

    return priceByVariantId;
  }

  function findVariantByPreferredTitles(variants, preferredTitles) {
    const safeVariants = Array.isArray(variants) ? variants : [];
    const safePreferredTitles = Array.isArray(preferredTitles)
      ? preferredTitles
      : [];

    for (const preferredTitle of safePreferredTitles) {
      if (!preferredTitle) continue;
      const matchingVariant = safeVariants.find(
        (variant) =>
          normalizeVariantTitle(variant?.title) ===
          normalizeVariantTitle(preferredTitle)
      );
      if (matchingVariant) return matchingVariant;
    }

    return null;
  }

  function findVariantByPreferredOptionSignatures(
    variants,
    preferredOptionSignatures
  ) {
    const safeVariants = Array.isArray(variants) ? variants : [];
    const safePreferredSignatures = Array.isArray(preferredOptionSignatures)
      ? preferredOptionSignatures
      : [];

    for (const preferredSignature of safePreferredSignatures) {
      if (!preferredSignature) continue;
      const matchingVariant = safeVariants.find(
        (variant) =>
          normalizeOptionSignature(variant?.options || []) === preferredSignature
      );
      if (matchingVariant) return matchingVariant;
    }

    return null;
  }

  // Chooses the most likely hidden variant to add:
  // 1) strict uniform variant-title match (when subscription has a single title)
  // 2) preferred variant-title match
  // 3) preferred option-signature match
  // 4) if no profile exists, fallback to first available variant
  // If a profile exists but no match is found, return null to keep filtering strict.
  function resolveHiddenVariantForBundleProduct(product, variantProfile) {
    const variants = Array.isArray(product?.variants)
      ? product.variants.filter(
          (variant) =>
            variant &&
            typeof variant === 'object' &&
            variant.available !== false
        )
      : [];

    if (!variants.length) return null;

    const uniformVariantTitle = normalizeVariantTitle(
      variantProfile?.uniformVariantTitle || null
    );
    const preferredTitles = Array.isArray(variantProfile?.preferredVariantTitles)
      ? variantProfile.preferredVariantTitles
      : [];

    if (uniformVariantTitle) {
      const uniformTitleMatch = findVariantByPreferredTitles(variants, [
        uniformVariantTitle,
      ]);
      if (uniformTitleMatch) return uniformTitleMatch;
      return null;
    }

    const titleMatch = findVariantByPreferredTitles(variants, preferredTitles);
    if (titleMatch) return titleMatch;

    const preferredOptionSignatures = Array.isArray(
      variantProfile?.preferredOptionSignatures
    )
      ? variantProfile.preferredOptionSignatures
      : [];
    const optionMatch = findVariantByPreferredOptionSignatures(
      variants,
      preferredOptionSignatures
    );
    if (optionMatch) return optionMatch;

    if (
      uniformVariantTitle ||
      preferredOptionSignatures.length ||
      preferredTitles.length
    ) {
      return null;
    }

    return variants[0];
  }

  function resolveBundleProductImage(product, variant) {
    const candidate =
      variant?.featured_image?.src ||
      variant?.featured_image ||
      product?.featured_image?.src ||
      product?.featured_image ||
      (Array.isArray(product?.images) ? product.images[0] : null);

    return normalizeAssetUrl(candidate);
  }

  // Requests a CDN rendition instead of the 2000px original. Every render site
  // goes through here, so ReCharge-sourced fallbacks are sized too.
  function sizeImageUrl(url, width) {
    const sharedSizer = window.etRechargeUtils?.sizeShopifyImageUrl;
    if (typeof sharedSizer === 'function') return sharedSizer(url, { width });
    // Rendering the raw URL still works -- it is just the 2000px original
    // again, which nothing else would surface.
    if (url) reportImageSizerUnavailable();
    return url;
  }

  // Carries the unsized URL so a failed rendition can fall back to it.
  function buildOriginalSrcAttribute(url, sizedUrl) {
    return url && sizedUrl !== url ? ` data-et-original-src="${escapeHtml(url)}"` : '';
  }

  // `src`/`srcset`/`width`/`height` for a 56px thumbnail, 1x and 2x.
  function buildThumbnailImageAttributes(url) {
    const src1x = sizeImageUrl(url, THUMBNAIL_IMAGE_WIDTH);
    const src2x = sizeImageUrl(url, THUMBNAIL_IMAGE_WIDTH * 2);
    const srcset = src2x !== src1x ? ` srcset="${escapeHtml(`${src1x} 1x, ${src2x} 2x`)}"` : '';
    return `src="${escapeHtml(src1x)}"${srcset}${buildOriginalSrcAttribute(url, src1x)} width="${THUMBNAIL_IMAGE_BOX}" height="${THUMBNAIL_IMAGE_BOX}"`;
  }

  function resolvePortalImageComponent(image) {
    if (image.closest('.et-update-meals-overview-slot')) return 'overview-header-slot';
    if (image.classList.contains('et-update-meals-sidebar-item__img')) return 'editor-sidebar';
    return 'editor-meal-card';
  }

  // A CDN rendition that fails to load swaps to the original once, so the
  // shopper still sees the meal, and reports it: a failed <img> is not a JS
  // error, so without this Sentry never hears about it.
  function handleSizedImageLoadError(event) {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) return;
    const originalSrc = image.getAttribute('data-et-original-src');
    if (!originalSrc) return;

    const failedSrc = image.currentSrc || image.src;
    // Removed before the retry so a failing original cannot loop.
    image.removeAttribute('data-et-original-src');
    image.removeAttribute('srcset');
    image.src = originalSrc;

    if (state.reportedSizedImageLoadFailure) return;
    state.reportedSizedImageLoadFailure = true;
    const observability = getEtObservability();
    if (!observability || typeof observability.captureException !== 'function') return;
    let failed = null;
    try {
      failed = new URL(failedSrc, window.location.href);
    } catch (_error) {
      // Report without URL details.
    }
    observability.captureException(new Error('Update meals sized image failed to load'), {
      level: 'warning',
      tags: {
        surface: 'affinity-update-meals',
        component: resolvePortalImageComponent(image),
        issue: 'sized-image-load-failed',
      },
      extra: {
        host: failed?.hostname || null,
        path: failed?.pathname || null,
        width: failed?.searchParams.get('width') || null,
      },
    });
  }

  function reportImageSizerUnavailable() {
    if (state.reportedImageSizerUnavailable) return;
    state.reportedImageSizerUnavailable = true;
    const observability = getEtObservability();
    if (!observability || typeof observability.captureException !== 'function') return;
    observability.captureException(new Error('Update meals image sizer unavailable'), {
      level: 'warning',
      tags: {
        surface: 'affinity-update-meals',
        component: 'image-sizing',
        issue: 'image-sizer-unavailable',
      },
      extra: {
        rechargeUtilsLoaded: Boolean(window.etRechargeUtils),
      },
    });
  }

  // Normalizes all supported media URL shapes to absolute URLs for reliable rendering.
  function normalizeAssetUrl(value) {
    if (!value) return null;
    if (typeof value === 'object') {
      const nested =
        value.src ||
        value.url ||
        value.original ||
        value.large ||
        value.medium ||
        value.small ||
        value.preview ||
        value.thumbnail ||
        null;
      if (!nested) return null;
      return normalizeAssetUrl(nested);
    }
    const raw = String(value).trim();
    if (!raw) return null;
    if (raw.startsWith('//')) {
      return `${window.location.protocol}${raw}`;
    }
    if (raw.startsWith('/')) {
      return `${window.location.origin}${raw}`;
    }
    return raw;
  }

  // Operational fee check must rely only on configured variant/product IDs.
  function isFeeItem(item, feeConfig = null) {
    if (!item || typeof item !== 'object') return false;

    const configuredFeeType = resolveConfiguredFeeType(item, feeConfig);
    return Boolean(configuredFeeType);
  }

  function pickImageUrl(image) {
    if (!image || typeof image !== 'object') return null;
    return image.large || image.medium || image.small || image.original || null;
  }

  function resolveItemRetailPrice(item) {
    if (!item || typeof item !== 'object') return null;

    const candidates = [
      item.__retailPrice,
      item.retail_price,
      item.compare_at_price,
      item.compareAtPrice,
      item.variant_compare_at_price,
      item?.pricing?.compare_at_price,
      item?.pricing?.compare_at_amount,
    ];

    for (const candidate of candidates) {
      const parsed = parseMoneyAmount(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    return null;
  }

  function resolveRechargeDiscountMultiplier() {
    const globalConfig =
      window.etUpdateMealsConfig && typeof window.etUpdateMealsConfig === 'object'
        ? window.etUpdateMealsConfig
        : {};

    const multiplierCandidate = parseFloat(
      String(
        globalConfig.rechargeDiscountMultiplier ||
          window.ET_RECHARGE_DISCOUNT_MULTIPLIER ||
          ''
      ).trim()
    );
    if (
      Number.isFinite(multiplierCandidate) &&
      multiplierCandidate > 0 &&
      multiplierCandidate < 1
    ) {
      return multiplierCandidate;
    }

    const discountPercentCandidate = parseFloat(
      String(
        globalConfig.rechargeDiscountPercent ||
          window.ET_RECHARGE_DISCOUNT_PERCENT ||
          ''
      ).trim()
    );
    if (
      Number.isFinite(discountPercentCandidate) &&
      discountPercentCandidate > 0 &&
      discountPercentCandidate < 100
    ) {
      return (100 - discountPercentCandidate) / 100;
    }

    return CONFIG.defaultRechargeDiscountMultiplier;
  }

  function roundCurrencyAmount(value) {
    const parsed = Number.parseFloat(String(value));
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100) / 100;
  }

  function resolveRechargeDiscountUnitPrice(item) {
    if (!item || typeof item !== 'object') return null;

    const firstPrice = Array.isArray(item.prices) ? item.prices[0] : null;
    const firstPlan = Array.isArray(firstPrice?.plans) ? firstPrice.plans[0] : null;
    const firstMinimumVariantPrice = Array.isArray(item.minimum_variant_prices)
      ? item.minimum_variant_prices[0]
      : null;

    const candidates = [
      item.discounted_price,
      firstPrice?.discounted_price,
      firstPlan?.discounted_price,
      firstPlan?.price,
      firstPrice?.unit_price,
      firstMinimumVariantPrice?.unit_price,
      firstMinimumVariantPrice?.price,
      item.unit_price,
      item.unitPrice,
      item.variant_price,
      item.price,
      item.price_amount,
      item.amount,
      item?.pricing?.price,
      item?.pricing?.amount,
    ];

    for (const candidate of candidates) {
      const parsed = parseMoneyAmount(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    return null;
  }

  // Tries multiple known price fields across Recharge + bundle-data payload variants.
  function resolveItemUnitPrice(item) {
    if (!item || typeof item !== 'object') return null;

    const firstPrice = Array.isArray(item.prices) ? item.prices[0] : null;
    const firstPlan = Array.isArray(firstPrice?.plans) ? firstPrice.plans[0] : null;
    const firstMinimumVariantPrice = Array.isArray(item.minimum_variant_prices)
      ? item.minimum_variant_prices[0]
      : null;

    const candidates = [
      item.price,
      item.unit_price,
      item.unitPrice,
      item.price_amount,
      item.amount,
      item.variant_price,
      firstPrice?.discounted_price,
      firstPrice?.unit_price,
      firstPlan?.discounted_price,
      firstPlan?.price,
      firstMinimumVariantPrice?.unit_price,
      firstMinimumVariantPrice?.price,
      item?.pricing?.price,
      item?.pricing?.amount,
    ];

    for (const candidate of candidates) {
      const parsed = parseMoneyAmount(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    return null;
  }

  // Parses money from number/string/object payloads with cents-aware heuristics.
  function parseMoneyAmount(value) {
    if (value === null || typeof value === 'undefined') return null;

    if (typeof value === 'object') {
      if ('amount' in value) return parseMoneyAmount(value.amount);
      if ('value' in value) return parseMoneyAmount(value.value);
      if ('price' in value) return parseMoneyAmount(value.price);
      return null;
    }

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return null;
      // In Recharge payloads, large integers often represent cents.
      if (Number.isInteger(value) && Math.abs(value) >= 250) {
        return value / 100;
      }
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().replace(/[$,\s]/g, '');
      if (!normalized) return null;
      const parsed = Number.parseFloat(normalized);
      if (!Number.isFinite(parsed)) return null;
      // If no decimal point exists and the number is large, treat it as cents.
      if (!normalized.includes('.') && Math.abs(parsed) >= 250) {
        return parsed / 100;
      }
      return parsed;
    }

    return null;
  }

  // Resolve currency from multiple payload sources, then fallback to USD.
  function resolveCurrencyCode(subscription, bundleSelection, items) {
    const candidates = [
      subscription?.currency,
      subscription?.currency_code,
      subscription?.presentment_currency,
      bundleSelection?.currency,
      bundleSelection?.currency_code,
      items?.[0]?.currency,
      items?.[0]?.currency_code,
    ];

    for (const candidate of candidates) {
      const normalized = normalizeCurrencyCode(candidate);
      if (normalized) return normalized;
    }

    return DEFAULT_CURRENCY_CODE;
  }

  function normalizeCurrencyCode(value) {
    if (!value) return null;
    const code = String(value).trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) return null;
    return code;
  }

  // Computes current editor total by summing quantity * unit price for each line.
  function calculateSubscriptionTotal(items) {
    if (!Array.isArray(items) || !items.length) return 0;
    return items.reduce((sum, item) => {
      const quantity = parseInteger(item.quantity ?? 0);
      const unitPrice = Number.isFinite(item.__unitPrice) ? item.__unitPrice : 0;
      return sum + quantity * Math.max(0, unitPrice);
    }, 0);
  }

  // Formatting wrapper with safe fallback when Intl currency formatting fails.
  function formatCurrencyValue(amount, currencyCode) {
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const safeCurrency =
      normalizeCurrencyCode(currencyCode) || DEFAULT_CURRENCY_CODE;
    try {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: safeCurrency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(safeAmount);
      return formatted.replace(/^US\$/, '$');
    } catch (error) {
      return `$${safeAmount.toFixed(2)}`;
    }
  }

  /**
   * Centralized close handler for every "exit editor" path.
   *
   * Cleanup checklist:
   * - cancel progressive render jobs and loading timers
   * - invalidate stale async request IDs
   * - clear transient editing-only caches
   * - restore overview summary snapshot
   *
   * Important: this does not clear `overviewSlot*` values because those belong to
   * summary mode and should still render after closing.
   */
  function closeEditor() {
    endEditorOpenSpan({ 'et.portal.outcome': 'closed' });
    state.editorOpen = false;
    cancelProgressiveHydrationJob();
    // Invalidate in-flight catalog requests so stale responses cannot rerender
    // a closed or newly reopened editor with outdated addable meal data.
    state.addMealsRequestId += 1;
    state.addableMealsSource = null;
    state.editorSelectedCategoryId = null;
    state.activeBundleInitialQuantityByMealKey = new Map();
    state.activeBundleInitialCollectionStatsById = new Map();
    state.activeFulfillmentType = null;
    state.activeFeeConfig = null;
    state.activeFeeTemplatesByType = createEmptyOperationalFeeTemplates();
    clearPendingScrollAnchor();
    stopLoadingDotsAnimation();
    if (state.overviewRefreshTimerId !== null) {
      window.clearTimeout(state.overviewRefreshTimerId);
      state.overviewRefreshTimerId = null;
    }

    renderOverviewHeaderSlot({
      isLoading: false,
      isEditing: false,
      subscriptionId: state.overviewSlotSubscriptionId,
      items: state.overviewSlotItems,
      currencyCode: state.overviewSlotCurrencyCode,
    });
  }

  function cancelProgressiveHydrationJob() {
    state.progressiveHydrationJobId += 1;
  }

  function stopLoadingDotsAnimation() {
    if (state.loadingDotsTimerId !== null) {
      window.clearInterval(state.loadingDotsTimerId);
      state.loadingDotsTimerId = null;
    }
  }

  function startLoadingDotsAnimation(statusNode, baseLabel = LOADING_MEALS_LABEL) {
    stopLoadingDotsAnimation();
    if (!(statusNode instanceof HTMLElement)) return;

    const label = String(baseLabel || LOADING_MEALS_LABEL).trim() || LOADING_MEALS_LABEL;
    const frames = ['', '.', '..', '...'];
    let frameIndex = 0;

    const renderFrame = () => {
      const suffix = frames[frameIndex % frames.length];
      statusNode.textContent = suffix ? `${label} ${suffix}` : label;
      frameIndex += 1;
    };

    // Start on 0 dots, then cycle.
    renderFrame();
    state.loadingDotsTimerId = window.setInterval(renderFrame, 300);
  }

  function buildMealsStatusMarkup(message, options = {}) {
    // Shared status markup factory keeps loading/error/empty states consistent.
    const classes = ['et-update-meals-add__status'];
    if (options.loading) classes.push('et-update-meals-add__status--loading');
    if (options.error) classes.push('et-update-meals-add__status--error');

    const dataRole = options.dataRole ? String(options.dataRole).trim() : '';
    const dataRoleAttribute = dataRole
      ? ` data-role="${escapeHtml(dataRole)}"`
      : '';

    return `<p class="${classes.join(' ')}"${dataRoleAttribute}>${escapeHtml(
      message
    )}</p>`;
  }

  function getDeliveryAndPackagingFeeAmounts(items) {
    const safe = Array.isArray(items) ? items : [];
    let deliveryFee = 0;
    let packagingFee = 0;
    const feeConfig = state.activeFeeConfig || null;
    for (const item of safe) {
      if (!item || typeof item !== 'object') continue;
      const feeType = resolveConfiguredFeeType(item, feeConfig);
      const qty = parseInteger(item.quantity ?? 0);
      const unitPrice = Number.isFinite(item.__unitPrice) ? item.__unitPrice : 0;
      const amount = qty * Math.max(0, unitPrice);
      if (feeType === 'delivery') deliveryFee += amount;
      else if (feeType === 'packaging') packagingFee += amount;
    }
    return { deliveryFee, packagingFee };
  }

  function buildSidebarMealRow(item, currencyCode) {
    const mealKey = getCatalogMealKey(item);
    if (!mealKey) return '';
    const label = resolveBundleItemDisplayLabel(item);
    const qty = Math.max(0, parseInteger(item.quantity ?? 0));
    const price = Number.isFinite(item.__unitPrice) ? item.__unitPrice : 0;
    const priceText = formatCurrencyValue(price, currencyCode);
    const imgUrl = resolveBundleItemImageUrl(item);
    const thumb = imgUrl
      ? `<img ${buildThumbnailImageAttributes(imgUrl)} alt="" class="et-update-meals-sidebar-item__img" loading="lazy" decoding="async" />`
      : '<span class="et-update-meals-sidebar-item__img et-update-meals-sidebar-item__img--placeholder"></span>';
    return `
      <div class="et-update-meals-sidebar-item" data-meal-key="${escapeHtml(mealKey)}">
        <div class="et-update-meals-sidebar-item__media">${thumb}</div>
        <div class="et-update-meals-sidebar-item__meta">
          <p class="et-update-meals-sidebar-item__name">${escapeHtml(label)}</p>
          <p class="et-update-meals-sidebar-item__price">${escapeHtml(priceText)}</p>
          <div class="et-update-meals-sidebar-item__qty">
            <button type="button" class="et-update-meals-sidebar-item__qty-btn" data-action="${escapeHtml(EDITOR_ACTIONS.adjustMealQuantity)}" data-meal-key="${escapeHtml(mealKey)}" data-delta="-1" aria-label="Decrease quantity">−</button>
            <span class="et-update-meals-sidebar-item__qty-value">${qty}</span>
            <button type="button" class="et-update-meals-sidebar-item__qty-btn" data-action="${escapeHtml(EDITOR_ACTIONS.adjustMealQuantity)}" data-meal-key="${escapeHtml(mealKey)}" data-delta="1" aria-label="Increase quantity">+</button>
          </div>
        </div>
      </div>
    `;
  }

  function buildSidebarMarkup(editableItems, currencyCode) {
    const items = Array.isArray(editableItems) ? editableItems : [];
    const initialByKey = state.activeBundleInitialQuantityByMealKey instanceof Map
      ? state.activeBundleInitialQuantityByMealKey
      : new Map();
    const currentItems = [];
    const newItems = [];
    for (const item of items) {
      if (!item || typeof item !== 'object' || item.__isLockedFee) continue;
      const mealKey = getCatalogMealKey(item);
      if (!mealKey) continue;
      const qty = Math.max(0, parseInteger(item.quantity ?? 0));
      if (qty <= 0) continue;
      const initialQty = Math.max(0, parseInteger(initialByKey.get(mealKey) ?? 0));
      if (initialQty > 0) currentItems.push(item);
      else newItems.push(item);
    }
    const currentRows = currentItems.map((item) => buildSidebarMealRow(item, currencyCode)).join('');
    const newRows = newItems.map((item) => buildSidebarMealRow(item, currencyCode)).join('');
    const { deliveryFee, packagingFee } = getDeliveryAndPackagingFeeAmounts(items);
    const mealsSubtotal = items
      .filter((item) => item && !item.__isLockedFee)
      .reduce((sum, item) => {
        const qty = parseInteger(item.quantity ?? 0);
        const unit = Number.isFinite(item.__unitPrice) ? item.__unitPrice : 0;
        return sum + qty * Math.max(0, unit);
      }, 0);
    const total = mealsSubtotal + deliveryFee + packagingFee;
    const deliveryText = formatCurrencyValue(deliveryFee, currencyCode);
    const packagingText = formatCurrencyValue(packagingFee, currencyCode);
    const totalText = formatCurrencyValue(total, currencyCode);
    return `
      <div class="et-update-meals-sidebar">
        <div class="et-update-meals-sidebar__scroll">
          <h3 class="et-update-meals-sidebar__title">Current Meals in Subscription</h3>
          <div class="et-update-meals-sidebar__list" data-role="sidebar-current">${currentRows || '<p class="et-update-meals-sidebar__empty">No current meals</p>'}</div>
          <h3 class="et-update-meals-sidebar__title">New meals to your Subscription</h3>
          <div class="et-update-meals-sidebar__list" data-role="sidebar-new">${newRows || '<p class="et-update-meals-sidebar__empty">No new meals added</p>'}</div>
        </div>
        <div class="et-update-meals-sidebar__totals">
          <div class="et-update-meals-sidebar__row"><span>Delivery Fee:</span><span>${escapeHtml(deliveryText)}</span></div>
          <div class="et-update-meals-sidebar__row"><span>Service Fee:</span><span>${escapeHtml(packagingText)}</span></div>
          <div class="et-update-meals-sidebar__row et-update-meals-sidebar__row--total"><span>Total:</span><span data-role="sidebar-total">${escapeHtml(totalText)}</span></div>
        </div>
        <button type="button" class="et-update-meals-sidebar__submit" data-action="${escapeHtml(EDITOR_ACTIONS.save)}">${escapeHtml(UPDATE_BUTTON_DEFAULT_LABEL)}</button>
      </div>
    `;
  }

  function buildMenuCategoriesMarkup(context) {
    const groups = Array.isArray(context?.groups) ? context.groups : [];
    const selectedId = state.editorSelectedCategoryId == null || String(state.editorSelectedCategoryId).trim() === ''
      ? 'all'
      : String(state.editorSelectedCategoryId).trim();
    const allActive = selectedId === 'all';
    const allChip = `<button type="button" class="et-update-meals-add__filter et-update-meals-add__filter--category${allActive ? ' is-active' : ''}" data-action="filter-category" data-collection-id="all" aria-pressed="${allActive}">All</button>`;
    const groupChips = groups
      .map((g) => {
        const id = String(g?.id || '').trim() || 'other';
        const title = String(g?.title || 'Meals').trim();
        const isActive = selectedId === id;
        return `<button type="button" class="et-update-meals-add__filter et-update-meals-add__filter--category${isActive ? ' is-active' : ''}" data-action="filter-category" data-collection-id="${escapeHtml(id)}" aria-pressed="${isActive}">${escapeHtml(title)}</button>`;
      })
      .join('');
    return `
      <div class="et-update-meals-add__categories">
        <h4 class="et-update-meals-add__heading">Menu Categories</h4>
        <div class="et-update-meals-add__filters">${allChip}${groupChips}</div>
      </div>
    `;
  } 

  function buildMealsResultsSectionMarkup(contentMarkup, options) {
    const includeAffinityLayout = options && options.affinityLayout === true;
    const sidebarMarkup = includeAffinityLayout && Array.isArray(state.activeBundleItems)
      ? buildSidebarMarkup(state.activeBundleItems, state.activeCurrencyCode || 'USD')
      : '';
    const categoriesContext = includeAffinityLayout && state.editorFullDisplayContextForCategories
      ? state.editorFullDisplayContextForCategories
      : options?.context || { groups: [] };
    const categoriesMarkup = includeAffinityLayout ? buildMenuCategoriesMarkup(categoriesContext) : '';
    const mainContent = `
      <div class="et-update-meals-editor__main">
        ${categoriesMarkup}
        <section class="et-update-meals-add">
          <div class="et-update-meals-add__results">${contentMarkup}</div>
        </section>
      </div>
    `;
    if (!includeAffinityLayout || !sidebarMarkup) {
      return `
        <section class="et-update-meals-add">
          <div class="et-update-meals-add__results">${contentMarkup}</div>
        </section>
      `;
    }
    return `
      <div class="et-update-meals-editor__layout">
        ${mainContent}
        <aside class="et-update-meals-editor__sidebar">${sidebarMarkup}</aside>
      </div>
    `;
  }

  function renderMealsStatusSection(body, message, options = {}) {
    if (!(body instanceof HTMLElement)) return null;
    const useAffinityLayout = Boolean(options.affinityLayout);
    const layoutOptions = useAffinityLayout ? { affinityLayout: true, context: options.context || { groups: [] } } : {};
    body.innerHTML = buildMealsResultsSectionMarkup(
      buildMealsStatusMarkup(message, options),
      layoutOptions
    );

    if (options.loading) {
      const statusNode = body.querySelector('.et-update-meals-add__status--loading');
      startLoadingDotsAnimation(statusNode, message);
      return statusNode;
    }

    return null;
  }

  // Renders a compact loading placeholder while async data is fetching.
  function setEditorLoadingState(message) {
    const body = getEditorBody();
    if (!body) return;
    const label =
      String(message || LOADING_MEALS_LABEL).trim() || LOADING_MEALS_LABEL;
    const context =
      state.editorFullDisplayContextForCategories || { groups: [] };
    renderMealsStatusSection(body, label, {
      loading: true,
      affinityLayout: true,
      context,
    });
    const footer = getEditorFooter();
    if (footer) footer.innerHTML = '';
  }

  // Renders recoverable error state in the editor body.
  function renderEditorError(message) {
    const body = getEditorBody();
    if (!body) return;
    body.innerHTML = `
      <div class="et-update-meals-error">
        <p>${escapeHtml(message)}</p>
        <button type="button" class="et-update-meals-retry">Close</button>
      </div>
    `;
    body.querySelector('.et-update-meals-retry')?.addEventListener('click', closeEditor);
    const footer = getEditorFooter();
    if (footer) footer.innerHTML = '';
  }

  function getCollectionLabel(meal) {
    const name = String(meal?.__collectionName || '').trim();
    if (name) return name;
    const id = String(meal?.__collectionId || meal?.collection_id || '').trim();
    if (!id || id === 'other') return 'Other meals';
    const shouldUseFallbackLabel =
      Date.now() >= (state.collectionTitleFallbackAtMs || 0) &&
      state.addableMealsLoading === false;
    if (shouldUseFallbackLabel) {
      return 'Meals';
    }
    return '';
  }

  function buildStorefrontCollectionNamesById() {
    const namesById =
      state.storefrontCollectionNamesById instanceof Map
        ? new Map(state.storefrontCollectionNamesById)
        : new Map();
    const collections = Array.isArray(state.addableMealsSource?.collections)
      ? state.addableMealsSource.collections
      : [];

    for (const collection of collections) {
      const collectionId = String(
        extractShopifyResourceId(collection?.id) || ''
      ).trim();
      const collectionName = String(collection?.title || '').trim();
      if (!collectionId || !collectionName || namesById.has(collectionId)) continue;
      namesById.set(collectionId, collectionName);
    }

    return namesById;
  }

  function buildStorefrontCollectionOrderById() {
    const orderById = new Map();
    const sourceCollections = Array.isArray(state.addableMealsSource?.collections)
      ? state.addableMealsSource.collections
      : [];
    const collections = sourceCollections.length
      ? sourceCollections
      : Array.isArray(state.storefrontCollectionsCache)
        ? state.storefrontCollectionsCache
        : [];

    collections.forEach((collection, index) => {
      const collectionId = String(
        extractShopifyResourceId(collection?.id) || ''
      ).trim();
      if (!collectionId || orderById.has(collectionId)) return;
      orderById.set(collectionId, index);
    });

    return orderById;
  }

  function buildBundleCollectionStatsById(items) {
    const statsById = new Map();
    const safeItems = Array.isArray(items) ? items : [];

    for (const item of safeItems) {
      if (!item || item.__isLockedFee) continue;

      const collectionId = String(item.__collectionId || item.collection_id || '').trim();
      if (!collectionId) continue;

      const quantity = Math.max(
        0,
        parseInteger(item.quantity ?? 0)
      );
      if (quantity <= 0) continue;

      const existing = statsById.get(collectionId) || {
        selectedLineCount: 0,
        totalQuantity: 0,
      };
      existing.selectedLineCount += 1;
      existing.totalQuantity += quantity;
      statsById.set(collectionId, existing);
    }

    return statsById;
  }

  function compareCollectionDisplayPriority(
    aCollectionId,
    bCollectionId,
    activeBundleCollectionStatsById,
    storefrontCollectionOrderById,
    collectionMealCountById = null
  ) {
    const aStats =
      activeBundleCollectionStatsById.get(aCollectionId) || null;
    const bStats =
      activeBundleCollectionStatsById.get(bCollectionId) || null;
    const aSelectedLineCount = aStats?.selectedLineCount || 0;
    const bSelectedLineCount = bStats?.selectedLineCount || 0;
    if (aSelectedLineCount !== bSelectedLineCount) {
      return bSelectedLineCount - aSelectedLineCount;
    }

    const aTotalQuantity = aStats?.totalQuantity || 0;
    const bTotalQuantity = bStats?.totalQuantity || 0;
    if (aTotalQuantity !== bTotalQuantity) {
      return bTotalQuantity - aTotalQuantity;
    }

    const hasCollectionMealCounts = collectionMealCountById instanceof Map;
    const aCollectionMealCount = hasCollectionMealCounts
      ? parseInteger(collectionMealCountById.get(aCollectionId) ?? 0)
      : 0;
    const bCollectionMealCount = hasCollectionMealCounts
      ? parseInteger(collectionMealCountById.get(bCollectionId) ?? 0)
      : 0;
    if (aCollectionMealCount !== bCollectionMealCount) {
      return bCollectionMealCount - aCollectionMealCount;
    }

    const aCollectionOrder = storefrontCollectionOrderById.has(aCollectionId)
      ? storefrontCollectionOrderById.get(aCollectionId)
      : Number.MAX_SAFE_INTEGER;
    const bCollectionOrder = storefrontCollectionOrderById.has(bCollectionId)
      ? storefrontCollectionOrderById.get(bCollectionId)
      : Number.MAX_SAFE_INTEGER;
    if (aCollectionOrder !== bCollectionOrder) {
      return aCollectionOrder - bCollectionOrder;
    }

    return 0;
  }

  function buildInitialBundleQuantityByMealKey(items) {
    const quantityByMealKey = new Map();
    const safeItems = Array.isArray(items) ? items : [];

    for (const item of safeItems) {
      if (!item || item.__isLockedFee) continue;
      const mealKey = getCatalogMealKey(item);
      if (!mealKey) continue;

      const quantity = Math.max(
        0,
        parseInteger(item.quantity ?? 0)
      );
      if (quantity <= 0) continue;

      quantityByMealKey.set(
        mealKey,
        (quantityByMealKey.get(mealKey) || 0) + quantity
      );
    }

    return quantityByMealKey;
  }

  function getInitialBundleQuantityForMeal(meal) {
    const mealKey = getCatalogMealKey(meal);
    if (!mealKey) return 0;
    const quantityByMealKey = state.activeBundleInitialQuantityByMealKey;
    if (!(quantityByMealKey instanceof Map)) return 0;
    return Math.max(
      0,
      parseInteger(quantityByMealKey.get(mealKey) ?? 0)
    );
  }

  function buildCollectionMealCountById(meals) {
    const countsById = new Map();
    const safeMeals = Array.isArray(meals) ? meals : [];

    for (const meal of safeMeals) {
      if (!meal || typeof meal !== 'object') continue;
      const collectionId = String(
        meal.__collectionId || meal.collection_id || 'other'
      ).trim() || 'other';
      countsById.set(collectionId, (countsById.get(collectionId) || 0) + 1);
    }

    return countsById;
  }

  function getCatalogMealKey(meal) {
    if (!meal || typeof meal !== 'object') return '';
    const collectionId = String(meal.__collectionId || meal.collection_id || 'other').trim() || 'other';
    const productId = String(meal.external_product_id || '').trim();
    const variantId = String(meal.external_variant_id || '').trim();
    if (!productId || !variantId) return '';
    return `${collectionId}::${productId}::${variantId}`;
  }

  function isPlaceholderMealLabel(label, productId, variantId) {
    const normalizedLabel = String(label || '').trim();
    const normalizedProductId = String(productId || '').trim();
    const normalizedVariantId = String(variantId || '').trim();
    if (!normalizedLabel) return true;

    const lowered = normalizedLabel.toLowerCase();
    return (
      lowered === 'meal' ||
      lowered === 'meal item' ||
      normalizedLabel === normalizedProductId ||
      normalizedLabel === normalizedVariantId
    );
  }

  /**
   * Merges active bundle rows with addable catalog rows into one render context.
   *
   * Why this exists:
   * - Selected meals must remain visible even when upstream catalog calls omit them.
   * - Catalog meals must be visible with quantity=0 for add interactions.
   * - Collection grouping/sorting needs both data sets at once.
   *
   * Output contract:
   * - `meals`: flat sorted list used for card rendering/actions.
   * - `groups`: collection buckets used for sectioned UI rendering.
   */
  function getUnifiedMealsForDisplayContext() {
    const collectionNamesById = new Map();
    const storefrontCollectionNamesById = buildStorefrontCollectionNamesById();
    const storefrontCollectionOrderById = buildStorefrontCollectionOrderById();
    const activeBundleCollectionStatsById =
      state.activeBundleInitialCollectionStatsById;
    for (const meal of state.addableMealsCatalog) {
      const collectionId = String(meal?.__collectionId || '').trim();
      if (!collectionId || collectionNamesById.has(collectionId)) continue;
      collectionNamesById.set(collectionId, getCollectionLabel(meal));
    }

    const mealsByKey = new Map();
    const mergeMeal = (rawMeal) => {
      if (!rawMeal || typeof rawMeal !== 'object') return;

      const productId = String(rawMeal.external_product_id || '').trim();
      const variantId = String(rawMeal.external_variant_id || '').trim();
      const collectionId = String(rawMeal.__collectionId || rawMeal.collection_id || 'other').trim() || 'other';
      if (!productId || !variantId) return;

      const quantity = Math.max(
        0,
        parseInteger(rawMeal.quantity ?? 0)
      );
      const unitPriceCandidate = Number.isFinite(rawMeal.__unitPrice)
        ? rawMeal.__unitPrice
        : resolveItemUnitPrice(rawMeal);
      const retailPriceCandidate = Number.isFinite(rawMeal.__retailPrice)
        ? rawMeal.__retailPrice
        : resolveItemRetailPrice(rawMeal);

      const normalizedMeal = {
        external_product_id: productId,
        external_variant_id: variantId,
        __collectionId: collectionId,
        __collectionName: String(
          rawMeal.__collectionName ||
            storefrontCollectionNamesById.get(collectionId) ||
            collectionNamesById.get(collectionId) ||
            (collectionId === 'other' ? 'Other meals' : '')
        ).trim(),
        __collectionSource: String(
          rawMeal.__collectionSource || rawMeal.collection_source || 'shopify'
        ).trim(),
        __label:
          rawMeal.__label ||
          rawMeal.product_title ||
          rawMeal.title ||
          rawMeal.name ||
          productId,
        __subtitle: rawMeal.__subtitle || rawMeal.variant_title || null,
        __optionValues: normalizeOptionValues(
          rawMeal.__optionValues || rawMeal.option_values || []
        ),
        __imageUrl: resolveBundleItemImageUrl(rawMeal),
        __retailPrice:
          Number.isFinite(retailPriceCandidate) && retailPriceCandidate >= 0
            ? retailPriceCandidate
            : null,
        __unitPrice:
          Number.isFinite(unitPriceCandidate) && unitPriceCandidate >= 0
            ? unitPriceCandidate
            : null,
        quantity,
        __isLockedFee: false,
      };

      const key = getCatalogMealKey(normalizedMeal);
      if (!key) return;

      const existing = mealsByKey.get(key);
      if (!existing) {
        mealsByKey.set(key, normalizedMeal);
        return;
      }

      existing.quantity = Math.max(existing.quantity, normalizedMeal.quantity);
      const existingLabel = String(existing.__label || '').trim();
      const normalizedLabel = String(normalizedMeal.__label || '').trim();
      const existingProductId = String(existing.external_product_id || '').trim();
      const existingVariantId = String(existing.external_variant_id || '').trim();
      const shouldUpgradeLabel =
        normalizedLabel &&
        normalizedLabel !== existingLabel &&
        isPlaceholderMealLabel(
          existingLabel,
          existingProductId,
          existingVariantId
        );
      if (shouldUpgradeLabel) {
        existing.__label = normalizedLabel;
      }
      if (!existing.__imageUrl && normalizedMeal.__imageUrl) {
        existing.__imageUrl = normalizedMeal.__imageUrl;
      }
      if (
        (!Number.isFinite(existing.__unitPrice) || existing.__unitPrice < 0) &&
        Number.isFinite(normalizedMeal.__unitPrice)
      ) {
        existing.__unitPrice = normalizedMeal.__unitPrice;
      }
      if (
        (!Number.isFinite(existing.__retailPrice) || existing.__retailPrice < 0) &&
        Number.isFinite(normalizedMeal.__retailPrice)
      ) {
        existing.__retailPrice = normalizedMeal.__retailPrice;
      }
      if (!existing.__subtitle && normalizedMeal.__subtitle) {
        existing.__subtitle = normalizedMeal.__subtitle;
      }
    };

    // Keep currently selected meals visible even if they are missing from catalog responses.
    for (const item of state.activeBundleItems) {
      if (!item || item.__isLockedFee) continue;
      mergeMeal(item);
    }
    for (const meal of state.addableMealsCatalog) {
      mergeMeal({ ...meal, quantity: 0 });
    }

    const unifiedMeals = Array.from(mealsByKey.values());
    const collectionMealCountById =
      buildCollectionMealCountById(unifiedMeals);

    const meals = unifiedMeals
      .sort((a, b) => {
        const aCollectionId = String(a.__collectionId || 'other').trim() || 'other';
        const bCollectionId = String(b.__collectionId || 'other').trim() || 'other';
        const collectionPriorityComparison = compareCollectionDisplayPriority(
          aCollectionId,
          bCollectionId,
          activeBundleCollectionStatsById,
          storefrontCollectionOrderById,
          collectionMealCountById
        );
        if (collectionPriorityComparison !== 0) {
          return collectionPriorityComparison;
        }

        if (aCollectionId === bCollectionId) {
          const aQuantity = getInitialBundleQuantityForMeal(a);
          const bQuantity = getInitialBundleQuantityForMeal(b);
          if (aQuantity !== bQuantity) {
            return bQuantity - aQuantity;
          }
        }

        return (
          String(a.__collectionName || '').localeCompare(
            String(b.__collectionName || ''),
            undefined,
            { sensitivity: 'base' }
          ) ||
          String(a.__label || '').localeCompare(
            String(b.__label || ''),
            undefined,
            { sensitivity: 'base' }
          )
        );
      });

    const groupedMeals = new Map();
    for (const meal of meals) {
      const collectionId = String(meal.__collectionId || 'other').trim() || 'other';
      if (!groupedMeals.has(collectionId)) {
        groupedMeals.set(collectionId, {
          id: collectionId,
          title: getCollectionLabel(meal),
          meals: [],
        });
      }
      groupedMeals.get(collectionId).meals.push(meal);
    }

    const groups = Array.from(groupedMeals.values()).sort((a, b) => {
      const collectionPriorityComparison = compareCollectionDisplayPriority(
        a.id,
        b.id,
        activeBundleCollectionStatsById,
        storefrontCollectionOrderById,
        collectionMealCountById
      );
      if (collectionPriorityComparison !== 0) {
        return collectionPriorityComparison;
      }

      return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
        sensitivity: 'base',
      });
    });

    return { meals, groups };
  }

  
  function getFilteredDisplayContext() {
    const full = getUnifiedMealsForDisplayContext();
    let meals = full.meals;
    let groups = full.groups;

    const categoryId = state.editorSelectedCategoryId;
    if (categoryId && String(categoryId).trim() !== '' && String(categoryId).trim() !== 'all') {
      const id = String(categoryId).trim();
      meals = meals.filter((m) => (String(m.__collectionId || 'other').trim() || 'other') === id);
      groups = groups.filter((g) => String(g.id || '').trim() === id);
    }

    return { meals, groups };
  }

  function buildMealPriceMarkup(meal, currencyCode) {
    let discountedPrice =
      Number.isFinite(meal?.__unitPrice) && meal.__unitPrice >= 0
        ? meal.__unitPrice
        : null;
    let retailPrice =
      Number.isFinite(meal?.__retailPrice) && meal.__retailPrice >= 0
        ? meal.__retailPrice
        : null;
    const discountMultiplier = resolveRechargeDiscountMultiplier();

    if (
      !(Number.isFinite(retailPrice) && retailPrice >= 0) &&
      Number.isFinite(discountedPrice) &&
      discountedPrice >= 0 &&
      discountMultiplier > 0 &&
      discountMultiplier < 1
    ) {
      retailPrice = roundCurrencyAmount(discountedPrice / discountMultiplier);
    }

    if (
      !(Number.isFinite(discountedPrice) && discountedPrice >= 0) &&
      Number.isFinite(retailPrice) &&
      retailPrice >= 0
    ) {
      discountedPrice = roundCurrencyAmount(retailPrice * discountMultiplier);
    }

    if (
      Number.isFinite(discountedPrice) &&
      Number.isFinite(retailPrice) &&
      discountedPrice >= retailPrice
    ) {
      discountedPrice = roundCurrencyAmount(retailPrice * discountMultiplier);
    }

    if (discountedPrice === null && retailPrice === null) {
      return '<p class="et-update-meals-add-card__price et-update-meals-add-card__price--unavailable">Price unavailable</p>';
    }

    const hasDiscount =
      discountedPrice !== null &&
      retailPrice !== null &&
      retailPrice > discountedPrice;

    const retailText = formatCurrencyValue(
      Number.isFinite(retailPrice) ? retailPrice : 0,
      currencyCode
    );
    const discountedText = formatCurrencyValue(
      Number.isFinite(discountedPrice) ? discountedPrice : 0,
      currencyCode
    );

    const retailValueClass = hasDiscount
      ? 'et-update-meals-add-card__price-retail'
      : 'et-update-meals-add-card__price-secondary';
    const discountedValueClass =
      discountedPrice !== null
        ? 'et-update-meals-add-card__price-discount'
        : 'et-update-meals-add-card__price-value--unavailable';

    return `
      <p class="et-update-meals-add-card__price">
        <span class="${retailValueClass}">${escapeHtml(retailText)}</span>
        <span class="${discountedValueClass}">${escapeHtml(discountedText)}</span>
      </p>
    `;
  }

  function buildCollectionGroupTitleMarkup(group) {
    const groupTitle = String(group?.title || '').trim();
    if (groupTitle) {
      return `<h4 class="et-update-meals-add-group__title">${escapeHtml(
        groupTitle
      )}</h4>`;
    }

    return '<h4 class="et-update-meals-add-group__title et-update-meals-add-group__title--loading" aria-hidden="true"><span class="et-update-meals-add-group__title-skeleton"></span></h4>';
  }

  function buildMealCardMarkup(meal, currencyCode) {
    const mealKey = getCatalogMealKey(meal);
    const displayLabel = resolveBundleItemDisplayLabel(meal);
    const quantity = Math.max(
      0,
      parseInteger(meal.quantity ?? 0)
    );
    const cardImageUrl = meal.__imageUrl
      ? sizeImageUrl(meal.__imageUrl, MEAL_CARD_IMAGE_WIDTH)
      : null;
    const thumbnail = cardImageUrl
      ? `<img src="${escapeHtml(cardImageUrl)}"${buildOriginalSrcAttribute(
          meal.__imageUrl,
          cardImageUrl
        )} alt="${escapeHtml(displayLabel)}" width="${MEAL_CARD_IMAGE_WIDTH}" height="${MEAL_CARD_IMAGE_WIDTH}" loading="lazy" decoding="async" />`
      : '<div class="et-update-meals-item__image-placeholder">Meal</div>';
    const priceMarkup = buildMealPriceMarkup(meal, currencyCode);

    return `
      <li class="et-update-meals-add-card" data-meal-key="${escapeHtml(mealKey)}">
        <div class="et-update-meals-add-card__media">${thumbnail}</div>
        <div class="et-update-meals-add-card__meta">
          <p class="et-update-meals-add-card__name">${escapeHtml(displayLabel)}</p>
          ${priceMarkup}
        </div>
        <div class="et-update-meals-add-card__qty">
          <button
            type="button"
            class="et-update-meals-add-card__qty-btn"
            data-action="${escapeHtml(EDITOR_ACTIONS.adjustMealQuantity)}"
            data-delta="-1"
            data-meal-key="${escapeHtml(mealKey)}"
            aria-label="Decrease ${escapeHtml(displayLabel)} quantity"
            ${quantity <= 0 ? 'disabled aria-disabled="true"' : ''}
          >
            −
          </button>
          <span class="et-update-meals-add-card__qty-value">${quantity}</span>
          <button
            type="button"
            class="et-update-meals-add-card__qty-btn"
            data-action="${escapeHtml(EDITOR_ACTIONS.adjustMealQuantity)}"
            data-delta="1"
            data-meal-key="${escapeHtml(mealKey)}"
            aria-label="Increase ${escapeHtml(displayLabel)} quantity"
          >
            +
          </button>
        </div>
      </li>
    `;
  }

  function buildGroupedMealsMarkup(context, currencyCode) {
    const groupedMarkup = context.groups
      .map((group) => {
        const cards = group.meals
          .map((meal) => buildMealCardMarkup(meal, currencyCode))
          .join('');

        return `
          <section class="et-update-meals-add-group">
            ${buildCollectionGroupTitleMarkup(group)}
            <ul class="et-update-meals-add-list">${cards}</ul>
          </section>
        `;
      })
      .join('');

    return groupedMarkup;
  }

  function buildGroupedMealsSkeletonMarkup(context) {
    const groupedMarkup = context.groups
      .map((group) => {
        const groupId = String(group?.id || 'other').trim() || 'other';
        const slots = group.meals
          .map((meal, index) => {
            const slotKey = `${groupId}::${index}`;
            const primarySkeletonWidthClass =
              index % 2 === 0
                ? 'et-update-meals-add-card__skeleton-block--name-primary-wide'
                : 'et-update-meals-add-card__skeleton-block--name-primary-narrow';

            return `
              <li class="et-update-meals-add-card et-update-meals-add-card--skeleton" data-slot-key="${escapeHtml(
                slotKey
              )}" aria-hidden="true">
                <div class="et-update-meals-add-card__media">
                  <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--media"></span>
                </div>
                <div class="et-update-meals-add-card__meta">
                  <p class="et-update-meals-add-card__name et-update-meals-add-card__name--skeleton">
                    <span class="et-update-meals-add-card__skeleton-block ${primarySkeletonWidthClass}"></span>
                    <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--name-secondary"></span>
                  </p>
                  <p class="et-update-meals-add-card__price et-update-meals-add-card__price--skeleton">
                    <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--price-secondary"></span>
                    <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--price-primary"></span>
                  </p>
                </div>
                <div class="et-update-meals-add-card__qty et-update-meals-add-card__qty--skeleton">
                  <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--qty-btn"></span>
                  <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--qty-value"></span>
                  <span class="et-update-meals-add-card__skeleton-block et-update-meals-add-card__skeleton-block--qty-btn"></span>
                </div>
              </li>
            `;
          })
          .join('');

        return `
          <section class="et-update-meals-add-group">
            ${buildCollectionGroupTitleMarkup(group)}
            <ul class="et-update-meals-add-list">${slots}</ul>
          </section>
        `;
      })
      .join('');

    return groupedMarkup;
  }

  function createElementFromMarkup(markup) {
    const template = document.createElement('template');
    template.innerHTML = String(markup || '').trim();
    return template.content.firstElementChild || null;
  }

  /**
   * Progressive hydration renderer for meal cards.
   *
   * Strategy:
   * - Render lightweight skeleton cards immediately for perceived responsiveness.
   * - Replace skeletons in small batches to keep main thread responsive.
   * - Use `progressiveHydrationJobId` to cancel stale jobs on close/rerender.
   */
  function renderGroupedMealsWithProgressiveHydration(body, context, currencyCode) {
    const totalCards = Array.isArray(context?.meals) ? context.meals.length : 0;
    if (!totalCards) {
      renderMealsStatusSection(body, NO_MEALS_AVAILABLE_MESSAGE);
      return;
    }

    body.innerHTML = buildMealsResultsSectionMarkup(
      `${buildMealsStatusMarkup(LOADING_MEALS_LABEL, {
        loading: true,
        dataRole: 'meals-loading-progress',
      })}${buildGroupedMealsSkeletonMarkup(context)}`,
      { affinityLayout: true, context }
    );

    const loadingStatusNode = body.querySelector(
      '[data-role="meals-loading-progress"]'
    );
    startLoadingDotsAnimation(loadingStatusNode, LOADING_MEALS_LABEL);

    const slotNodes = body.querySelectorAll(
      '.et-update-meals-add-card--skeleton[data-slot-key]'
    );
    const slotNodeByKey = new Map();
    slotNodes.forEach((slotNode) => {
      if (!(slotNode instanceof HTMLElement)) return;
      const slotKey = String(slotNode.getAttribute('data-slot-key') || '').trim();
      if (!slotKey) return;
      slotNodeByKey.set(slotKey, slotNode);
    });

    const queue = [];
    context.groups.forEach((group) => {
      const groupId = String(group?.id || 'other').trim() || 'other';
      const meals = Array.isArray(group?.meals) ? group.meals : [];
      meals.forEach((meal, index) => {
        queue.push({
          slotKey: `${groupId}::${index}`,
          meal,
        });
      });
    });

    let cursor = 0;
    let nextBatchTimerId = null;

    const clearProgressiveTimers = () => {
      if (nextBatchTimerId !== null) {
        window.clearTimeout(nextBatchTimerId);
        nextBatchTimerId = null;
      }
    };

    const hydrationJobId = ++state.progressiveHydrationJobId;
    const isHydrationCancelled = () =>
      hydrationJobId !== state.progressiveHydrationJobId || !state.editorOpen;
    const stopHydration = (removeLoadingMessage = false) => {
      // Consolidated cleanup avoids timer leaks and duplicated branch logic.
      clearProgressiveTimers();
      stopLoadingDotsAnimation();
      if (removeLoadingMessage) loadingStatusNode?.remove();
    };

    const finalizeHydration = () => {
      if (isHydrationCancelled()) {
        stopHydration();
        return;
      }
      stopHydration(true);
      applyPendingScrollAnchor(body);
    };

    const applyBatch = (batchSize) => {
      if (isHydrationCancelled()) {
        stopHydration();
        return;
      }

      const safeBatchSize = Math.max(1, Number(batchSize) || 1);
      const batchStart = cursor;
      const batchEnd = Math.min(totalCards, cursor + safeBatchSize);
      for (; cursor < batchEnd; cursor += 1) {
        const entry = queue[cursor];
        if (!entry) continue;
        const slotNode = slotNodeByKey.get(entry.slotKey);
        if (!(slotNode instanceof HTMLElement) || !slotNode.isConnected) continue;

        const nextCard = createElementFromMarkup(
          buildMealCardMarkup(entry.meal, currencyCode)
        );
        if (!(nextCard instanceof HTMLElement)) continue;
        nextCard.classList.add('et-update-meals-add-card--hydrate-enter');
        slotNode.replaceWith(nextCard);
        const indexInBatch = cursor - batchStart;
        const enterDelayMs = Math.min(120, indexInBatch * 16);
        window.requestAnimationFrame(() => {
          window.setTimeout(() => {
            if (!nextCard.isConnected) return;
            nextCard.classList.add('et-update-meals-add-card--hydrate-active');
            nextCard.classList.remove('et-update-meals-add-card--hydrate-enter');
            window.setTimeout(() => {
              nextCard.classList.remove('et-update-meals-add-card--hydrate-active');
            }, 320);
          }, enterDelayMs);
        });
      }

      // As soon as the first cards are visible, remove the loading message.
      if (cursor > 0 && loadingStatusNode?.isConnected) {
        stopLoadingDotsAnimation();
        loadingStatusNode.remove();
      }

      if (cursor < totalCards) {
        nextBatchTimerId = window.setTimeout(() => {
          window.requestAnimationFrame(() => {
            applyBatch(CONFIG.progressiveHydrationBatchSize);
          });
        }, Math.max(0, Number(CONFIG.progressiveHydrationBatchDelayMs) || 0));
        return;
      }

      finalizeHydration();
    };

    applyBatch(CONFIG.progressiveHydrationInitialBatchSize);
  }

  /**
   * Renders the editor body with one of four states:
   * - error state
   * - loading state
   * - empty state
   * - grouped meals state (progressive or immediate)
   */
  function renderEditorBody(body, context, currencyCode, shouldHydrateProgressively) {
    if (state.addableMealsError) {
      renderMealsStatusSection(body, state.addableMealsError, { error: true });
      return;
    }

    if (!context.meals.length) {
      if (state.addableMealsLoading) {
        renderMealsStatusSection(body, LOADING_MEALS_LABEL, { loading: true });
      } else {
        renderMealsStatusSection(body, NO_MEALS_AVAILABLE_MESSAGE);
      }
      return;
    }

    if (shouldHydrateProgressively) {
      renderGroupedMealsWithProgressiveHydration(body, context, currencyCode);
      return;
    }

    body.innerHTML = buildMealsResultsSectionMarkup(
      buildGroupedMealsMarkup(context, currencyCode),
      { affinityLayout: true, context }
    );
  }

  function renderEditorFooterMarkup(formattedTotal) {
    return `
      <div class="et-update-meals-actions et-update-meals-actions--review">
        <p class="et-update-meals-actions__total">
          <span class="et-update-meals-actions__total-label">Total:</span>
          <span data-role="footer-subscription-total">${escapeHtml(formattedTotal)}</span>
        </p>
        <div class="et-update-meals-actions__buttons">
          <button type="button" class="et-update-meals-actions__secondary" data-action="${escapeHtml(
            EDITOR_ACTIONS.cancel
          )}">Cancel</button>
          <button type="button" class="et-update-meals-actions__primary" data-action="${escapeHtml(
            EDITOR_ACTIONS.save
          )}">${escapeHtml(UPDATE_BUTTON_DEFAULT_LABEL)}</button>
        </div>
      </div>
    `;
  }

  function bindDelegatedEditorActions(container) {
    if (!(container instanceof HTMLElement)) return;
    if (container.dataset.etUpdateMealsActionsBound === 'true') return;
    container.addEventListener('click', onEditorActionClick);
    container.dataset.etUpdateMealsActionsBound = 'true';
  }

  function renderEditorFooter(formattedTotal) {
    const footer = getEditorFooter();
    if (!(footer instanceof HTMLElement)) return null;
    footer.innerHTML = renderEditorFooterMarkup(formattedTotal);
    return footer;
  }

  /**
   * Top-level editor renderer.
   *
   * Render contract:
   * - Reads all UI inputs from `state`.
   * - Rerenders body + footer each time quantities or loading states change.
   * - Preserves scroll position when rerendering already-mounted content.
   * - Re-binds delegated handlers idempotently.
   */
  function renderEditorForm(
    editableItems,
    currencyCode,
    options = {}
  ) {
    const body = getEditorBody();
    if (!body) return;
    const previousScrollTop = body.scrollTop;
    const hadPriorContent = body.childElementCount > 0;
    const shouldHydrateProgressively = Boolean(options?.progressiveHydration);
    cancelProgressiveHydrationJob();
    stopLoadingDotsAnimation();

    const formattedTotal = formatCurrencyValue(
      calculateSubscriptionTotal(editableItems),
      currencyCode
    );
    state.editorFullDisplayContextForCategories = getUnifiedMealsForDisplayContext();
    const context = getFilteredDisplayContext();
    renderEditorBody(body, context, currencyCode, shouldHydrateProgressively);
    const footer = renderEditorFooter(formattedTotal);
    bindDelegatedEditorActions(body);
    bindDelegatedEditorActions(footer);

    if (hadPriorContent) {
      body.scrollTop = previousScrollTop;
    }
    if (!shouldHydrateProgressively) {
      applyPendingScrollAnchor(body);
    }
  }

  // Maps an addable meal card into the bundle-selection item shape used by save API.
  function createBundleItemFromCatalogMeal(meal) {
    const collectionId = String(meal.__collectionId || '').trim();
    const collectionSource = String(meal.__collectionSource || 'shopify').trim();
    if (!collectionId) {
      return null;
    }

    const nextItem = {
      quantity: 1,
      external_product_id: String(meal.external_product_id || '').trim(),
      external_variant_id: String(meal.external_variant_id || '').trim(),
      collection_id: collectionId,
      collection_source: collectionSource || 'shopify',
      __label: meal.__label || 'Meal item',
      __subtitle: meal.__subtitle || null,
      __optionValues: normalizeOptionValues(meal.__optionValues || []),
      __imageUrl: resolveBundleItemImageUrl(meal),
      __retailPrice:
        Number.isFinite(meal.__retailPrice) && meal.__retailPrice >= 0
          ? meal.__retailPrice
          : null,
      __unitPrice:
        Number.isFinite(meal.__unitPrice) && meal.__unitPrice >= 0
          ? meal.__unitPrice
          : null,
      __isLockedFee: false,
    };
    if (Number.isFinite(meal.__unitPrice) && meal.__unitPrice >= 0) {
      nextItem.price = meal.__unitPrice.toFixed(2);
    }

    return nextItem;
  }

  // Adjusts quantity directly from grouped meal cards.
  function adjustMealQuantityByKey(mealKey, delta) {
    const normalizedMealKey = String(mealKey || '').trim();
    const parsedDelta = parseInteger(delta || 0);
    if (!normalizedMealKey || !Number.isFinite(parsedDelta) || parsedDelta === 0) {
      return false;
    }

    const [, , variantIdRaw] = normalizedMealKey.split('::');
    const variantId = String(variantIdRaw || '').trim();
    if (!variantId) return false;

    const existingIndex = state.activeBundleItems.findIndex(
      (item) =>
        !item?.__isLockedFee &&
        String(item.external_variant_id || '').trim() === variantId
    );

    if (existingIndex >= 0) {
      const currentQty = parseInteger(
        state.activeBundleItems[existingIndex].quantity ?? 0
      );
      state.activeBundleItems[existingIndex].quantity = Math.max(
        0,
        currentQty + parsedDelta
      );
      return true;
    }

    // Nothing to decrement when item is not yet in active bundle rows.
    if (parsedDelta < 0) return false;

    const context = getUnifiedMealsForDisplayContext();
    const catalogMeal = context.meals.find(
      (meal) => getCatalogMealKey(meal) === normalizedMealKey
    );
    if (!catalogMeal) return false;

    const nextItem = createBundleItemFromCatalogMeal(catalogMeal);
    if (
      !nextItem ||
      !nextItem.external_product_id ||
      !nextItem.external_variant_id ||
      !nextItem.collection_id
    ) {
      return false;
    }

    nextItem.quantity = parsedDelta;

    const firstLockedFeeIndex = state.activeBundleItems.findIndex(
      (item) => item.__isLockedFee
    );
    if (firstLockedFeeIndex >= 0) {
      state.activeBundleItems.splice(firstLockedFeeIndex, 0, nextItem);
    } else {
      state.activeBundleItems.push(nextItem);
    }

    return true;
  }

  /**
   * Delegated action dispatcher for editor controls.
   *
   * Uses `data-action` attributes instead of direct handlers so rerendered DOM
   * does not require re-attaching listeners to every button node.
   */
  function onEditorActionClick(event) {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;
    const actionNode = target.closest('[data-action]');
    if (!(actionNode instanceof HTMLElement)) return;

    const action = actionNode.getAttribute('data-action');
    if (!action) return;

    switch (action) {
      case EDITOR_ACTIONS.cancel:
        closeEditor();
        return;
      case EDITOR_ACTIONS.save:
        submitBundleSelectionUpdate();
        return;
      case EDITOR_ACTIONS.adjustMealQuantity:
        handleAdjustMealQuantityAction(actionNode);
        return;
      case EDITOR_ACTIONS.filterCategory:
        handleFilterCategoryAction(actionNode);
        return;
      default:
        return;
    }
  }

  function handleFilterCategoryAction(button) {
    if (!(button instanceof HTMLElement)) return;  
    const id = button.dataset.collectionId?.trim();  

    state.editorSelectedCategoryId = (id && id !== 'all') ? id : null;

    renderActiveEditor();
  }

  /**
   * Handles +/- quantity interactions from card controls.
   *
   * After quantity changes, we:
   * - re-apply operational fee rules
   * - recompute derived state (currency, ordering snapshots)
   * - preserve visual scroll anchor to avoid jumpy UX
   * - rerender editor
   */
  function handleAdjustMealQuantityAction(actionNode) {
    const mealKey = String(actionNode.getAttribute('data-meal-key') || '').trim();
    const delta = parseInteger(actionNode.getAttribute('data-delta') || '0');
    if (!mealKey || !Number.isFinite(delta)) return;

    const pendingScrollAnchor = buildPendingScrollAnchor(actionNode);
    const wasAdjusted = adjustMealQuantityByKey(mealKey, delta);
    if (!wasAdjusted) return;

    applyActiveOperationalFeeRules();
    syncActiveBundleComputedState();
    if (pendingScrollAnchor) {
      state.pendingScrollAnchor = pendingScrollAnchor;
    }
    renderActiveEditor();
  }

  /**
   * Persists current editor state to Recharge.
   *
   * Save sequence:
   * 1) Lock save button and prevent duplicate submits.
   * 2) Apply fee rules and recompute derived state.
   * 3) Build sanitized payload and submit update.
   * 4) Invalidate subscription cache and refresh Affinity overview.
   * 5) Restore button state in finally block.
   */
  async function submitBundleSelectionUpdate() {
    if (state.submitting) return;
    if (!state.activeBundleSelectionId) return;

    const hasAnyMeals = Array.isArray(state.activeBundleItems)
      ? state.activeBundleItems.some((item) => {
          if (!item || typeof item !== 'object') return false;
          if (item.__isLockedFee) return false;
          const qty = Math.max(0, parseInteger(item.quantity ?? 0));
          return qty > 0;
        })
      : false;
    if (!hasAnyMeals) {
      showNoMealsDialog();
      return;
    }

    const observability = getEtObservability();

    function isLikelyTransientError(error) {
      if (!error || typeof error !== 'object') return true;
      const status =
        Number(error.status || error.statusCode || error.code) ||
        Number(error?.response?.status);

      if (!Number.isFinite(status)) {
        return true;
      }
      if (
        status === 409 ||
        status === 429 ||
        (status >= 500 && status <= 599)
      ) {
        return true;
      }
      return false;
    }

    // A dead customer-portal session token means the save -- and every later
    // save -- keeps reusing it, leaving the customer stuck on "try again" until
    // a full reload (SHOPIFY-11S). Classification and refresh are owned by the
    // shared util: the local copies this replaces matched only 401/403, so they
    // missed the 422 "Invalid Token" Recharge actually sends, and their refresh
    // left the shared cache empty so the next getPortalSession short-circuited
    // straight back to the dead token (SHOPIFY-3HH).
    function isAuthError(error) {
      const sharedIsAuthError = window.etRechargeUtils?.isPortalAuthError;
      if (typeof sharedIsAuthError === 'function') return sharedIsAuthError(error);

      if (!error || typeof error !== 'object') return false;
      const status =
        Number(error.status || error.statusCode || error.code) ||
        Number(error?.response?.status);
      return status === 401 || status === 403;
    }

    async function refreshPortalSession() {
      // This asset keeps its own session cache alongside the shared one, so it
      // has to be evicted here either way.
      state.sessionPromise = null;

      const sharedRefresh = window.etRechargeUtils?.refreshPortalSession;
      if (typeof sharedRefresh === 'function') return sharedRefresh();
      return loginCustomerPortal();
    }

    async function updateBundleSelectionWithRetry(initialSession, selectionId, payload, maxAttempts) {
      let session = initialSession;
      let lastError = null;
      let reauthAttempted = false;

      for (let attempt = 1; attempt <= maxAttempts; ) {
        try {
          return await updateBundleSelection(session, selectionId, payload);
        } catch (error) {
          lastError = error;

          // Re-establish the session once on the first auth failure and retry
          // immediately, without consuming the transient-retry budget.
          if (isAuthError(error) && !reauthAttempted) {
            reauthAttempted = true;
            try {
              session = await refreshPortalSession();
              continue;
            } catch (reauthError) {
              lastError = reauthError;
              // Re-login failed: the portal credential itself is gone. Fall
              // through and report the original auth error as fatal.
            }
          }

          const shouldRetry = isLikelyTransientError(error) && attempt < maxAttempts;
          if (!shouldRetry) {
            if (
              observability &&
              typeof observability.captureException === 'function'
            ) {
              observability.captureException(error, {
                level: 'error',
                tags: {
                  surface: 'affinity-update-meals',
                  component: 'bundle-selection-update',
                  operation: 'submitBundleSelectionUpdate',
                },
                extra: {
                  attempt,
                  maxAttempts,
                  reauthAttempted,
                  subscriptionId: state.activeSubscriptionId || null,
                  activeBundleSelectionId: state.activeBundleSelectionId || null,
                  hasAnyMeals,
                  payload,
                },
              });
              error.__etBundleUpdateLogged = true;
            }
            throw error;
          }

          const backoffMs = 250 * attempt;
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          attempt++;
        }
      }

      throw lastError || new Error('Unknown bundle selection update error');
    }

    let payloadForLogging = null;
    try {
      state.submitting = true;
      setSaveButtonState(true, SAVE_BUTTON_LOADING_LABEL);
      const session = await getPortalSession();
      applyActiveOperationalFeeRules();
      syncActiveBundleComputedState();
      const payload = buildBundleSelectionUpdatePayload(
        state.activeBundleItems,
        state.activeBundleSelection
      );
      payloadForLogging = payload;

      const response = await updateBundleSelectionWithRetry(
        session,
        state.activeBundleSelectionId,
        payload,
        BUNDLE_SELECTION_UPDATE_MAX_ATTEMPTS
      );

      showInlineSuccessToast('Subscription updated successfully.');
      closeEditor();
      // Refresh Affinity views that depend on subscription/bundle data.
      document.dispatchEvent(new CustomEvent('Affinity:refresh'));
      return response;
    } catch (error) {
      if (
        observability &&
        typeof observability.captureException === 'function' &&
        !error.__etBundleUpdateLogged
      ) {
        observability.captureException(error, {
          level: 'error',
          tags: {
            surface: 'affinity-update-meals',
            component: 'bundle-selection-update',
            operation: 'submitBundleSelectionUpdate',
          },
          extra: {
            subscriptionId: state.activeSubscriptionId || null,
            activeBundleSelectionId: state.activeBundleSelectionId || null,
            payload: payloadForLogging,
          },
        });
      }
      // An auth failure here means re-login could not recover the session, so
      // "try again" in place will keep failing -- the customer must reload.
      showInlineErrorToast(
        isAuthError(error)
          ? 'Your session has expired. Please refresh the page and try again.'
          : 'Unable to save your subscription right now. Please try again.'
      );
      throw error;
    } finally {
      state.submitting = false;
      setSaveButtonState(false, UPDATE_BUTTON_DEFAULT_LABEL);
    }
  }

  // Sanitizes UI item models into API-safe payload:
  // - strips internal `__` fields
  // - strips read-only identifiers/timestamps
  // - drops zero-quantity rows
  function buildBundleSelectionUpdatePayload(items, bundleSelection) {
    // Preserve item payload shape while normalizing quantity and removing obvious
    // read-only metadata keys that APIs typically reject in PATCH payloads.
    const sanitizedItems = items
      .map(sanitizeBundleSelectionItemForPayload)
      .filter((item) => item.quantity > 0);

    const payload = { items: sanitizedItems };

    const purchaseItemId = parseOptionalSubscriptionId(
      bundleSelection?.purchase_item_id
    );
    if (purchaseItemId) {
      payload.purchase_item_id = purchaseItemId;
    }

    return payload;
  }

  function sanitizeBundleSelectionItemForPayload(item) {
    const outbound = { ...item };
    for (const key of Object.keys(outbound)) {
      if (key.startsWith('__')) {
        delete outbound[key];
      }
    }
    delete outbound.id;
    delete outbound.created_at;
    delete outbound.updated_at;
    delete outbound.bundle_selection_id;
    outbound.quantity = parseInteger(item.quantity);
    return outbound;
  }

  // Save button is rendered dynamically, so guard for missing node during rerenders.
  function setSaveButtonState(isLoading, label) {
    const footer = getEditorFooter();
    const body = getEditorBody();
    const buttons = [];
    const footerBtn = footer?.querySelector(`[data-action="${EDITOR_ACTIONS.save}"]`);
    if (footerBtn instanceof HTMLButtonElement) buttons.push(footerBtn);
    const sidebarBtn = body?.querySelector('.et-update-meals-sidebar__submit');
    if (sidebarBtn instanceof HTMLButtonElement) buttons.push(sidebarBtn);
    buttons.forEach((button) => {
      button.disabled = isLoading;
      button.textContent = label;
    });
  }

  function queryEditorContainer(selector) {
    const inlineRoot = resolveMountedOverviewHeaderSlotElement();
    if (!(inlineRoot instanceof HTMLElement)) return null;
    const editorNode = inlineRoot.querySelector(selector);
    if (editorNode instanceof HTMLElement) return editorNode;
    return null;
  }

  // DOM helper for inline editor body container.
  function getEditorBody() {
    return queryEditorContainer(CONFIG.editorBodySelector);
  }

  // DOM helper for inline editor footer container.
  function getEditorFooter() {
    return queryEditorContainer(CONFIG.editorFooterSelector);
  }

  // Tone-specific convenience wrapper for success notifications.
  function showInlineSuccessToast(message) {
    showToast(message, 'success');
  }

  function showNoMealsDialog() {
    try {
      const existing = document.querySelector('.et-update-meals-dialog-backdrop[data-dialog=\"no-meals\"]');
      if (existing) existing.remove();
    } catch (e) {
      // nothing
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'et-update-meals-dialog-backdrop';
    backdrop.setAttribute('data-dialog', 'no-meals');

    const dialog = document.createElement('div');
    dialog.className = 'et-update-meals-dialog';
    dialog.innerHTML = [
      '<h3 class=\"et-update-meals-dialog__title\">No meals in your subscription</h3>',
      '<p class=\"et-update-meals-dialog__body\">Please add at least one meal to your subscription before saving your changes.</p>',
      '<div class=\"et-update-meals-dialog__actions\">',
      '  <button type=\"button\" class=\"et-update-meals-dialog__button\" data-role=\"dialog-close\">OK</button>',
      '</div>',
    ].join('');

    const close = () => {
      backdrop.remove();
    };

    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) {
        close();
      }
    });
    dialog.querySelector('[data-role=\"dialog-close\"]')?.addEventListener('click', close);

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);
  }

  // Tone-specific convenience wrapper for error notifications.
  function showInlineErrorToast(message) {
    showToast(message, 'error');
  }
  window.etShowInlineErrorToast = showInlineErrorToast;

  // Lightweight toast utility; avoids external dependency for notifications.
  function showToast(message, tone) {
    const toast = document.createElement('div');
    toast.className = `et-update-meals-toast et-update-meals-toast--${tone}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    window.setTimeout(() => {
      toast.classList.add('is-visible');
    }, 10);
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 250);
    }, 2500);
  }

  // Minimal HTML-escaping helper for user/content-derived strings rendered into templates.
  function escapeHtml(value) {
    const sharedEscape = window.etRechargeUtils?.escapeHtml;
    if (typeof sharedEscape === 'function') {
      return sharedEscape(value);
    }
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }  


})();

(function () {
  var scroll_button_root = typeof document !== 'undefined' ? document : null;
  if (!scroll_button_root || typeof scroll_button_root.addEventListener !== 'function') return;

  var SHOW_SCROLL_THRESHOLD = 600;

  // Scroll happens inside .et-update-meals-editor__main (or window), not always on window.
  // Listen on document so we get scroll from the editor when it exists.
  scroll_button_root.addEventListener('scroll', function (e) {
    var button = scroll_button_root.querySelector('.afinity-scroll-up-btn');
    if (!button) return;

    var scrollTarget = e.target;
    var scrollTop = 0;
    if (scrollTarget && scrollTarget.nodeType === 1) {
      if (scrollTarget.closest && scrollTarget.closest('.et-update-meals-overview-slot--editing')) {
        scrollTop = typeof scrollTarget.scrollTop === 'number' ? scrollTarget.scrollTop : 0;
      } else {
        scrollTop = typeof window.scrollY === 'number' ? window.scrollY : 0;
      }
    } else {
      scrollTop = typeof window.scrollY === 'number' ? window.scrollY : 0;
    }

    if (scrollTop > SHOW_SCROLL_THRESHOLD) {
      button.classList.add('is-visible');
    } else {
      button.classList.remove('is-visible');
    }
  }, true);

  scroll_button_root.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    if (!e.target.closest('.afinity-scroll-up-btn')) return;

    var sidebar = scroll_button_root.querySelector('.et-update-meals-inline-editor__footer');
    if (sidebar) sidebar.scrollIntoView({ behavior: 'smooth' });
  });
})();
